import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { authService, UserProfileDto, UpdateProfileDto, LearningPreferencesDto } from '../services/authService';
import { PersianText } from './shared/PersianText';
import { ProfileComboList } from './shared/ProfileComboList';
import { LearnerPageHero } from './shared/LearnerPageHero';
import { SimplePageHeader } from './shared/SimplePageHeader';
import { LearnerProfileActionButtons, LearnerProfileHeroCard } from './shared/LearnerProfileHeroCard';
import { PersianOverlayLoader } from './shared/PersianLoader';
import { LearnerInlineContentLoader } from './shared/LearnerInlineContentLoader';
import { CollapsibleFadePanel } from './shared/CollapsibleFadePanel';
import { UserCommentsPanel } from './shared/UserCommentsModal';
import { Camera, User, Calendar, Trophy, BookOpen, Library, Target, CreditCard, ChevronDown, ChevronRight, Mail, MapPin, Phone, Clock, Heart, Loader2, TrendingUp, PieChart, Activity, LayoutDashboard, ArrowLeft, AtSign, FileText, Tag, Users, Timer, PenLine, BarChart3, ClipboardList, Brain, Sparkles, MessageSquare, type LucideIcon } from 'lucide-react';
import { getIranPhoneNumberFormatError, iranPhonesAreEquivalent, normalizeIranPhoneNumber, toStoredE164Input } from '../utils/iranPhoneNumber';
import { consumeTelegramPhoneHint, TELEGRAM_PHONE_HINT_EVENT } from '../utils/telegramPhoneHint';
import { favoriteService, FavoriteLessonItemDto, FavoriteTagBlockItemDto, FavoriteTagLessonItemDto } from '../services/favoriteService';
import { libraryService, FavoriteLibraryBookItemDto } from '../services/libraryService';
import { normalizeAppHostedUploadUrl } from '../utils/normalizeAppHostedUploadUrl';
import { useNotifications } from './feedback/NotificationSystem';
import { notifyGamificationAfterAction } from '../utils/gamificationNotif';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { validateImageFile, getValidationErrorMessage, getImageDimensions, profileFormatsCsvToImageOptions } from '../utils/imageValidation';
import { PasswordChangeForm } from './auth/PasswordChangeForm';
import { useTheme } from '../hooks/useTheme';
import { useAppLocale } from '../providers/LocaleProvider';
import { useLocalizedContent } from '../hooks/useLocalizedContent';
import { formatAppDate, formatAppDateTime } from '../utils/localeDateFormat';
import { colorWithAlpha, darkenColor } from '../utils/colorUtils';
import { mirrorRtlDecor, mirrorLinearGradientAngle } from '../utils/mirrorRtlDecor';
import { RtlDecorLayer } from './shared/RtlDecorLayer';
import { FlowContextBreadcrumb } from './shared/FlowContextBreadcrumb';
import Inbox from './Inbox';
import { InboxPushHint } from './shared/InboxPushHint';
import { messageService } from '../services/messageService';
import { subscriptionService } from '../services/subscriptionService';
import { contentTagService, PublicContentTagDto } from '../services/contentTagService';
import { contentService, TagProgressDto } from '../services/contentService';
import { levelProgressionService, KnowledgePlacementStateDto } from '../services/levelProgressionService';
import { KNOWLEDGE_PLACEMENT_EVENT } from '../utils/knowledgePlacementStorage';
import { LEARNER_TIMEZONE_CHANGED_EVENT, DAILY_LEARNING_GOAL_CHANGED_EVENT, FOCUS_DAILY_COMMITMENT_EVENT, FOCUS_DAILY_COMMITMENT_STORAGE_KEY } from '../constants/learnerPersianFonts';
import { TOUR_TARGET_FOCUS_EVENT, useTourCollapsibleTarget, type TourTargetEventDetail } from '../learnerTour/tourTargetEvents';
import { profileTourPageKey } from '../learnerTour/learnerTourRegistry';
import { emitLearnerTourPageKey } from '../contexts/ProductTourContext';
import { readLearnerHomeDesign, useLearnerHomeDesign } from '../utils/learnerHomeDesign';
import { MmePickBack, MmePickList } from './dashboard/MmePickList';
import { getDeviceTimeZone } from '../utils/ianaTimeZones';
import { getIconByName } from '../utils/iconUtils';
import { SubscriptionPlan, UserSubscriptionStatus } from '../types/subscription';
import { getActivePlanBadgeInfo } from '../utils/normalizeSubscriptionStatus';
import { ActiveSubscriptionPlanBadge } from './shared/ActiveSubscriptionPlanBadge';
import { HorizontalEdgeScrollButtons } from './shared/HorizontalEdgeScrollButtons';
import {
  HorizontalFilterPillRow,
  type HorizontalFilterPillRowTheme
} from './shared/HorizontalFilterPillRow';
import { ActivityHeatmap, normalizeHeatmapResponse } from './shared/ActivityHeatmap';
import { HourlyActivityPatternCard } from './shared/HourlyActivityPatternCard';
import { ProfileSubscriptionHistory } from './profile/ProfileSubscriptionHistory';
import { ProfileSlideFormModalShell } from './profile/ProfileSlideFormModalShell';
import { ProfilePhoneVerificationField } from './profile/ProfilePhoneVerificationField';
import { SubscriptionPlanCard } from './subscription/SubscriptionPlanCard';
import { SubscriptionPlanIconGlyph, subscriptionPlanBadgeStyle } from '../utils/subscriptionPlanIcon';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  LineElement,
  PointElement,
  Filler
} from 'chart.js';
import type { Chart } from 'chart.js';
import { Bar, Pie, Line, Doughnut } from 'react-chartjs-2';
import { LEARNER_UI_PERSIAN_STACK } from '../constants/learnerPersianFonts';
import {
  schedulePaneHeightSync,
  scrollChildIntoHorizontalScroller,
  afterHorizontalScrollSettles,
  applyTabIndicator,
  settleCarouselDrag,
  TAB_CAROUSEL_TRANSITION
} from '../utils/scrollChildIntoHorizontalScroller';

const PROFILE_CHART_FONT_LATIN = 'system-ui, -apple-system, "Segoe UI", sans-serif';

function profileChartFontFamily(isPersian: boolean): string {
  return isPersian ? LEARNER_UI_PERSIAN_STACK : PROFILE_CHART_FONT_LATIN;
}

function getProfileChartAxisFontSize(): number {
  if (typeof window === 'undefined') return 10;
  return window.innerWidth < 640 ? 8 : 10;
}

function getProfileChartBarLabelFontSize(): number {
  if (typeof window === 'undefined') return 11;
  return window.innerWidth < 640 ? 9 : 11;
}

/** Minute labels on top of each bar (matches learner dashboard practice-time chart). */
function createProfileTimeBarTopLabelsPlugin(
  isDarkMode: boolean,
  fontFamily: string,
  formatMinutesLabel: (minutes: number) => string,
  barLabelFontSize = getProfileChartBarLabelFontSize()
) {
  return {
    id: 'profileProgressTimeBarTopLabels',
    afterDatasetsDraw(chart: Chart) {
      const meta = chart.getDatasetMeta(0);
      if (!meta?.data?.length) return;
      const ds = chart.data.datasets[0];
      if (!ds) return;
      const ctx = chart.ctx;
      ctx.save();
      ctx.textAlign = 'center';
      ctx.textBaseline = 'bottom';
      ctx.font = `600 ${barLabelFontSize}px ${fontFamily}`;
      ctx.fillStyle = isDarkMode ? '#f3f4f6' : '#1f2937';
      meta.data.forEach((element, i) => {
        const raw = ds.data?.[i];
        const v = typeof raw === 'number' ? raw : Number(raw);
        if (!Number.isFinite(v)) return;
        const props = element.getProps(['x', 'y', 'base'], true) as { x: number; y: number; base: number };
        const topY = Math.min(props.y, props.base) - 5;
        ctx.fillText(formatMinutesLabel(v), props.x, topY);
      });
      ctx.restore();
    }
  };
}

// Register Chart.js components
ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  LineElement,
  PointElement,
  Filler
);

// Add custom styles for scrollbar and animations (always refresh text so HMR picks up indicator CSS).
if (typeof document !== 'undefined') {
  let styleSheet = document.getElementById('profile-modal-styles') as HTMLStyleElement | null;
  if (!styleSheet) {
    styleSheet = document.createElement('style');
    styleSheet.id = 'profile-modal-styles';
    document.head.appendChild(styleSheet);
  }
  styleSheet.textContent = `
    .custom-scrollbar::-webkit-scrollbar {
      width: 8px;
    }
    .custom-scrollbar::-webkit-scrollbar-track {
      background: #f1f1f1;
      border-radius: 10px;
    }
    .custom-scrollbar::-webkit-scrollbar-thumb {
      background: #c1c1c1;
      border-radius: 10px;
    }
    .custom-scrollbar::-webkit-scrollbar-thumb:hover {
      background: #a8a8a8;
    }
    .scrollbar-hide {
      -ms-overflow-style: none;
      scrollbar-width: none;
    }
    .scrollbar-hide::-webkit-scrollbar {
      display: none;
    }
    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    @keyframes slideUp {
      from { transform: translateY(20px); opacity: 0; }
      to { transform: translateY(0); opacity: 1; }
    }
    @keyframes slideInFromRight {
      from { transform: translateX(100%); opacity: 0; }
      to { transform: translateX(0); opacity: 1; }
    }
    @keyframes slideInFromLeft {
      from { transform: translateX(-100%); opacity: 0; }
      to { transform: translateX(0); opacity: 1; }
    }
    @keyframes slideOutToLeft {
      from { transform: translateX(0); opacity: 1; }
      to { transform: translateX(-100%); opacity: 0; }
    }
    @keyframes slideOutToRight {
      from { transform: translateX(0); opacity: 1; }
      to { transform: translateX(100%); opacity: 0; }
    }
    .animate-fadeIn {
      animation: fadeIn 0.2s ease-out;
    }
    .animate-slideUp {
      animation: slideUp 0.3s ease-out;
    }
    .animate-slideInRight {
      animation: slideInFromRight var(--slide-duration, 0.15s) ease-out both;
    }
    .animate-slideInLeft {
      animation: slideInFromLeft var(--slide-duration, 0.15s) ease-out both;
    }
    .animate-slideOutLeft {
      animation: slideOutToLeft var(--slide-duration, 0.15s) ease-out forwards;
      pointer-events: none;
    }
    .animate-slideOutRight {
      animation: slideOutToRight var(--slide-duration, 0.15s) ease-out forwards;
      pointer-events: none;
    }
    .tab-content-container {
      position: relative;
      overflow: hidden;
      min-height: 200px;
      scrollbar-gutter: stable;
    }
    .tab-content-wrapper {
      position: relative;
      width: 100%;
      transition: height 0.2s ease-out;
      overflow: hidden;
    }
    .tab-content-wrapper.transitioning {
      min-height: fit-content;
    }
    .tab-content-wrapper:not(.transitioning) {
      height: auto !important;
    }
    .tab-content-slide {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      will-change: transform;
      overflow: visible;
      scrollbar-gutter: stable;
    }
    .tab-content-slide.entering,
    .tab-content-slide.exiting {
      position: absolute;
      height: auto;
      min-height: 100%;
    }
    .tab-content-slide.entering {
      z-index: 2;
      /* Start off-screen - initial state set by animation keyframes */
    }
    .tab-content-slide.entering[data-direction="right"] {
      transform: translateX(100%);
      opacity: 0;
    }
    .tab-content-slide.entering[data-direction="left"] {
      transform: translateX(-100%);
      opacity: 0;
    }
    /* Ensure entering elements are properly hidden when transition completes */
    .tab-content-wrapper:not(.transitioning) .tab-content-slide.entering {
      display: none;
    }
    .tab-content-slide.exiting {
      z-index: 1;
      pointer-events: none;
    }
    .tab-content-slide.exiting.animate-slideOutLeft,
    .tab-content-slide.exiting.animate-slideOutRight {
      animation-fill-mode: forwards;
    }
    /* Ensure exiting elements are completely hidden and don't interfere */
    .tab-content-wrapper:not(.transitioning) .tab-content-slide.exiting {
      display: none;
    }
    .tab-content-slide.active {
      position: relative;
      z-index: 0;
    }
    .tab-content-slide.intermediate {
      z-index: 1;
      animation-fill-mode: both;
      /* Start off-screen - will be set via inline style with data-direction */
    }
    @keyframes slideThroughRight {
      0% { transform: translateX(100%); opacity: 0; }
      50% { transform: translateX(0); opacity: 1; }
      100% { transform: translateX(-100%); opacity: 0; }
    }
    @keyframes slideThroughLeft {
      0% { transform: translateX(-100%); opacity: 0; }
      50% { transform: translateX(0); opacity: 1; }
      100% { transform: translateX(100%); opacity: 0; }
    }
    .animate-slideThroughRight {
      animation: slideThroughRight var(--slide-duration, 0.1s) ease-out;
    }
    .animate-slideThroughLeft {
      animation: slideThroughLeft var(--slide-duration, 0.1s) ease-out;
    }
    /* Tab indicator — left/width set inline via applyTabIndicator */
    .tab-indicator {
      position: absolute;
      bottom: 0;
      left: 0;
      height: 2px;
      width: 0;
      background: #3b82f6;
      transition: left 0.28s cubic-bezier(0.22, 1, 0.36, 1), width 0.28s cubic-bezier(0.22, 1, 0.36, 1);
      will-change: left, width;
      z-index: 10;
      pointer-events: none;
    }
    .dark .tab-indicator {
      background: #60a5fa;
    }
    /* Tab button sliding animation */
    .tab-nav-container {
      position: relative;
      overflow: hidden;
    }
    .tab-nav-slider {
      display: flex;
      transition: transform 0.2s ease-out;
      will-change: transform;
    }
    /* Reserve space for scrollbar to prevent layout shift */
    html {
      scrollbar-gutter: stable;
    }
    body {
      scrollbar-gutter: stable;
    }
    /* Prevent horizontal scrollbar during tab transitions */
  `;
}

// List of countries for nationality dropdown
const COUNTRIES = [
  'Afghanistan', 'Albania', 'Algeria', 'Argentina', 'Armenia', 'Australia', 'Austria', 'Azerbaijan',
  'Bahrain', 'Bangladesh', 'Belarus', 'Belgium', 'Bolivia', 'Bosnia and Herzegovina', 'Brazil', 'Bulgaria',
  'Cambodia', 'Canada', 'Chile', 'China', 'Colombia', 'Croatia', 'Czech Republic',
  'Denmark', 'Ecuador', 'Egypt', 'Estonia', 'Ethiopia',
  'Finland', 'France', 'Georgia', 'Germany', 'Ghana', 'Greece',
  'Hungary', 'Iceland', 'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland', 'Israel', 'Italy',
  'Japan', 'Jordan', 'Kazakhstan', 'Kenya', 'Kuwait',
  'Latvia', 'Lebanon', 'Lithuania', 'Luxembourg',
  'Malaysia', 'Mexico', 'Morocco', 'Netherlands', 'New Zealand', 'Nigeria', 'Norway',
  'Pakistan', 'Peru', 'Philippines', 'Poland', 'Portugal',
  'Qatar', 'Romania', 'Russia', 'Saudi Arabia', 'Singapore', 'Slovakia', 'Slovenia', 'South Africa', 'South Korea', 'Spain', 'Sri Lanka', 'Sweden', 'Switzerland',
  'Thailand', 'Turkey', 'Ukraine', 'United Arab Emirates', 'United Kingdom', 'United States', 'Uruguay', 'Venezuela', 'Vietnam'
];

type EditProfileComboField = 'nationality' | 'age' | 'gender';

/** Shared look for edit-profile combo triggers (matches settings timezone). */
const EDIT_PROFILE_COMBO_STYLE = {
  useFixedPortal: true,
  triggerClassName: 'border-slate-200 dark:border-gray-600',
  accentSelectedClass: 'bg-sky-50 dark:bg-sky-950/45 text-sky-900 dark:text-sky-100',
  accentCheckClass: 'text-sky-600 dark:text-sky-400',
} as const;

const EDIT_PROFILE_SECTION_HEADING_CLASS =
  'text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4 flex items-center gap-2.5';




/**
 * Attach non-passive touch listeners directly to a `<input type="range">` so dragging the slider on
 * a phone / tablet does not scroll the host page. The browser decides whether `preventDefault()`
 * can stop scrolling based on the `touch-action` of the touch *target*; setting it on a wrapper
 * div is not enough — it has to be on the input itself.
 */
function useStopRangeFromScrollingPage(ref: React.RefObject<HTMLInputElement | null>) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.touchAction = 'none';
    const onTouch = (e: TouchEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };
    el.addEventListener('touchstart', onTouch, { passive: false });
    el.addEventListener('touchmove', onTouch, { passive: false });
    el.addEventListener('touchend', onTouch, { passive: false });
    return () => {
      el.removeEventListener('touchstart', onTouch);
      el.removeEventListener('touchmove', onTouch);
      el.removeEventListener('touchend', onTouch);
    };
  }, [ref]);
}

const DAILY_TIME_PRESETS = [15, 30, 45, 60, 90, 120] as const;

const DAILY_TIME_CHIP_THEME: HorizontalFilterPillRowTheme = {
  rowClass: '',
  labelIconShellClass: '',
  labelIconClass: '',
  labelTextClass: '',
  trackShellClass:
    'overflow-hidden rounded-full border border-violet-200/70 bg-gradient-to-r from-violet-50/95 via-white/90 to-fuchsia-50/80 shadow-[inset_0_1px_2px_rgba(124,58,237,0.08)] dark:border-violet-700/50 dark:from-violet-950/50 dark:via-slate-900/80 dark:to-fuchsia-950/35',
  pillActiveClass: 'text-white',
  pillInactiveClass:
    'text-violet-800/85 hover:text-violet-950 dark:text-violet-100/80 dark:hover:text-white',
  scrollBtnClass:
    'border-violet-200/55 bg-white/60 text-violet-700 hover:bg-violet-50/90 dark:border-violet-600/40 dark:bg-slate-900/50 dark:text-violet-200 dark:hover:bg-slate-800/70',
  indicatorClass:
    'bg-gradient-to-r from-violet-600 to-fuchsia-500 shadow-sm shadow-violet-500/25'
};

const RANGE_FILL_TRANSITION = 'width 0.35s cubic-bezier(0.4, 0, 0.2, 1)';
const RANGE_THUMB_TRANSITION = 'inset-inline-start 0.35s cubic-bezier(0.4, 0, 0.2, 1)';

/**
 * Range input with a custom fill/thumb that animates when the value is changed
 * from chips or a number field. Transitions are disabled while the user drags
 * so the thumb stays under the pointer. Touch handlers keep the page from
 * scrolling while dragging on mobile.
 */
const AnimatedTouchSafeRangeInput: React.FC<
  Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'>
> = ({ min = 0, max = 100, value = 0, onChange, className, ...rest }) => {
  const localRef = useRef<HTMLInputElement | null>(null);
  const [dragging, setDragging] = useState(false);
  useStopRangeFromScrollingPage(localRef);

  useEffect(() => {
    if (!dragging) return;
    const endDrag = () => setDragging(false);
    window.addEventListener('pointerup', endDrag);
    window.addEventListener('pointercancel', endDrag);
    return () => {
      window.removeEventListener('pointerup', endDrag);
      window.removeEventListener('pointercancel', endDrag);
    };
  }, [dragging]);

  const numericMin = Number(min);
  const numericMax = Number(max);
  const numericValue = Number(value);
  const span = numericMax - numericMin;
  const pct = span <= 0 ? 0 : ((numericValue - numericMin) / span) * 100;
  const clampedPct = Math.min(100, Math.max(0, Number.isFinite(pct) ? pct : 0));

  return (
    <div className={`relative flex h-8 items-center rounded-full focus-within:ring-2 focus-within:ring-violet-400/60 ${className ?? ''}`}>
      <div
        className="pointer-events-none absolute inset-x-0 top-1/2 h-2 -translate-y-1/2 overflow-hidden rounded-full bg-violet-200/90 dark:bg-violet-950/70"
        aria-hidden
      >
        <div
          className="h-full rounded-full bg-gradient-to-r from-violet-600 to-fuchsia-500"
          style={{
            width: `${clampedPct}%`,
            transition: dragging ? 'none' : RANGE_FILL_TRANSITION
          }}
        />
      </div>
      <div
        className="pointer-events-none absolute top-1/2 z-[1] h-5 w-5 rounded-full border-2 border-violet-600 bg-white shadow-sm ltr:-translate-x-1/2 rtl:translate-x-1/2 -translate-y-1/2 dark:border-violet-400 dark:bg-violet-100"
        style={{
          insetInlineStart: `${clampedPct}%`,
          transition: dragging ? 'none' : RANGE_THUMB_TRANSITION
        }}
        aria-hidden
      />
      <input
        ref={localRef}
        type="range"
        min={min}
        max={max}
        value={value}
        {...rest}
        onChange={onChange}
        onPointerDown={() => setDragging(true)}
        className="relative z-[2] h-8 w-full cursor-pointer appearance-none bg-transparent opacity-0"
      />
    </div>
  );
};

export interface UserProfileDashboardProps {
  className?: string;
  onNavigateHome?: () => void;
  onUserUpdate?: (user: UserProfileDto) => void;
  initialUser?: UserProfileDto | null;
  /** When set (e.g. from "Go to Subscription Plans"), open this tab on mount */
  initialTab?: string | null;
  /** Called after initialTab has been applied so parent can clear it */
  onInitialTabConsumed?: () => void;
  onNavigateToLesson?: (lessonId: number) => void;
  onNavigateToTag?: (tagId: number, blockId?: number, lessonId?: number) => void;
  onNavigateToBook?: (bookId: number) => void;
  onOpenKnowledgePlacement?: () => void;
}

type TabType = 'profile' | 'goals' | 'subscription' | 'progress' | 'messages' | 'comments' | 'favorites';

type NonProfileTab = Exclude<TabType, 'profile'>;

/** Pinned action strip: 1px top border + 12px gap (Tailwind pt-3). Stick when the tab bar reaches this gap so the buttons do not jump. */
const PROFILE_ACTIONS_STICK_OFFSET_PX = 13;

const PROFILE_TAB_ICONS: Record<TabType, LucideIcon> = {
  profile: User,
  goals: Target,
  subscription: CreditCard,
  progress: BarChart3,
  messages: Mail,
  comments: MessageSquare,
  favorites: Heart,
};

const TAB_HERO_ICONS: Record<NonProfileTab, { Icon: LucideIcon; heroGradientStops: [string, string, string] }> = {
  goals: { heroGradientStops: ['#ecfdf5', '#a7f3d0', '#5eead4'], Icon: Target },
  subscription: { heroGradientStops: ['#eff6ff', '#bfdbfe', '#93c5fd'], Icon: CreditCard },
  progress: { heroGradientStops: ['#f0f9ff', '#bae6fd', '#7dd3fc'], Icon: BarChart3 },
  messages: { heroGradientStops: ['#ecfeff', '#a5f3fc', '#67e8f9'], Icon: Mail },
  comments: { heroGradientStops: ['#f5f3ff', '#ddd6fe', '#c4b5fd'], Icon: MessageSquare },
  favorites: { heroGradientStops: ['#fff7ed', '#fed7aa', '#fdba74'], Icon: Heart },
};

export const UserProfileDashboard: React.FC<UserProfileDashboardProps> = ({
  className,
  onNavigateHome,
  onUserUpdate,
  initialUser,
  initialTab,
  onInitialTabConsumed,
  onNavigateToLesson,
  onNavigateToTag,
  onNavigateToBook,
  onOpenKnowledgePlacement
}) => {
  const { t: tProfile } = useTranslation('profile');
  const { t: tCommon } = useTranslation('common');
  const { t: tSubscription } = useTranslation('subscription');
  const { t: tLearning } = useTranslation('learning');
  const { direction, locale, isPersian } = useAppLocale();
  const { pickTagName, pickTagDescription, pickBlockTitle, pickTitle, pickBookTitle, pickBookAuthor, pickDescription } = useLocalizedContent();
  const isRtl = direction === 'rtl';
  const buildFavoriteContextPath = useCallback(
    (...segments: { title: string; titleFa?: string | null }[]) => {
      const enSegments: string[] = [];
      const faSegments: string[] = [];
      for (const segment of segments) {
        if (!segment.title?.trim()) continue;
        enSegments.push(segment.title.trim());
        if (segment.titleFa?.trim()) faSegments.push(segment.titleFa.trim());
      }
      if (enSegments.length === 0) return null;
      return {
        contextSubtitle: enSegments.join(' › '),
        contextSubtitleFa: faSegments.length > 0 ? faSegments.join(' › ') : undefined
      };
    },
    []
  );
  const favTextContentDir = isRtl ? ('rtl' as const) : ('ltr' as const);
  const favTextAlignClass = isRtl ? 'text-right' : 'text-left';
  const favTitleTextClass =
    'block truncate text-xs font-medium text-gray-900 dark:text-gray-100 sm:text-sm';
  const favMetaTextClass =
    'block truncate text-[11px] text-gray-500 dark:text-gray-400 sm:text-xs';
  const favTagHeaderTitleClass =
    'block truncate text-xs font-semibold text-gray-900 dark:text-gray-100 sm:text-sm';
  const favKindLabelClass = (tone: 'violet' | 'emerald') =>
    `block truncate text-[9px] font-bold uppercase tracking-wide sm:text-[10px] ${
      tone === 'violet'
        ? 'text-violet-600 dark:text-violet-300'
        : 'text-emerald-600 dark:text-emerald-300'
    }`;
  const rtlDecor = useCallback((className: string) => mirrorRtlDecor(className, isRtl), [isRtl]);
  // All hooks must be called at the top level
  const { showSuccess, showError } = useNotifications();
  const { handleUploadError, handleApiError } = useErrorHandler();
  const { isDark } = useTheme();

  const openIfStillAvailable = useCallback(
    (available: boolean | undefined, missingMessage: string, open: () => void) => {
      if (available === false) {
        showError(missingMessage);
        return;
      }
      open();
    },
    [showError]
  );

  const makeGradient = (...stops: string[]) => {
    if (!stops.length) return undefined;
    const adjusted = stops.map((stop) => isDark ? darkenColor(stop, 0.35) : stop);
    const angle = mirrorLinearGradientAngle(135, isRtl);
    return `linear-gradient(${angle}deg, ${adjusted.join(', ')})`;
  };
  const tabHeroRowClass = 'relative flex flex-col items-center gap-3 sm:flex-row sm:items-center';
  const tabHeroShellClass =
    'relative overflow-hidden rounded-xl px-4 py-5 text-center sm:px-5 sm:py-6 sm:text-start';

  const [user, setUser] = useState<UserProfileDto | null>(initialUser || null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success] = useState<string | null>(null);
  const TAB_ORDER: TabType[] = ['profile', 'subscription', 'messages', 'favorites', 'comments', 'goals', 'progress'];
  /** FA mirrors tab bar visually (profile rightmost) via flex `order`; carousel uses reversed pane order + inverted swipe math. */
  const tabVisualOrder = useCallback(
    (tab: TabType) => {
      const idx = TAB_ORDER.indexOf(tab);
      return isRtl ? TAB_ORDER.length - 1 - idx : idx;
    },
    [isRtl]
  );
  const tabCarouselIndex = useCallback(
    (tab: TabType) => {
      const idx = TAB_ORDER.indexOf(tab);
      return isRtl ? TAB_ORDER.length - 1 - idx : idx;
    },
    [isRtl]
  );
  const tabCarouselOrder = useMemo(
    () => (isRtl ? [...TAB_ORDER].reverse() : TAB_ORDER),
    [isRtl]
  );

  const [activeTab, setActiveTab] = useState<TabType>(() => {
    if (initialTab && (TAB_ORDER as readonly string[]).includes(initialTab)) {
      return initialTab as TabType;
    }
    return 'profile';
  });
  const [homeDesign] = useLearnerHomeDesign();
  const simpleHome = homeDesign === 'mme';
  useEffect(() => {
    const key = simpleHome ? 'profile' : profileTourPageKey(activeTab);
    const timer = window.setTimeout(() => emitLearnerTourPageKey(key), 0);
    return () => window.clearTimeout(timer);
  }, [activeTab, simpleHome]);
  const [profileMenu, setProfileMenu] = useState(() => {
    const hash = typeof window !== 'undefined' ? window.location.hash.replace('#', '') : '';
    if (hash === 'menus') return true;
    if (hash && (TAB_ORDER as readonly string[]).includes(hash)) return false;
    return !initialTab;
  });
  const [inboxRefreshKey, setInboxRefreshKey] = useState(0);
  const [kpRefresh, setKpRefresh] = useState(0);
  const [placementState, setPlacementState] = useState<KnowledgePlacementStateDto | null>(null);
  const [isKnowledgePlacementExpanded, setIsKnowledgePlacementExpanded] = useState(false);
  useTourCollapsibleTarget('profile-knowledge-placement', isKnowledgePlacementExpanded, setIsKnowledgePlacementExpanded);
  useEffect(() => {
    const fn = () => setKpRefresh((n) => n + 1);
    window.addEventListener(KNOWLEDGE_PLACEMENT_EVENT, fn);
    return () => window.removeEventListener(KNOWLEDGE_PLACEMENT_EVENT, fn);
  }, []);

  useEffect(() => {
    if (!user?.id) {
      setPlacementState(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const state = await levelProgressionService.getKnowledgePlacementState();
        if (!cancelled) setPlacementState(state);
      } catch {
        if (!cancelled) setPlacementState(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id, kpRefresh]);
  const tabRefs = useRef<{ [key in TabType]?: HTMLButtonElement }>({});
  const navRef = useRef<HTMLElement>(null);
  const profileTabBarRef = useRef<HTMLDivElement>(null);
  const [profileTabBarHeight, setProfileTabBarHeight] = useState(0);
  const lastTabNavLayoutWidthRef = useRef(0);
  const tabNavRowRef = useRef<HTMLDivElement | null>(null);
  const tabIndicatorElRef = useRef<HTMLDivElement | null>(null);
  const cardsScrollRef = useRef<HTMLDivElement | null>(null);
  /** In-flow Edit / Change password row. A fixed copy pins under the tab bar once this row scrolls above it. */
  const profileActionsSentinelRef = useRef<HTMLDivElement | null>(null);
  const [pinProfileActions, setPinProfileActions] = useState(false);
  const [pinnedActionsFrame, setPinnedActionsFrame] = useState<{ top: number; left: number; width: number } | null>(null);
  // Per-tab content refs for measuring intrinsic height (so the carousel container fits the active tab only).
  const tabContentRefs = useRef<{ [key in TabType]?: HTMLDivElement | null }>({});
  const tabContentContainerRef = useRef<HTMLDivElement | null>(null);
  const tabHeightCommitTimerRef = useRef(0);
  const pendingTabHeightRef = useRef<{ tab: TabType; h: number } | null>(null);
  const [tabHeights, setTabHeights] = useState<{ [key in TabType]?: number }>({});
  // Translate-based carousel: drag offset lives in --profile-tab-drag-x (DOM) while the finger is down.
  const dragOffsetPxRef = useRef(0);
  const [isDragging, setIsDragging] = useState(false);
  // Stable refs so the indicator updater can read latest active tab / drag without being re-created.
  const activeTabRef = useRef<TabType>('profile');
  const dragUiRef = useRef({ isDragging: false, dragOffsetPx: 0 });
  const tabCarouselIndexRef = useRef(tabCarouselIndex);
  tabCarouselIndexRef.current = tabCarouselIndex;
  const isRtlRef = useRef(isRtl);
  useEffect(() => {
    isRtlRef.current = isRtl;
  }, [isRtl]);

  const pushProfileTabHistory = useCallback((tab: TabType) => {
    if (typeof window === 'undefined') return;
    const currentHash = window.location.hash.replace('#', '');
    if (currentHash === tab) return;
    const url = `${window.location.pathname}${window.location.search}#${tab}`;
    const prev = window.history.state;
    const base = typeof prev === 'object' && prev !== null ? { ...prev } : {};
    window.history.pushState({ ...base, view: 'profile' }, '', url);
  }, []);
  const pushProfileTabHistoryRef = useRef(pushProfileTabHistory);
  useEffect(() => {
    pushProfileTabHistoryRef.current = pushProfileTabHistory;
  }, [pushProfileTabHistory]);
  // Tracks whether the tab nav has off-screen content on either side so we can show chevrons.
  const [tabNavOverflow, setTabNavOverflow] = useState<{ moreLeft: boolean; moreRight: boolean }>({ moreLeft: false, moreRight: false });

  const [editMode, setEditMode] = useState(false);
  const [telegramPhoneHint, setTelegramPhoneHint] = useState(false);
  const [openEditProfileCombo, setOpenEditProfileCombo] = useState<EditProfileComboField | null>(null);

  useEffect(() => {
    const applyHint = () => {
      if (!consumeTelegramPhoneHint()) return;
      setActiveTab('profile');
      setEditMode(true);
      setTelegramPhoneHint(true);
    };
    applyHint();
    window.addEventListener(TELEGRAM_PHONE_HINT_EVENT, applyHint);
    return () => window.removeEventListener(TELEGRAM_PHONE_HINT_EVENT, applyHint);
  }, []);

  useEffect(() => {
    if (user?.phoneNumberConfirmed) setTelegramPhoneHint(false);
  }, [user?.phoneNumberConfirmed]);

  useEffect(() => {
    if (!telegramPhoneHint || !editMode) return undefined;
    const timer = window.setTimeout(() => {
      document.getElementById('profile-phone-verification')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 450);
    return () => window.clearTimeout(timer);
  }, [telegramPhoneHint, editMode]);
  const [updating, setUpdating] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [progressTimeSectionOpen, setProgressTimeSectionOpen] = useState(true);
  const [progressActivitySectionOpen, setProgressActivitySectionOpen] = useState(true);

  const [phoneNumberError, setPhoneNumberError] = useState<string | null>(null);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const [subscriptionStatus, setSubscriptionStatus] = useState<UserSubscriptionStatus | null>(null);
  const [subscriptionPlans, setSubscriptionPlans] = useState<SubscriptionPlan[]>([]);
  const [loadingSubscription, setLoadingSubscription] = useState(false);

  const loadSubscriptionData = useCallback(async () => {
    setLoadingSubscription(true);
    try {
      const [status, plans] = await Promise.all([
        subscriptionService.getSubscriptionStatus().catch(() => null),
        subscriptionService.getPlans().catch(() => [])
      ]);
      setSubscriptionStatus(status);
      setSubscriptionPlans(plans);
    } catch (error) {
      console.error('Failed to load subscription data:', error);
    } finally {
      setLoadingSubscription(false);
    }
  }, []);

  const activePlanBadge = useMemo(
    () => getActivePlanBadgeInfo(subscriptionStatus),
    [subscriptionStatus]
  );

  const profileSubscriptionPlanLabel = useMemo(() => {
    if (subscriptionStatus?.isActive && subscriptionStatus.planName?.trim()) {
      return subscriptionStatus.planName.trim();
    }
    return tProfile('subscriptionTab.freePlan');
  }, [subscriptionStatus, tProfile]);

  const navigateToSubscriptionTab = useCallback(() => {
    setActiveTab('subscription');
    requestAnimationFrame(() => {
      const nav = navRef.current;
      const btn = tabRefs.current.subscription;
      if (nav && btn) scrollChildIntoHorizontalScroller(nav, btn);
    });
    void loadSubscriptionData();
  }, [loadSubscriptionData]);

  useEffect(() => {
    const onSubscriptionUpdated = () => void loadSubscriptionData();
    window.addEventListener('subscription-updated', onSubscriptionUpdated);
    return () => window.removeEventListener('subscription-updated', onSubscriptionUpdated);
  }, [loadSubscriptionData]);

  useEffect(() => {
    if (activeTab === 'subscription') {
      void loadSubscriptionData();
    }
  }, [activeTab, loadSubscriptionData]);

  const [progressionStats, setProgressionStats] = useState<any>(null);
  const [progressDetail, setProgressDetail] = useState<any>(null);
  const [loadingProgress, setLoadingProgress] = useState(false);
  const [progressScopeCourseIds, setProgressScopeCourseIds] = useState<number[] | null>(null);
  const [progressScopedDetail, setProgressScopedDetail] = useState<any>(null);
  const [loadingProgressScope, setLoadingProgressScope] = useState(false);
  const [progressCoursesCatalog, setProgressCoursesCatalog] = useState<Array<{ id: number; title: string }>>([]);
  const [progressPublicTags, setProgressPublicTags] = useState<PublicContentTagDto[]>([]);
  const [progressTagProgressList, setProgressTagProgressList] = useState<TagProgressDto[]>([]);
  const [loadingFavorites, setLoadingFavorites] = useState(false);
  // Learning preferences (Goals & Time tab)
  const [goalsPrefs, setGoalsPrefs] = useState<LearningPreferencesDto | null>(null);
  const [goalsSavedPrefs, setGoalsSavedPrefs] = useState<LearningPreferencesDto | null>(null);
  const [loadingGoals, setLoadingGoals] = useState(false);
  const [savingGoals, setSavingGoals] = useState(false);
  const [savingDailyCommitment, setSavingDailyCommitment] = useState(false);
  const [goalsDirty, setGoalsDirty] = useState(false);
  const [dailyCommitmentPulse, setDailyCommitmentPulse] = useState(false);
  const [dailyCommitmentFocusNonce, setDailyCommitmentFocusNonce] = useState(0);
  const dailyCommitmentRef = useRef<HTMLDivElement | null>(null);
  const nationalityComboOptions = useMemo(() => COUNTRIES.map((c) => ({ value: c, label: c })), []);
  const ageComboOptions = useMemo(
    () => Array.from({ length: 86 }, (_, i) => i + 5).map((a) => ({ value: String(a), label: String(a) })),
    []
  );
  const genderComboOptions = useMemo(
    () => [
      { value: 'Male', label: tProfile('form.male') },
      { value: 'Female', label: tProfile('form.female') },
      { value: 'Other', label: tProfile('form.other') },
      { value: 'Prefer not to say', label: tProfile('form.preferNotToSay') },
    ],
    [tProfile]
  );

  useEffect(() => {
    if (!editMode) setOpenEditProfileCombo(null);
  }, [editMode]);

  const editProfileComboOpenChange = useCallback(
    (field: EditProfileComboField) => (next: boolean) => {
      setOpenEditProfileCombo((current) => (next ? field : current === field ? null : current));
    },
    []
  );
  const [favoriteLessons, setFavoriteLessons] = useState<FavoriteLessonItemDto[]>([]);
  const [favoriteLibraryBooks, setFavoriteLibraryBooks] = useState<FavoriteLibraryBookItemDto[]>([]);
  const [favoriteTags, setFavoriteTags] = useState<FavoriteTagBlockItemDto[]>([]);
  const [favoriteTagLessons, setFavoriteTagLessons] = useState<FavoriteTagLessonItemDto[]>([]);
  const [favoritesRefreshKey, setFavoritesRefreshKey] = useState(0);
  /** Favorites tab only: which list item is awaiting remove confirmation. */
  const [favoriteRemovePendingKey, setFavoriteRemovePendingKey] = useState<string | null>(null);
  const [favoriteRemoveClosingKey, setFavoriteRemoveClosingKey] = useState<string | null>(null);
  const [favoriteRemovingKey, setFavoriteRemovingKey] = useState<string | null>(null);
  const favoriteConfirmAnimMs = 220;
  const [favBooksSectionOpen, setFavBooksSectionOpen] = useState(true);
  const [favLessonsSectionOpen, setFavLessonsSectionOpen] = useState(true);
  const [favTopicTagsSectionOpen, setFavTopicTagsSectionOpen] = useState(true);
  const [isProfileExpanded, setIsProfileExpanded] = useState(true);
  const [profilePhotoLimits, setProfilePhotoLimits] = useState({
    maxSizeBytes: 5 * 1024 * 1024,
    allowedImageFormats: 'jpg,jpeg,png,gif,webp',
    maxImageWidth: 4096,
    maxImageHeight: 4096
  });

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    userName: '',
    phoneNumber: '',
    bio: '',
    nationality: '',
    age: undefined as number | undefined,
    gender: ''
  });

  useEffect(() => {
    // Always fetch profile from database to get latest photo
    loadUserProfile();
    loadUnreadCount();
    // Defer non-critical work so the profile shell stays responsive (progress loads when user opens that tab).
    let idleId: number | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      idleId = window.requestIdleCallback(() => {
        loadSubscriptionData();
        loadFavoritesData();
      });
    } else {
      timeoutId = setTimeout(() => {
        loadSubscriptionData();
        loadFavoritesData();
      }, 150);
    }
    const cancelSchedule = () => {
      if (idleId !== undefined && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
    };

    // Check for hash-based tab navigation (e.g., /profile#subscription)
    const checkHash = () => {
      if (typeof window !== 'undefined') {
        const hash = window.location.hash.replace('#', '');
        if (hash === 'menus') {
          setProfileMenu(true);
          return;
        }
        if (hash && (TAB_ORDER as readonly string[]).includes(hash)) {
          const targetTab = hash as TabType;
          setProfileMenu(false);
          setActiveTab(targetTab);
        }
      }
    };

    // Check hash on mount
    checkHash();
    const hash = typeof window !== 'undefined' ? window.location.hash.replace('#', '') : '';
    const openMenu = readLearnerHomeDesign() === 'mme' && (hash === 'menus' || hash === '');
    if (openMenu) {
      setProfileMenu(true);
      if (hash !== 'menus') {
        const menuUrl = `${window.location.pathname}${window.location.search}#menus`;
        const prev = window.history.state;
        const base = typeof prev === 'object' && prev !== null ? { ...prev } : {};
        window.history.replaceState({ ...base, view: 'profile' }, '', menuUrl);
      }
    } else if (!hash || !(TAB_ORDER as readonly string[]).includes(hash)) {
      const fallbackTab =
        initialTab && (TAB_ORDER as readonly string[]).includes(initialTab)
          ? (initialTab as TabType)
          : 'profile';
      const url = `${window.location.pathname}${window.location.search}#${fallbackTab}`;
      const prev = window.history.state;
      const base = typeof prev === 'object' && prev !== null ? { ...prev } : {};
      window.history.replaceState({ ...base, view: 'profile' }, '', url);
    }

    // If parent passed initialTab (e.g. "Go to Subscription Plans"), we already used it in useState; tell parent to clear
    if (initialTab && onInitialTabConsumed) {
      onInitialTabConsumed();
    }

    // Listen for hash / history changes so Back restores the previous profile tab
    const handleHashChange = () => {
      checkHash();
    };

    window.addEventListener('hashchange', handleHashChange);
    window.addEventListener('popstate', handleHashChange);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
      window.removeEventListener('popstate', handleHashChange);
      cancelSchedule();
    };
  }, []);

  // When already on profile and app:navigate requests a tab (e.g. Favorites from bottom banner), switch to that tab
  useEffect(() => {
    if (initialTab && (TAB_ORDER as readonly string[]).includes(initialTab)) {
      const targetTab = initialTab as TabType;
      setProfileMenu(false);
      setActiveTab(targetTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTab]);

  // Indicator placement is driven by `tabRefs` button offsets *inside the scrolling row* so that the
  // underline stays glued to the active tab as the nav itself scrolls horizontally. While the user is
  // swiping the content carousel we interpolate between the active tab and the next/prev tab so the
  // underline tracks the swipe instead of jumping when the carousel snaps.
  const updateIndicator = useCallback(() => {
    const row = tabNavRowRef.current;
    if (!row) return;
    const tab = activeTabRef.current;
    const activeButton = tabRefs.current[tab];
    if (!activeButton) return;

    const { isDragging: dragging, dragOffsetPx: drag } = dragUiRef.current;
    const cards = cardsScrollRef.current;
    const cardWidth = cards?.clientWidth ?? 0;
    const idx = TAB_ORDER.indexOf(tab);

    const measureInRow = (btn: HTMLElement) => {
      const rowRect = row.getBoundingClientRect();
      const btnRect = btn.getBoundingClientRect();
      return { left: btnRect.left - rowRect.left, width: btnRect.width };
    };

    let { left, width } = measureInRow(activeButton);

    if (dragging && cardWidth > 0) {
      // progress is -1..0..1 (negative = previous tab, positive = next tab)
      const progress = isRtlRef.current ? drag / cardWidth : -drag / cardWidth;
      let neighborIdx = idx;
      if (progress > 0 && idx < TAB_ORDER.length - 1) neighborIdx = idx + 1;
      else if (progress < 0 && idx > 0) neighborIdx = idx - 1;

      const neighborTab = TAB_ORDER[neighborIdx];
      const neighborButton = neighborTab ? tabRefs.current[neighborTab] : undefined;
      if (neighborButton && neighborIdx !== idx) {
        const t = Math.min(1, Math.abs(progress));
        const neighbor = measureInRow(neighborButton);
        left = left + (neighbor.left - left) * t;
        width = width + (neighbor.width - width) * t;
      }
    }

    applyTabIndicator(tabIndicatorElRef.current, left, width, !dragging);
    // intentionally empty deps: reads latest values via refs to stay stable across renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateIndicatorRef = useRef(updateIndicator);
  updateIndicatorRef.current = updateIndicator;

  // Compute whether the tab nav can scroll further in either direction so we can show/hide chevron buttons (mobile only).
  const updateTabNavOverflow = useCallback(() => {
    const nav = navRef.current;
    if (!nav) return;
    const isCompactTabNav = window.matchMedia('(max-width: 767px)').matches;
    const canScroll = nav.scrollWidth > nav.clientWidth + 1;
    if (!isCompactTabNav || !canScroll) {
      if (nav.scrollLeft !== 0) nav.scrollLeft = 0;
      setTabNavOverflow((prev) =>
        prev.moreLeft || prev.moreRight ? { moreLeft: false, moreRight: false } : prev
      );
      return;
    }
    const maxScroll = Math.max(0, nav.scrollWidth - nav.clientWidth);
    const scrollPos = nav.scrollLeft;
    const moreLeft = scrollPos > 1;
    const moreRight = scrollPos < maxScroll - 1;
    setTabNavOverflow((prev) => (prev.moreLeft === moreLeft && prev.moreRight === moreRight ? prev : { moreLeft, moreRight }));
  }, []);

  /** Smooth programmatic scroll does not always emit a final `scroll` on some engines; refresh overflow after it settles. */
  const scrollTabNavBy = useCallback(
    (delta: number) => {
      const nav = navRef.current;
      if (!nav) return;
      const bump = () => {
        updateTabNavOverflow();
      };
      nav.scrollBy({ left: delta, behavior: 'smooth' });
      bump();
      requestAnimationFrame(bump);
      requestAnimationFrame(() => {
        requestAnimationFrame(bump);
      });
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        bump();
      };
      const t = window.setTimeout(finish, 420);
      nav.addEventListener(
        'scrollend',
        () => {
          window.clearTimeout(t);
          finish();
        },
        { once: true }
      );
    },
    [updateTabNavOverflow]
  );

  // Keep the activeTabRef in sync before layout effects (indicator, carousel) measure the active tab.
  useLayoutEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);

  useEffect(() => {
    const onFocus = (event: Event) => {
      const detail = (event as CustomEvent<TourTargetEventDetail>).detail;
      const key = detail?.targetKey ?? '';
      if (!key.startsWith('profile-tab-')) return;
      const tab = key.slice('profile-tab-'.length) as TabType;
      if ((TAB_ORDER as readonly string[]).includes(tab)) {
        flushSync(() => {
          setProfileMenu(true);
          setActiveTab(tab);
        });
      }
    };
    window.addEventListener(TOUR_TARGET_FOCUS_EVENT, onFocus);
    return () => window.removeEventListener(TOUR_TARGET_FOCUS_EVENT, onFocus);
  }, [TAB_ORDER]);

  // Mirror drag UI state into a ref so updateIndicator can read it without being re-created on every frame.
  useEffect(() => {
    dragUiRef.current = { ...dragUiRef.current, isDragging };
  }, [isDragging]);

  // Re-measure the underline + overflow when the active tab, nav contents, or window size change.
  // Active tab clicks/scrolls also bring the tab into view inside the nav for a smooth experience.
  useLayoutEffect(() => {
    if (loading || !user) return;
    updateIndicator();
    updateTabNavOverflow();
    const btn = tabRefs.current[activeTab];
    const nav = navRef.current;
    const needsTabNavScroll =
      window.matchMedia('(max-width: 767px)').matches &&
      nav != null &&
      nav.scrollWidth > nav.clientWidth + 1;
    let cancelScrollSettle: (() => void) | undefined;
    if (btn && nav && needsTabNavScroll) {
      const didScroll = scrollChildIntoHorizontalScroller(nav, btn, 'smooth');
      if (didScroll) {
        // Underline is row-relative so it stays glued while the nav scrolls; only refresh chevrons after settle.
        cancelScrollSettle = afterHorizontalScrollSettles(nav, () => updateTabNavOverflow());
      }
    }
    lastTabNavLayoutWidthRef.current = window.innerWidth;
    const onLayoutWidthChange = () => {
      const width = window.innerWidth;
      // Mobile URL-bar show/hide fires resize with the same layout width. Re-running
      // the tab nav here re-renders the sticky strip and is what jitters on phones.
      if (width === lastTabNavLayoutWidthRef.current) return;
      lastTabNavLayoutWidthRef.current = width;
      updateIndicator();
      updateTabNavOverflow();
    };
    window.addEventListener('resize', onLayoutWidthChange);
    return () => {
      cancelScrollSettle?.();
      window.removeEventListener('resize', onLayoutWidthChange);
    };
  }, [activeTab, updateIndicator, updateTabNavOverflow, loading, user, unreadMessageCount, activePlanBadge, isRtl]);

  // Update indicator while the user drags the carousel (drag offset lives in refs / CSS vars).
  useLayoutEffect(() => {
    if (!isDragging) return;
    updateIndicator();
  }, [isDragging, updateIndicator]);

  const loadProgressData = useCallback(async () => {
    setLoadingProgress(true);
    try {
      const [detail, tags, tagProg] = await Promise.all([
        levelProgressionService.getProfileProgressDetail().catch(() => null),
        contentTagService.getPublicTags().catch(() => [] as PublicContentTagDto[]),
        contentService.getTagsProgress().catch(() => [] as TagProgressDto[])
      ]);
      const statsFromDetail = detail?.stats ?? detail?.Stats;
      if (statsFromDetail && typeof statsFromDetail === 'object') {
        setProgressionStats(statsFromDetail);
      } else {
        const stats = await levelProgressionService.getProgressionStats().catch(() => null);
        setProgressionStats(stats);
      }
      setProgressPublicTags(Array.isArray(tags) ? tags : []);
      setProgressTagProgressList(Array.isArray(tagProg) ? tagProg : []);
      setProgressDetail(detail && typeof detail === 'object' ? detail : null);
      setProgressScopeCourseIds(null);
      setProgressScopedDetail(null);
    } catch (error) {
      console.error('Failed to load progress data:', error);
      setProgressionStats(null);
      setProgressPublicTags([]);
      setProgressTagProgressList([]);
      setProgressDetail(null);
      setProgressScopeCourseIds(null);
      setProgressScopedDetail(null);
    } finally {
      setLoadingProgress(false);
    }
  }, []);

  useEffect(() => {
    if (!user?.id) {
      setProgressionStats(null);
      setProgressDetail(null);
      setProgressScopeCourseIds(null);
      setProgressScopedDetail(null);
      return;
    }
    if (loading || activeTab !== 'progress') return;
    void loadProgressData();
  }, [loading, user?.id, activeTab, loadProgressData]);

  useEffect(() => {
    if (!user?.id || activeTab !== 'progress') return;
    contentService
      .getAvailableCourses()
      .then((courses) => {
        const list = (courses || [])
          .filter((c) => c.isAccessible !== false)
          .map((c) => ({ id: c.id, title: c.title }));
        setProgressCoursesCatalog(list);
      })
      .catch(() => setProgressCoursesCatalog([]));
  }, [activeTab, user?.id]);

  useEffect(() => {
    if (!user?.id || activeTab !== 'progress') return;
    if (!progressScopeCourseIds || progressScopeCourseIds.length === 0) {
      setProgressScopedDetail(null);
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setLoadingProgressScope(true);
      try {
        const d = await levelProgressionService.getProfileProgressDetail(progressScopeCourseIds);
        if (!cancelled) setProgressScopedDetail(d && typeof d === 'object' ? d : null);
      } catch {
        if (!cancelled) setProgressScopedDetail(null);
      } finally {
        if (!cancelled) setLoadingProgressScope(false);
      }
    }, 280);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [activeTab, user?.id, progressScopeCourseIds]);

  const loadUnreadCount = async () => {
    if (!user) return;
    try {
      const count = await messageService.getUnreadCount();
      setUnreadMessageCount(count);
    } catch (error) {
      console.error('Failed to load unread count:', error);
    }
  };

  const loadUnreadCountRef = useRef(loadUnreadCount);
  loadUnreadCountRef.current = loadUnreadCount;

  const loadFavoritesData = async () => {
    setLoadingFavorites(true);
    try {
      const [lessonList, libraryList, tagBlockList, tagLessonList] = await Promise.all([
        favoriteService.getFavorites().catch(() => []),
        libraryService.getFavoriteBooks().catch(() => []),
        favoriteService.getFavoriteTags().catch(() => []),
        favoriteService.getFavoriteTagLessons().catch(() => [])
      ]);
      setFavoriteLessons(lessonList);
      setFavoriteLibraryBooks(libraryList);
      setFavoriteTags(tagBlockList);
      setFavoriteTagLessons(tagLessonList);
    } catch (error) {
      console.error('Failed to load favorites:', error);
      setFavoriteLessons([]);
      setFavoriteLibraryBooks([]);
      setFavoriteTags([]);
      setFavoriteTagLessons([]);
    } finally {
      setLoadingFavorites(false);
    }
  };

  const loadGoalsData = useCallback(async () => {
    setLoadingGoals(true);
    try {
      const prefs = await authService.getLearningPreferences();
      const normalized = {
        ...prefs,
        preferredLanguage: prefs.preferredLanguage === 'fa' ? 'fa' : 'en',
      };
      setGoalsPrefs(normalized);
      setGoalsSavedPrefs(normalized);
      setGoalsDirty(false);
    } catch (error) {
      console.error('Failed to load learning preferences:', error);
      setGoalsPrefs(null);
      setGoalsSavedPrefs(null);
    } finally {
      setLoadingGoals(false);
    }
  }, []);

  // Lazy-load learning preferences for Goals & Time, and for Progress (activity chart label / server TZ alignment).
  useEffect(() => {
    if (!user?.id || loading) return;
    if (activeTab !== 'goals' && activeTab !== 'progress') return;
    if (goalsPrefs || loadingGoals) return;
    void loadGoalsData();
  }, [activeTab, user?.id, loading, goalsPrefs, loadingGoals, loadGoalsData]);

  // When the learner changes their time zone from the Quick settings modal, refresh the
  // chached learning preferences and the progress detail so the activity-pattern chart
  // rebuckets against the new zone (server uses ApplicationUser.TimeZone for grouping).
  useEffect(() => {
    if (!user?.id) return;
    const onTimezoneChanged = () => {
      void loadGoalsData();
      if (activeTabRef.current === 'progress') {
        void loadProgressData();
      }
    };
    window.addEventListener(LEARNER_TIMEZONE_CHANGED_EVENT, onTimezoneChanged);
    return () => window.removeEventListener(LEARNER_TIMEZONE_CHANGED_EVENT, onTimezoneChanged);
  }, [user?.id, loadGoalsData, loadProgressData]);

  const updateGoalsField = useCallback(<K extends keyof LearningPreferencesDto>(key: K, value: LearningPreferencesDto[K]) => {
    setGoalsPrefs((prev) => (prev ? { ...prev, [key]: value } : prev));
    setGoalsDirty(true);
  }, []);

  const handleSaveGoals = useCallback(async () => {
    if (!goalsPrefs || savingGoals) return;
    const clampedMinutes = Math.min(480, Math.max(5, Math.round(goalsPrefs.dailyLearningGoalMinutes || 30)));
    const payload: LearningPreferencesDto = {
      ...goalsPrefs,
      dailyLearningGoalMinutes: clampedMinutes,
      preferredLanguage: goalsPrefs.preferredLanguage === 'fa' ? 'fa' : 'en',
      hasSetDailyLearningGoal: true,
    };
    setSavingGoals(true);
    try {
      const saved = await authService.updateLearningPreferences(payload);
      const normalized = {
        ...saved,
        preferredLanguage: saved.preferredLanguage === 'fa' ? 'fa' : 'en',
      };
      setGoalsPrefs(normalized);
      setGoalsSavedPrefs(normalized);
      setGoalsDirty(false);
      showSuccess(tProfile('goals.savedSuccess'));
      notifyGamificationAfterAction();
      window.dispatchEvent(new CustomEvent(DAILY_LEARNING_GOAL_CHANGED_EVENT));
      if (activeTabRef.current === 'progress') {
        void loadProgressData();
      }
    } catch (error) {
      console.error('Failed to update learning preferences:', error);
      showError(error instanceof Error ? error.message : tProfile('goals.saveFailed'));
    } finally {
      setSavingGoals(false);
    }
  }, [goalsPrefs, savingGoals, showSuccess, showError, loadProgressData, tProfile]);

  const handleSaveDailyCommitment = useCallback(async () => {
    if (!goalsPrefs || !goalsSavedPrefs || savingDailyCommitment || savingGoals) return;
    const clampedMinutes = Math.min(480, Math.max(5, Math.round(goalsPrefs.dailyLearningGoalMinutes || 30)));
    const payload: LearningPreferencesDto = {
      ...goalsSavedPrefs,
      dailyLearningGoalMinutes: clampedMinutes,
      preferredLanguage: goalsSavedPrefs.preferredLanguage === 'fa' ? 'fa' : 'en',
      hasSetDailyLearningGoal: true,
    };
    setSavingDailyCommitment(true);
    try {
      const saved = await authService.updateLearningPreferences(payload);
      const normalizedSaved = {
        ...saved,
        preferredLanguage: saved.preferredLanguage === 'fa' ? 'fa' : 'en',
      };
      setGoalsSavedPrefs(normalizedSaved);
      setGoalsPrefs((prev) => {
        if (!prev) return normalizedSaved;
        return {
          ...normalizedSaved,
          learningGoals: prev.learningGoals,
          preferredLearningStyle: prev.preferredLearningStyle,
          preferredLanguage: prev.preferredLanguage === 'fa' ? 'fa' : 'en',
        };
      });
      setGoalsDirty(
        goalsPrefs.learningGoals !== normalizedSaved.learningGoals ||
          goalsPrefs.preferredLearningStyle !== normalizedSaved.preferredLearningStyle ||
          (goalsPrefs.preferredLanguage === 'fa' ? 'fa' : 'en') !== normalizedSaved.preferredLanguage
      );
      showSuccess(tProfile('goals.dailyTargetSaved', { defaultValue: 'Daily study time saved' }));
      notifyGamificationAfterAction();
      window.dispatchEvent(new CustomEvent(DAILY_LEARNING_GOAL_CHANGED_EVENT));
    } catch (error) {
      console.error('Failed to save daily time commitment:', error);
      showError(error instanceof Error ? error.message : tProfile('goals.saveFailed'));
    } finally {
      setSavingDailyCommitment(false);
    }
  }, [goalsPrefs, goalsSavedPrefs, savingDailyCommitment, savingGoals, showSuccess, showError, tProfile]);

  useEffect(() => {
    if (activeTab !== 'goals' || !goalsPrefs || loadingGoals) return;
    let pending = false;
    try {
      pending = window.sessionStorage.getItem(FOCUS_DAILY_COMMITMENT_STORAGE_KEY) === '1';
    } catch {
      pending = false;
    }
    if (!pending) return;

    try {
      window.sessionStorage.removeItem(FOCUS_DAILY_COMMITMENT_STORAGE_KEY);
    } catch {
      /* ignore */
    }

    let cancelled = false;
    const timer = window.setTimeout(() => {
      if (cancelled) return;
      const el = dailyCommitmentRef.current;
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.focus({ preventScroll: true });
      setDailyCommitmentPulse(true);
      window.setTimeout(() => {
        if (!cancelled) setDailyCommitmentPulse(false);
      }, 1800);
    }, 380);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [activeTab, goalsPrefs, loadingGoals, dailyCommitmentFocusNonce]);

  useEffect(() => {
    const onFocusRequest = () => {
      try {
        window.sessionStorage.setItem(FOCUS_DAILY_COMMITMENT_STORAGE_KEY, '1');
      } catch {
        /* ignore */
      }
      if (activeTabRef.current !== 'goals') {
        setActiveTab('goals');
      }
      setDailyCommitmentFocusNonce((n) => n + 1);
    };
    window.addEventListener(FOCUS_DAILY_COMMITMENT_EVENT, onFocusRequest);
    return () => window.removeEventListener(FOCUS_DAILY_COMMITMENT_EVENT, onFocusRequest);
  }, []);

  // Also refresh when initialUser prop changes (from App.tsx)
  useEffect(() => {
    if (initialUser && initialUser.id !== user?.id) {
      setUser(initialUser);
      loadUnreadCount();
    }
  }, [initialUser, user]);

  // Load unread count when user changes and set up periodic refresh
  useEffect(() => {
    if (!user) return;
    
    loadUnreadCount();
    // Refresh every 30 seconds (same as Navigation component)
    const interval = setInterval(loadUnreadCount, 30000);
    
    // Listen for message read events to refresh immediately
    const handleMessageRead = () => {
      // Refresh immediately and again after a delay to ensure DB is updated
      loadUnreadCount();
      setTimeout(() => {
        loadUnreadCount();
      }, 500);
    };
    window.addEventListener('messageRead', handleMessageRead);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('messageRead', handleMessageRead);
    };
  }, [user]);

  useEffect(() => {
    if (!user || loading) return;
    let cancelled = false;
    authService
      .getProfilePhotoUploadLimits()
      .then((l) => {
        if (!cancelled) {
          setProfilePhotoLimits({
            maxSizeBytes: l.maxSizeBytes,
            allowedImageFormats: l.allowedImageFormats,
            maxImageWidth: l.maxImageWidth,
            maxImageHeight: l.maxImageHeight
          });
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [user, loading]);

  // Refresh favorites list when key changes (e.g. after remove)
  useEffect(() => {
    if (favoritesRefreshKey > 0) loadFavoritesData();
  }, [favoritesRefreshKey]);

  const mergedTopicTagFavorites = useMemo(() => {
    type TopicTagFavGroup = {
      tagId: number;
      tagName: string;
      tagDescription?: string;
      color: string;
      blocks: FavoriteTagBlockItemDto[];
      lessons: FavoriteTagLessonItemDto[];
    };
    const map = new Map<number, TopicTagFavGroup>();
    const makeGroup = (
      tagId: number,
      tagName: string,
      color: string,
      tagDescription?: string
    ): TopicTagFavGroup => ({
      tagId,
      tagName,
      color,
      blocks: [],
      lessons: [],
      ...(tagDescription != null && tagDescription !== '' ? { tagDescription } : {}),
    });
    for (const b of favoriteTags) {
      let entry = map.get(b.tagId);
      if (!entry) {
        entry = makeGroup(
          b.tagId,
          pickTagName({ namePublished: b.tagName, namePublishedFa: b.tagNameFa }),
          b.color,
          pickTagDescription({
            descriptionPublished: b.tagDescription,
            descriptionPublishedFa: b.tagDescriptionFa
          }) || undefined
        );
        map.set(b.tagId, entry);
      }
      entry.blocks.push(b);
    }
    for (const l of favoriteTagLessons) {
      let entry = map.get(l.tagId);
      if (!entry) {
        entry = makeGroup(
          l.tagId,
          pickTagName({ namePublished: l.tagName, namePublishedFa: l.tagNameFa }),
          l.color,
          pickTagDescription({
            descriptionPublished: l.tagDescription,
            descriptionPublishedFa: l.tagDescriptionFa
          }) || undefined
        );
        map.set(l.tagId, entry);
      }
      entry.lessons.push(l);
    }
    return [...map.values()].sort((a, b) => a.tagName.localeCompare(b.tagName));
  }, [favoriteTags, favoriteTagLessons, pickTagName, pickTagDescription]);

  const favoriteTopicTagsItemCount = useMemo(
    () => mergedTopicTagFavorites.reduce((sum, g) => sum + g.blocks.length + g.lessons.length, 0),
    [mergedTopicTagFavorites]
  );

  useEffect(() => {
    if (activeTab !== 'favorites') {
      setFavoriteRemovePendingKey(null);
      setFavoriteRemoveClosingKey(null);
    }
  }, [activeTab]);

  const dismissFavoriteRemoveConfirm = useCallback((itemKey: string) => {
    if (favoriteRemoveClosingKey === itemKey) return;
    setFavoriteRemoveClosingKey(itemKey);
    window.setTimeout(() => {
      setFavoriteRemovePendingKey((current) => (current === itemKey ? null : current));
      setFavoriteRemoveClosingKey((current) => (current === itemKey ? null : current));
    }, favoriteConfirmAnimMs);
  }, [favoriteRemoveClosingKey, favoriteConfirmAnimMs]);

  useEffect(() => {
    const pendingKey = favoriteRemovePendingKey;
    if (!pendingKey || activeTab !== 'favorites') return;

    const handlePointerDown = (event: PointerEvent) => {
      const root = document.querySelector(`[data-favorite-confirm-root="${pendingKey}"]`);
      if (root?.contains(event.target as Node)) return;
      dismissFavoriteRemoveConfirm(pendingKey);
    };

    const timeoutId = window.setTimeout(() => {
      document.addEventListener('pointerdown', handlePointerDown, true);
    }, 0);

    return () => {
      window.clearTimeout(timeoutId);
      document.removeEventListener('pointerdown', handlePointerDown, true);
    };
  }, [favoriteRemovePendingKey, activeTab, dismissFavoriteRemoveConfirm]);

  const renderFavoriteRemoveControl = useCallback(
    (itemKey: string, onRemove: () => Promise<void>) => {
      const isPending = favoriteRemovePendingKey === itemKey;
      const isClosing = favoriteRemoveClosingKey === itemKey;
      const isOpen = isPending || isClosing;
      const isRemoving = favoriteRemovingKey === itemKey;

      return (
        <div className="relative flex flex-shrink-0 items-center self-stretch pr-1">
          {isOpen && (
            <div
              data-favorite-confirm-root={itemKey}
              className={`absolute right-full top-1/2 z-10 mr-1.5 flex min-w-[10.75rem] -translate-y-1/2 items-center gap-2 rounded-lg border border-gray-400/35 bg-white/20 px-3 py-0.5 shadow-lg backdrop-blur-[2px] dark:border-gray-500/35 dark:bg-gray-950/20 ${
                isClosing ? 'animate-favorite-confirm-out' : 'animate-favorite-confirm-in'
              }`}
              onClick={(e) => e.stopPropagation()}
              role="group"
              aria-label={tProfile('confirmRemoveFavorite')}
            >
              <span className="whitespace-nowrap text-xs font-semibold text-gray-800 drop-shadow-sm dark:text-gray-100">
                Remove?
              </span>
              <button
                type="button"
                disabled={isRemoving || isClosing}
                onClick={(e) => {
                  e.stopPropagation();
                  dismissFavoriteRemoveConfirm(itemKey);
                }}
                className="min-w-[2.25rem] rounded-md px-2.5 py-1 text-xs font-medium text-gray-800 hover:bg-black/10 disabled:opacity-50 dark:text-gray-100 dark:hover:bg-white/10"
              >
                No
              </button>
              <button
                type="button"
                disabled={isRemoving || isClosing}
                onClick={async (e) => {
                  e.stopPropagation();
                  setFavoriteRemovingKey(itemKey);
                  try {
                    await onRemove();
                    setFavoriteRemovePendingKey(null);
                    setFavoriteRemoveClosingKey(null);
                    showSuccess(tProfile('favorites.removedSuccess'));
                  } catch (err) {
                    handleApiError(err);
                  } finally {
                    setFavoriteRemovingKey(null);
                  }
                }}
                className="min-w-[2.25rem] rounded-md bg-rose-500/25 px-2.5 py-1 text-xs font-medium text-rose-800 hover:bg-rose-500/35 disabled:opacity-50 dark:text-rose-200 dark:hover:bg-rose-500/30"
              >
                {isRemoving ? (
                  <Loader2 className="mx-auto h-4 w-4 animate-spin" aria-hidden />
                ) : (
                  'Yes'
                )}
              </button>
            </div>
          )}
          <button
            type="button"
            disabled={isRemoving || isOpen}
            onClick={(e) => {
              e.stopPropagation();
              if (isOpen) return;
              setFavoriteRemoveClosingKey(null);
              setFavoriteRemovePendingKey(itemKey);
            }}
            className="relative z-20 shrink-0 rounded-lg p-2 transition-colors hover:bg-gray-200 disabled:opacity-100 dark:hover:bg-gray-600"
            title={tProfile('removeFromFavorites')}
            aria-label={tProfile('removeFromFavorites')}
          >
            {isRemoving && !isOpen ? (
              <Loader2 className="h-5 w-5 animate-spin text-rose-500 dark:text-rose-400" aria-hidden />
            ) : (
              <Heart className="h-5 w-5 fill-rose-500 text-rose-600 dark:fill-rose-500 dark:text-rose-400" />
            )}
          </button>
        </div>
      );
    },
    [
      favoriteRemovePendingKey,
      favoriteRemoveClosingKey,
      favoriteRemovingKey,
      dismissFavoriteRemoveConfirm,
      handleApiError,
      showSuccess,
    ]
  );

  const loadUserProfile = async () => {
    try {
      console.log('UserProfileDashboard: Loading user profile...');
      const profile = await authService.getProfile();
      console.log('UserProfileDashboard: Loaded profile:', profile);
      console.log('UserProfileDashboard: Profile has photo:', profile.profilePhoto ? 'YES' : 'NO');
      if (profile.profilePhoto) {
        console.log('UserProfileDashboard: Photo URL:', profile.profilePhoto);
      }
      setUser(profile);
      setFormData({
        firstName: profile.firstName || '',
        lastName: profile.lastName || '',
        email: profile.email || '',
        userName: profile.userName || '',
        phoneNumber: toStoredE164Input(profile.phoneNumber),
        bio: profile.bio || '',
        nationality: profile.nationality || '',
        age: profile.age || undefined,
        gender: profile.gender || ''
      });
      // Refresh unread count after profile is loaded
      loadUnreadCount();
    } catch (err) {
      console.error('UserProfileDashboard: Failed to load profile:', err);
      setError(err instanceof Error ? err.message : tProfile('form.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) =>
    formatAppDate(dateString, locale, { year: 'numeric', month: 'long', day: 'numeric' });

  const handlePhoneNumberChange = (newPhoneNumber: string) => {
    setFormData({ ...formData, phoneNumber: newPhoneNumber });
    setPhoneNumberError(null);
  };

  const handlePhoneVerified = (profile: UserProfileDto) => {
    setFormData((prev) => ({
      ...prev,
      phoneNumber: toStoredE164Input(profile.phoneNumber) || prev.phoneNumber
    }));
    setUser(profile);
    onUserUpdate?.(profile);
    setPhoneNumberError(null);
    showSuccess(tProfile('formEdit.phoneSavedVerified'));
  };

  const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    console.log('UserProfileDashboard: Photo upload started:', {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type
    });

    // Validate file
    const formatOpts = profileFormatsCsvToImageOptions(profilePhotoLimits.allowedImageFormats);
    const validation = validateImageFile(file, {
      maxSizeBytes: profilePhotoLimits.maxSizeBytes,
      ...formatOpts
    });
    if (!validation.isValid) {
      const errorMessage = getValidationErrorMessage(validation);
      handleUploadError(new Error(errorMessage));
      return;
    }

    try {
      const { width, height } = await getImageDimensions(file);
      if (width > profilePhotoLimits.maxImageWidth || height > profilePhotoLimits.maxImageHeight) {
        handleUploadError(
          new Error(
            `Image is ${width}×${height}px. Maximum allowed is ${profilePhotoLimits.maxImageWidth}×${profilePhotoLimits.maxImageHeight}px.`
          )
        );
        return;
      }
    } catch {
      handleUploadError(new Error('Could not read this image. Try another file.'));
      return;
    }

    setUploadingPhoto(true);
    try {
      console.log('UserProfileDashboard: Calling authService.uploadProfilePhoto...');
      const updatedUser = await authService.uploadProfilePhoto(file);

      console.log('UserProfileDashboard: Photo upload successful, updated user:', updatedUser);
      console.log('UserProfileDashboard: New photo URL:', updatedUser.profilePhoto);

      // Clear cache and fetch fresh profile from DB to ensure we have the latest photo
      try {
        const freshProfile = await authService.getProfile(true);
        setUser(freshProfile);
        onUserUpdate?.(freshProfile);
        // Refresh unread count after profile refresh
        loadUnreadCount();
      } catch (refreshError) {
        console.warn('Failed to refresh profile after photo upload:', refreshError);
        // Fallback to updated user if refresh fails
        setUser(updatedUser);
        onUserUpdate?.(updatedUser);
        // Refresh unread count even on fallback
        loadUnreadCount();
      }

      // Clear the file input to allow re-uploading the same file
      const fileInput = document.getElementById('photo-upload') as HTMLInputElement;
      if (fileInput) {
        fileInput.value = '';
      }

      showSuccess(tProfile('form.photoUpdateSuccess'));
      notifyGamificationAfterAction();
    } catch (error) {
      console.error('UserProfileDashboard: Photo upload failed:', error);
      handleUploadError(error);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleUpdateProfile = async (event: React.FormEvent) => {
    event.preventDefault();
    setUpdating(true);
    setError(null);

    const phoneFormatError = getIranPhoneNumberFormatError(formData.phoneNumber);
    if (phoneFormatError) {
      setPhoneNumberError(phoneFormatError);
      setUpdating(false);
      return;
    }

    const normalizedPhone = formData.phoneNumber.trim()
      ? normalizeIranPhoneNumber(formData.phoneNumber)
      : '';

    if (normalizedPhone && !iranPhonesAreEquivalent(normalizedPhone, user?.phoneNumber || '')) {
      setPhoneNumberError(tProfile('formEdit.phoneVerifyBeforeSave'));
      setUpdating(false);
      return;
    }

    try {
      const updateData: UpdateProfileDto = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phoneNumber: normalizedPhone,
        bio: formData.bio,
        nationality: formData.nationality,
        gender: formData.gender
      };
      if (formData.age !== undefined) {
        updateData.age = formData.age;
      }
      if (!formData.gender) {
        delete updateData.gender;
      }

      console.log('UserProfileDashboard: Updating profile with data:', updateData);
      const updatedUser = await authService.updateProfile(updateData);
      console.log('UserProfileDashboard: Profile updated successfully:', updatedUser);
      
      // Use the response directly instead of making another API call
      setUser(updatedUser);
      onUserUpdate?.(updatedUser);
      // Refresh unread count after profile update
      loadUnreadCount();
      
      setEditMode(false);
      setPhoneNumberError(null);
      showSuccess(tProfile('form.updateSuccess'));
      notifyGamificationAfterAction();
    } catch (error) {
      console.error('UserProfileDashboard: Profile update failed:', error);
      handleApiError(error);
      setError(error instanceof Error ? error.message : tProfile('form.updateFailed'));
    } finally {
      setUpdating(false);
    }
  };

  // Click tab → switch active tab; the carousel translate + auto-height effects react to the new value.
  const handleTabChange = (newTab: TabType, event?: React.MouseEvent) => {
    if (newTab === activeTabRef.current) return;
    if (event) event.preventDefault();
    const track = cardsScrollRef.current;
    dragOffsetPxRef.current = 0;
    dragUiRef.current = { isDragging: false, dragOffsetPx: 0 };
    if (track) {
      track.style.transition = TAB_CAROUSEL_TRANSITION;
      track.style.setProperty('--profile-tab-drag-x', '0px');
    }
    setIsDragging(false);
    activeTabRef.current = newTab;
    setActiveTab(newTab);
    updateIndicator();
    queueMicrotask(() => pushProfileTabHistory(newTab));

    if (newTab === 'messages') {
      loadUnreadCount();
      setInboxRefreshKey((prev) => prev + 1);
      setTimeout(() => window.dispatchEvent(new CustomEvent('refreshInbox')), 100);
    }
  };

  useLayoutEffect(() => {
    if (loading || !user) return;
    const tabBar = profileTabBarRef.current;
    if (!tabBar) return;
    const updateHeight = () => {
      // Layout size, not getBoundingClientRect: the visual box of a sticky node
      // wobbles during mobile scroll and would re-render the bar.
      const height = tabBar.offsetHeight;
      if (height < 1) return;
      setProfileTabBarHeight((prev) => (prev === height ? prev : height));
    };
    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(tabBar);
    return () => observer.disconnect();
  }, [loading, user]);

  const readTabPaneHeight = useCallback((target: HTMLElement) => {
    // contentRect.height misses overflow / late layout (charts, images). scrollHeight + offsetHeight covers full painted column.
    return Math.ceil(Math.max(target.scrollHeight, target.offsetHeight, target.getBoundingClientRect().height));
  }, []);

  const applyActiveTabHeight = useCallback((tab: TabType, h: number, commit: boolean) => {
    if (h < 1) return;
    const container = tabContentContainerRef.current;
    if (container && activeTabRef.current === tab) {
      container.style.height = `${h}px`;
    }
    if (commit) {
      window.clearTimeout(tabHeightCommitTimerRef.current);
      pendingTabHeightRef.current = null;
      setTabHeights((prev) => (prev[tab] === h ? prev : { ...prev, [tab]: h }));
      return;
    }
    // Follow CSS collapse/expand on the shell without re-rendering the whole carousel every frame
    // (that is what made the Messages filter feel slower than Library).
    pendingTabHeightRef.current = { tab, h };
    window.clearTimeout(tabHeightCommitTimerRef.current);
    tabHeightCommitTimerRef.current = window.setTimeout(() => {
      const pending = pendingTabHeightRef.current;
      if (!pending) return;
      pendingTabHeightRef.current = null;
      setTabHeights((prev) => (prev[pending.tab] === pending.h ? prev : { ...prev, [pending.tab]: pending.h }));
    }, 50);
  }, []);

  const syncActiveTabHeight = useCallback(() => {
    const tab = activeTabRef.current;
    const el = tabContentRefs.current[tab];
    if (!el) return;
    applyActiveTabHeight(tab, readTabPaneHeight(el), true);
  }, [applyActiveTabHeight, readTabPaneHeight]);

  // Only observe the active pane. Off-screen siblings in the translate carousel often report a clipped
  // or stretched size on mobile WebKit, which then locks the shell too short until a full refresh.
  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') return;
    const tab = activeTab;
    const el = tabContentRefs.current[tab];
    if (!el) return;
    let raf = 0;
    const obs = new ResizeObserver(() => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        const target = tabContentRefs.current[tab];
        if (!target || activeTabRef.current !== tab) return;
        applyActiveTabHeight(tab, readTabPaneHeight(target), false);
      });
    });
    obs.observe(el);
    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      window.clearTimeout(tabHeightCommitTimerRef.current);
      obs.disconnect();
    };
  }, [activeTab, loading, user, readTabPaneHeight, applyActiveTabHeight]);

  // Re-read height after the slide settles so resizing the shell does not interrupt the transform animation.
  useLayoutEffect(() => {
    const timer = window.setTimeout(() => {
      schedulePaneHeightSync(syncActiveTabHeight);
    }, isDragging ? 0 : 300);
    return () => window.clearTimeout(timer);
  }, [activeTab, syncActiveTabHeight, isDragging, loadingProgress, loadingProgressScope, progressScopedDetail, progressScopeCourseIds, loadingSubscription, loadingFavorites, loadingGoals, goalsPrefs, goalsDirty, savingGoals, inboxRefreshKey, favoritesRefreshKey, kpRefresh]);

  // Custom one-tab-per-gesture touch carousel. Replaces native scroll-snap so a single fast swipe can
  // never skip past the next tab, and so we can drive the tab indicator from the live drag offset.
  useEffect(() => {
    if (simpleHome) return undefined;
    const track = cardsScrollRef.current;
    if (!track) return;

    const listenTargets = [track];
    const cleanups: Array<() => void> = [];

    for (const listenEl of listenTargets) {
      let startX = 0;
      let startY = 0;
      let lastX = 0;
      let dragging = false;
      let axis: 'horizontal' | 'vertical' | null = null;
      let ignoreCarouselTouch = false;
      let suppressClick = false;
      let horizontalStripEl: HTMLElement | null = null;
      let activityHeatmapScrollEl: HTMLElement | null = null;
      const SWIPE_DISTANCE_PX = 50;
      const SWIPE_VELOCITY_PX_PER_MS = 0.4;
      let startTime = 0;

      const resetTrackDrag = () => {
        dragOffsetPxRef.current = 0;
        dragUiRef.current = { isDragging: false, dragOffsetPx: 0 };
        track.style.transition = TAB_CAROUSEL_TRANSITION;
        track.style.setProperty('--profile-tab-drag-x', '0px');
        setIsDragging(false);
      };

      const onTouchStart = (e: TouchEvent) => {
        const t = e.touches[0];
        if (!t || e.touches.length !== 1) return;
        const target = e.target;
        ignoreCarouselTouch =
          target instanceof Element && typeof target.closest === 'function' && !!target.closest('[data-no-tab-swipe]');
        horizontalStripEl =
          target instanceof Element && typeof target.closest === 'function'
            ? (target.closest('.horizontal-filter-pill-strip') as HTMLElement | null)
            : null;
        activityHeatmapScrollEl =
          target instanceof Element && typeof target.closest === 'function'
            ? (target.closest('.activity-heatmap-scroll') as HTMLElement | null)
            : null;
        if (ignoreCarouselTouch) return;
        startX = t.clientX;
        startY = t.clientY;
        lastX = t.clientX;
        startTime = Date.now();
        dragging = false;
        axis = null;
      };

      const onTouchMove = (e: TouchEvent) => {
        if (ignoreCarouselTouch) return;
        const t = e.touches[0];
        if (!t || e.touches.length !== 1) return;
        const dx = t.clientX - startX;
        const dy = t.clientY - startY;
        lastX = t.clientX;

        if (axis === null) {
          if (Math.abs(dx) > 8 || Math.abs(dy) > 8) {
            axis = Math.abs(dx) > Math.abs(dy) ? 'horizontal' : 'vertical';
          }
        }
        // Allow normal vertical page scroll when the gesture is vertical (including over the heatmap).
        if (axis === 'vertical') {
          if (dragging) {
            dragging = false;
            resetTrackDrag();
          }
          return;
        }
        if (axis !== 'horizontal') return;

        if (horizontalStripEl) {
          const tolerance = 2;
          const atLeft = horizontalStripEl.scrollLeft <= tolerance;
          const atRight =
            horizontalStripEl.scrollLeft + horizontalStripEl.clientWidth >=
            horizontalStripEl.scrollWidth - tolerance;
          const canScrollStrip = (dx > 0 && !atLeft) || (dx < 0 && !atRight);
          if (canScrollStrip) {
            if (dragging) {
              dragging = false;
              resetTrackDrag();
            }
            return;
          }
        }

        if (activityHeatmapScrollEl) {
          const tolerance = 2;
          const atLeft = activityHeatmapScrollEl.scrollLeft <= tolerance;
          const atRight =
            activityHeatmapScrollEl.scrollLeft + activityHeatmapScrollEl.clientWidth >=
            activityHeatmapScrollEl.scrollWidth - tolerance;
          const canScrollHeatmap = (dx > 0 && !atLeft) || (dx < 0 && !atRight);
          if (canScrollHeatmap) {
            if (dragging) {
              dragging = false;
              resetTrackDrag();
            }
            return;
          }
        }

        if (!dragging) {
          dragging = true;
          track.style.transition = 'none';
          setIsDragging(true);
        }
        // resist sliding past the first/last tab
        const idx = TAB_ORDER.indexOf(activeTabRef.current);
        const width = track.clientWidth || 1;
        const rtl = isRtlRef.current;
        let offset = dx;
        const atFirstRubber = rtl ? idx === 0 && offset < 0 : idx === 0 && offset > 0;
        const atLastRubber = rtl ? idx === TAB_ORDER.length - 1 && offset > 0 : idx === TAB_ORDER.length - 1 && offset < 0;
        if (atFirstRubber || atLastRubber) {
          offset = offset / 3; // rubber-band
        }
        // clamp so the indicator interpolation never thinks we crossed multiple tabs in one gesture
        offset = Math.max(-width, Math.min(width, offset));
        dragOffsetPxRef.current = offset;
        dragUiRef.current = { isDragging: true, dragOffsetPx: offset };
        track.style.setProperty('--profile-tab-drag-x', `${offset}px`);
        updateIndicatorRef.current();
        e.preventDefault();
      };

      const finishDrag = () => {
        const offset = dragOffsetPxRef.current;
        const width = track.clientWidth || 1;
        const elapsed = Math.max(1, Date.now() - startTime);
        const velocity = (lastX - startX) / elapsed;
        const idx = TAB_ORDER.indexOf(activeTabRef.current);
        const rtl = isRtlRef.current;
        let nextIdx = idx;
        if (rtl) {
          if ((offset > SWIPE_DISTANCE_PX || velocity > SWIPE_VELOCITY_PX_PER_MS) && idx < TAB_ORDER.length - 1) {
            nextIdx = idx + 1;
          } else if ((offset < -SWIPE_DISTANCE_PX || velocity < -SWIPE_VELOCITY_PX_PER_MS) && idx > 0) {
            nextIdx = idx - 1;
          } else if (Math.abs(offset) > width / 2) {
            nextIdx = offset > 0
              ? Math.min(TAB_ORDER.length - 1, idx + 1)
              : Math.max(0, idx - 1);
          }
        } else if ((offset < -SWIPE_DISTANCE_PX || velocity < -SWIPE_VELOCITY_PX_PER_MS) && idx < TAB_ORDER.length - 1) {
          nextIdx = idx + 1;
        } else if ((offset > SWIPE_DISTANCE_PX || velocity > SWIPE_VELOCITY_PX_PER_MS) && idx > 0) {
          nextIdx = idx - 1;
        } else if (Math.abs(offset) > width / 2) {
          nextIdx = offset < 0
            ? Math.min(TAB_ORDER.length - 1, idx + 1)
            : Math.max(0, idx - 1);
        }
        const nextTab = TAB_ORDER[nextIdx];
        const currentTab = activeTabRef.current;
        const fromCarousel = tabCarouselIndexRef.current(currentTab);
        const toCarousel = nextTab ? tabCarouselIndexRef.current(nextTab) : fromCarousel;

        if (nextTab && nextTab !== currentTab) {
          // Keep the finger position continuous, then ease the remaining distance.
          settleCarouselDrag({
            track,
            dragVar: '--profile-tab-drag-x',
            fromCarouselIndex: fromCarousel,
            toCarouselIndex: toCarousel,
            offsetPx: offset,
            paneWidthPx: width,
            onSettling: () => {
              activeTabRef.current = nextTab;
              dragOffsetPxRef.current = offset + (toCarousel - fromCarousel) * width;
              dragUiRef.current = { isDragging: true, dragOffsetPx: dragOffsetPxRef.current };
              setActiveTab(nextTab);
            },
            onReleased: () => {
              dragOffsetPxRef.current = 0;
              dragUiRef.current = { isDragging: false, dragOffsetPx: 0 };
              setIsDragging(false);
              updateIndicatorRef.current();
            }
          });
          queueMicrotask(() => pushProfileTabHistoryRef.current(nextTab));
          if (nextTab === 'messages') {
            void loadUnreadCountRef.current();
            setInboxRefreshKey((prev) => prev + 1);
            setTimeout(() => window.dispatchEvent(new CustomEvent('refreshInbox')), 100);
          }
        } else {
          resetTrackDrag();
          queueMicrotask(() => updateIndicatorRef.current());
        }
      };

      const onTouchEnd = () => {
        if (ignoreCarouselTouch) {
          ignoreCarouselTouch = false;
          return;
        }
        if (!dragging) return;
        suppressClick = true;
        dragging = false;
        finishDrag();
      };

      const onTouchCancel = () => {
        if (ignoreCarouselTouch) {
          ignoreCarouselTouch = false;
          return;
        }
        if (!dragging) return;
        dragging = false;
        resetTrackDrag();
      };

      const onClickCapture = (e: MouseEvent) => {
        if (!suppressClick) return;
        suppressClick = false;
        e.preventDefault();
        e.stopPropagation();
      };

      listenEl.addEventListener('touchstart', onTouchStart, { passive: true });
      listenEl.addEventListener('touchmove', onTouchMove, { passive: false });
      listenEl.addEventListener('touchend', onTouchEnd, { passive: true });
      listenEl.addEventListener('touchcancel', onTouchCancel, { passive: true });
      listenEl.addEventListener('click', onClickCapture, true);
      cleanups.push(() => {
        listenEl.removeEventListener('touchstart', onTouchStart);
        listenEl.removeEventListener('touchmove', onTouchMove);
        listenEl.removeEventListener('touchend', onTouchEnd);
        listenEl.removeEventListener('touchcancel', onTouchCancel);
        listenEl.removeEventListener('click', onClickCapture, true);
      });
    }

    return () => {
      cleanups.forEach((cleanup) => cleanup());
    };
  }, [loading, user, activeTab, simpleHome]);

  useEffect(() => {
    const syncPinnedProfileActions = () => {
      if (activeTabRef.current !== 'profile' || dragUiRef.current.isDragging) {
        setPinProfileActions(false);
        return;
      }
      const sentinel = profileActionsSentinelRef.current;
      const bar = profileTabBarRef.current;
      if (!sentinel || !bar) {
        setPinProfileActions(false);
        return;
      }
      const barRect = bar.getBoundingClientRect();
      const sentinelRect = sentinel.getBoundingClientRect();
      // Begin the sticky strip when the tab bar reaches the gap above the buttons, so they are already in their stuck position.
      const shouldPin = sentinelRect.top <= barRect.bottom + PROFILE_ACTIONS_STICK_OFFSET_PX;
      setPinProfileActions((prev) => (prev === shouldPin ? prev : shouldPin));
      if (shouldPin) {
        const next = {
          top: barRect.bottom,
          left: sentinelRect.left,
          width: sentinelRect.width
        };
        setPinnedActionsFrame((prev) =>
          prev &&
          prev.top === next.top &&
          prev.left === next.left &&
          prev.width === next.width
            ? prev
            : next
        );
      }
    };
    syncPinnedProfileActions();
    document.addEventListener('scroll', syncPinnedProfileActions, { passive: true, capture: true });
    window.addEventListener('resize', syncPinnedProfileActions);
    return () => {
      document.removeEventListener('scroll', syncPinnedProfileActions, { capture: true });
      window.removeEventListener('resize', syncPinnedProfileActions);
    };
  }, [activeTab, isDragging, user, loading]);

  const tabContentHeroMeta = useMemo(() => {
    const tabs: NonProfileTab[] = ['goals', 'subscription', 'progress', 'messages', 'comments', 'favorites'];
    return Object.fromEntries(
      tabs.map((tab) => [
        tab,
        {
          title: tProfile(`tabHero.${tab}.title`),
          description: tProfile(`tabHero.${tab}.description`),
          eyebrow: tProfile(`tabHero.${tab}.eyebrow`),
          ...TAB_HERO_ICONS[tab],
        },
      ])
    ) as Record<
      NonProfileTab,
      {
        title: string;
        description: string;
        eyebrow: string;
        heroGradientStops: [string, string, string];
        Icon: LucideIcon;
      }
    >;
  }, [tProfile]);

  const renderTabContentHero = (tab: TabType): React.ReactNode => {
    if (tab === 'profile' || simpleHome) return null;
    const meta = tabContentHeroMeta[tab];
    const Icon = meta.Icon;
    const [g0, g1, g2] = meta.heroGradientStops;
    return (
      <div className="clay-card mb-4 w-full overflow-hidden p-1 dark:border dark:border-gray-700 sm:mb-6 sm:p-1.5">
        <div
          className={tabHeroShellClass}
          dir={direction}
          style={{ background: makeGradient(g0, g1, g2) }}
        >
          <RtlDecorLayer className="absolute -right-6 -top-6 h-24 w-24 rounded-full bg-white/35 dark:bg-white/10" aria-hidden />
          <RtlDecorLayer className="absolute -left-8 bottom-0 h-28 w-28 rounded-full bg-black/[0.06] dark:bg-black/25" aria-hidden />
          <div className={tabHeroRowClass}>
            <div className="mx-auto flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white/85 text-slate-800 shadow-sm dark:bg-black/25 dark:text-white sm:mx-0">
              <Icon className="h-6 w-6" strokeWidth={2} aria-hidden />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-800/75 dark:text-white/80">{meta.eyebrow}</p>
              <h2 className="text-lg font-bold tracking-tight text-slate-950 dark:text-white sm:text-xl">{meta.title}</h2>
              <p className="mt-0.5 max-w-2xl text-xs leading-relaxed text-slate-800/80 dark:text-white/85 sm:text-sm">{meta.description}</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderTabContent = (tab: TabType) => {
    if (tab === 'profile') {
      return (
        <div dir={direction}>
          <LearnerProfileHeroCard
            className="mb-4"
            user={user}
            showPhotoUpload
            uploadingPhoto={uploadingPhoto}
            photoInputId="photo-upload"
            photoAccept={
              profilePhotoLimits.allowedImageFormats
                .split(',')
                .map((s) => {
                  const t = s.trim().toLowerCase().replace(/^\./, '');
                  return t ? `.${t}` : '';
                })
                .filter(Boolean)
                .join(',') || 'image/*'
            }
            onPhotoChange={handlePhotoUpload}
          />
          <div
            ref={profileActionsSentinelRef}
            className="mb-8"
            style={{
              visibility:
                pinProfileActions && activeTab === 'profile' && !isDragging ? 'hidden' : 'visible'
            }}
          >
            <LearnerProfileActionButtons
              onEditProfile={() => setEditMode(true)}
              onChangePassword={() => setShowPasswordChange(true)}
            />
          </div>
          <div className="space-y-8">
          {/* Expandable Profile Information */}
          {user && (
            <div className="clay-card bg-white dark:bg-gray-800 dark:border dark:border-gray-700 overflow-hidden">
              {/* Header with Expand/Collapse Button */}
              <button
                onClick={() => setIsProfileExpanded(!isProfileExpanded)}
                className={`w-full p-6 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${isRtl ? 'text-end' : 'text-start'}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`p-3 rounded-xl shrink-0 ${rtlDecor('bg-gradient-to-br from-purple-500 to-pink-500')}`}>
                    <User className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-800 dark:text-gray-200 text-start">
                      {tProfile('details.profileInformation')}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 text-start">
                      {tProfile('details.viewCompleteDetails')}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-sm text-gray-500 dark:text-gray-400 hidden sm:inline">
                    {isProfileExpanded ? tProfile('hideDetails') : tProfile('showDetails')}
                  </span>
                  <ChevronDown 
                    className={`w-6 h-6 text-gray-500 dark:text-gray-400 transition-transform duration-200 ease-out ${
                      isProfileExpanded ? 'rotate-180' : 'rotate-0'
                    }`}
                  />
                </div>
              </button>

              {/* Expandable Content */}
              <CollapsibleFadePanel expanded={isProfileExpanded}>
                <div className="profile-info-details px-6 pb-6 border-t border-gray-200 dark:border-gray-700">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-6">
                    {/* Personal Information Card */}
                    <div className={rtlDecor('bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl p-5 border border-blue-100 dark:border-blue-800/50')}>
                      <div className="flex items-center gap-2 mb-4">
                        <div className="p-2 bg-blue-500 rounded-lg shrink-0">
                          <User className="w-5 h-5 text-white" />
                        </div>
                        <h4 className="flex-1 text-lg font-bold text-gray-800 dark:text-gray-200 text-start">
                          {tProfile('details.personalInformation')}
                        </h4>
                      </div>
                      
                      <div className="space-y-4">
                        <div className="flex items-start gap-3">
                          <User className="w-5 h-5 text-blue-500 dark:text-blue-400 mt-1 flex-shrink-0" />
                          <div className="flex-1">
                            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1 text-start">
                              {tProfile('details.firstName')}
                            </p>
                            <p className="text-base font-medium text-gray-800 dark:text-gray-200">
                              {user.firstName || <span className="text-gray-400 italic">{tProfile('form.notProvided')}</span>}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-start gap-3">
                          <User className="w-5 h-5 text-blue-500 dark:text-blue-400 mt-1 flex-shrink-0" />
                          <div className="flex-1">
                            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1 text-start">
                              {tProfile('details.lastName')}
                            </p>
                            <p className="text-base font-medium text-gray-800 dark:text-gray-200">
                              {user.lastName || <span className="text-gray-400 italic">{tProfile('form.notProvided')}</span>}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-start gap-3">
                          <AtSign className="w-5 h-5 text-blue-500 dark:text-blue-400 mt-1 flex-shrink-0" />
                          <div className="flex-1">
                            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1 text-start">
                              {tProfile('details.username')}
                            </p>
                            <p className="text-base font-medium text-gray-800 dark:text-gray-200">
                              {user.userName || <span className="text-gray-400 italic">{tProfile('form.notProvided')}</span>}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-start gap-3 pt-2 border-t border-blue-200 dark:border-blue-800/50">
                          <Mail className="w-5 h-5 text-blue-500 dark:text-blue-400 mt-1 flex-shrink-0" />
                          <div className="flex-1">
                            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1 text-start">
                              {tProfile('details.emailAddress')}
                            </p>
                            <p className="text-base font-medium text-gray-800 dark:text-gray-200 break-all">
                              {user.email}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Contact & Additional Information Card */}
                    <div className={rtlDecor('bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl p-5 border border-green-100 dark:border-green-800/50')}>
                      <div className="flex items-center gap-2 mb-4">
                        <div className="p-2 bg-green-500 rounded-lg shrink-0">
                          <Phone className="w-5 h-5 text-white" />
                        </div>
                        <h4 className="flex-1 text-lg font-bold text-gray-800 dark:text-gray-200 text-start">
                          {tProfile('details.contactAdditional')}
                        </h4>
                      </div>
                      
                      <div className="space-y-4">
                        <div className="flex items-start gap-3">
                          <Phone className="w-5 h-5 text-green-500 dark:text-green-400 mt-1 flex-shrink-0" />
                          <div className="flex-1">
                            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1 text-start">
                              {tProfile('details.phoneNumber')}
                            </p>
                            <p className="text-base font-medium text-gray-800 dark:text-gray-200">
                              {toStoredE164Input(user.phoneNumber) || <span className="text-gray-400 italic">{tProfile('form.notProvided')}</span>}
                            </p>
                            {user.phoneNumber ? (
                              <p className={`mt-1 text-xs font-semibold ${
                                user.phoneNumberConfirmed
                                  ? 'text-emerald-600 dark:text-emerald-400'
                                  : 'text-amber-700 dark:text-amber-300'
                              }`}>
                                {user.phoneNumberConfirmed
                                  ? tProfile('details.phoneVerified')
                                  : tProfile('details.phoneUnverified')}
                              </p>
                            ) : null}
                            {telegramPhoneHint && (
                              <p className="mt-2 rounded-lg border border-amber-300 bg-amber-50 px-2.5 py-2 text-sm font-medium text-amber-900 dark:border-amber-700 dark:bg-amber-950/50 dark:text-amber-100">
                                {tProfile('telegramBotPhoneHint')}
                              </p>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-start gap-3">
                          <MapPin className="w-5 h-5 text-green-500 dark:text-green-400 mt-1 flex-shrink-0" />
                          <div className="flex-1">
                            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1 text-start">
                              {tProfile('details.nationality')}
                            </p>
                            <p className="text-base font-medium text-gray-800 dark:text-gray-200">
                              {user.nationality || <span className="text-gray-400 italic">{tProfile('form.notProvided')}</span>}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-start gap-3">
                          <Calendar className="w-5 h-5 text-green-500 dark:text-green-400 mt-1 flex-shrink-0" />
                          <div className="flex-1">
                            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1 text-start">
                              {tProfile('details.age')}
                            </p>
                            <p className="text-base font-medium text-gray-800 dark:text-gray-200">
                              {user.age ? tProfile('details.yearsOld', { count: user.age }) : <span className="text-gray-400 italic">{tProfile('form.notProvided')}</span>}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-start gap-3 pt-2 border-t border-green-200 dark:border-green-800/50">
                          <Users className="w-5 h-5 text-green-500 dark:text-green-400 mt-1 flex-shrink-0" />
                          <div className="flex-1">
                            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1 text-start">
                              {tProfile('details.gender')}
                            </p>
                            <p className="text-base font-medium text-gray-800 dark:text-gray-200">
                              {user.gender || <span className="text-gray-400 italic">{tProfile('form.notProvided')}</span>}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Bio Section - Full Width */}
                    <div className={rtlDecor('md:col-span-2 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl p-5 border border-purple-100 dark:border-purple-800/50')}>
                      <div className="flex items-center gap-2 mb-3">
                        <div className="p-2 bg-purple-500 rounded-lg shrink-0">
                          <FileText className="w-5 h-5 text-white" />
                        </div>
                        <h4 className="flex-1 text-lg font-bold text-gray-800 dark:text-gray-200 text-start">
                          {tProfile('details.bio')}
                        </h4>
                      </div>
                      <div className="flex items-start gap-3">
                        <FileText className="w-5 h-5 text-purple-500 dark:text-purple-400 mt-0.5 flex-shrink-0 md:ml-10" />
                        <p className="text-base text-gray-800 dark:text-gray-200 leading-relaxed flex-1">
                          {user.bio || <span className="text-gray-400 italic">{tProfile('form.notProvided')}</span>}
                        </p>
                      </div>
                    </div>

                    {/* Account Information - Full Width */}
                    <div className={rtlDecor('md:col-span-2 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-xl p-5 border border-amber-100 dark:border-amber-800/50')}>
                      <div className="flex items-center gap-2 mb-4">
                        <div className="p-2 bg-amber-500 rounded-lg shrink-0">
                          <Calendar className="w-5 h-5 text-white" />
                        </div>
                        <h4 className="flex-1 text-lg font-bold text-gray-800 dark:text-gray-200 text-start">
                          {tProfile('details.accountInformation')}
                        </h4>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex items-start gap-3">
                          <Calendar className="w-5 h-5 text-amber-500 dark:text-amber-400 mt-1 flex-shrink-0" />
                          <div className="flex-1">
                            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1 text-start">
                              {tProfile('details.memberSinceLabel')}
                            </p>
                            <p className="text-base font-medium text-gray-800 dark:text-gray-200">
                              {user.membershipDate ? formatDate(user.membershipDate) : (user.createdAt ? formatDate(user.createdAt) : tProfile('form.notAvailable'))}
                            </p>
                          </div>
                        </div>
                        
                        <button
                          type="button"
                          onClick={navigateToSubscriptionTab}
                          className="flex w-full items-start gap-3 rounded-lg px-1 py-1 text-start transition-colors hover:bg-amber-100/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500/50 dark:hover:bg-amber-900/25"
                          aria-label={tProfile('account.viewSubscriptionPlan')}
                        >
                          <CreditCard className="w-5 h-5 text-amber-500 dark:text-amber-400 mt-1 flex-shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1 text-start">
                              {tProfile('account.currentSubscriptionPlan')}
                            </p>
                            <p className="text-base font-medium text-amber-800 dark:text-amber-200">
                              {loadingSubscription && !subscriptionStatus
                                ? tCommon('loading')
                                : profileSubscriptionPlanLabel}
                            </p>
                          </div>
                          <ChevronRight
                            className={`mt-1.5 h-4 w-4 shrink-0 text-amber-500/80 dark:text-amber-400/80 ${isRtl ? 'rotate-180' : ''}`}
                            aria-hidden
                          />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </CollapsibleFadePanel>
            </div>
          )}
          </div>
        </div>
      );
    }

    if (tab === 'goals') {
      const LEARNING_STYLES = [
        { value: 'Visual', labelKey: 'styleVisual' },
        { value: 'Auditory', labelKey: 'styleAuditory' },
        { value: 'Reading', labelKey: 'styleReading' },
        { value: 'Kinesthetic', labelKey: 'styleKinesthetic' },
      ] as const;
      const preferredLang = goalsPrefs && goalsPrefs.preferredLanguage === 'fa' ? 'fa' : 'en';
      const formatMinutes = (m: number) => {
        if (m < 60) return tProfile('goals.minutesPerDay', { count: m });
        const h = Math.floor(m / 60);
        const rem = m % 60;
        if (rem === 0) return tProfile('goals.hoursPerDay', { count: h });
        return tProfile('goals.hoursMinutesPerDay', { hours: h, minutes: rem });
      };
      return (
        <div className="space-y-6" dir={direction}>
          {renderTabContentHero('goals')}

          {loadingGoals && !goalsPrefs ? (
            <LearnerInlineContentLoader message={tProfile('goals.loading')} />
          ) : goalsPrefs ? (
            <div className="space-y-4">
              {/* Daily time commitment — first (before learning goals text). */}
              {(() => {
                const currentMinutes = Math.min(480, Math.max(5, goalsPrefs.dailyLearningGoalMinutes || 30));
                const sliderMax = Math.max(240, currentMinutes); // grow the slider's reach if user enters a high custom value
                const matchedPreset = (DAILY_TIME_PRESETS as readonly number[]).includes(currentMinutes)
                  ? currentMinutes
                  : null;
                const dailyPresetOptions = DAILY_TIME_PRESETS.map((preset) => ({
                  value: preset,
                  label: tProfile('goals.minPreset', { count: preset })
                }));
                return (
                  <div
                    ref={dailyCommitmentRef}
                    id="profile-daily-commitment"
                    tabIndex={-1}
                    className={`clay-card p-5 sm:p-6 dark:border dark:border-gray-700 outline-none transition-[box-shadow] duration-500 ${
                      dailyCommitmentPulse
                        ? 'ring-2 ring-violet-500 ring-offset-2 ring-offset-white dark:ring-violet-400 dark:ring-offset-gray-900'
                        : ''
                    }`}
                    style={{
                      background: isDark
                        ? makeGradient('#1e1b4b', '#312e81', '#3b0764')
                        : makeGradient('#ede9fe', '#e0e7ff', '#fce7f3')
                    }}
                  >
                    <div className="flex items-start gap-3 mb-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-violet-600 text-white shadow-md">
                        <Timer className="w-5 h-5" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                          {tProfile('goals.dailyCommitment')}
                        </h3>
                        <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300">
                          {tProfile('goals.dailyCommitmentHint')}
                        </p>
                      </div>
                    </div>

                    <div className="mb-4">
                      <HorizontalFilterPillRow
                        label={tProfile('goals.dailyCommitment')}
                        icon={Timer}
                        hideLabel
                        allowUnmatched
                        options={dailyPresetOptions}
                        active={matchedPreset}
                        onChange={(value) => {
                          if (value != null) updateGoalsField('dailyLearningGoalMinutes', value);
                        }}
                        theme={DAILY_TIME_CHIP_THEME}
                      />
                    </div>

                    <div className="flex items-center justify-between gap-3 mb-1">
                      <span className="text-sm font-semibold tabular-nums text-gray-800 dark:text-gray-100">
                        {formatMinutes(currentMinutes)}
                      </span>
                      <input
                        type="number"
                        min={5}
                        max={480}
                        step={5}
                        value={goalsPrefs.dailyLearningGoalMinutes}
                        onChange={(e) => {
                          const next = Number(e.target.value);
                          updateGoalsField('dailyLearningGoalMinutes', Number.isFinite(next) ? next : 0);
                        }}
                        aria-label={tProfile('goals.dailyTargetMinutes')}
                        className="w-20 text-right rounded-lg border border-violet-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400/60"
                      />
                    </div>

                    <div className="overscroll-contain py-2 -my-1">
                      <AnimatedTouchSafeRangeInput
                        min={5}
                        max={sliderMax}
                        step={5}
                        value={currentMinutes}
                        onChange={(e) => updateGoalsField('dailyLearningGoalMinutes', Number(e.target.value))}
                        aria-label={tProfile('goals.dailyTargetSlider')}
                        className="w-full touch-none"
                      />
                    </div>

                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                      {tProfile('goals.dailyTip')}
                    </p>
                    <div className="mt-3 flex items-center justify-end">
                      <button
                        type="button"
                        onClick={() => void handleSaveDailyCommitment()}
                        disabled={
                          savingDailyCommitment ||
                          savingGoals ||
                          loadingGoals ||
                          !(
                            !goalsPrefs.hasSetDailyLearningGoal ||
                            (goalsSavedPrefs != null &&
                              goalsPrefs.dailyLearningGoalMinutes !== goalsSavedPrefs.dailyLearningGoalMinutes)
                          )
                        }
                        className="inline-flex items-center gap-1.5 rounded-lg bg-violet-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {savingDailyCommitment && (
                          <span className="h-3 w-3 rounded-full border-2 border-white/40 border-t-white animate-spin" aria-hidden />
                        )}
                        {tProfile('goals.saveDailyTarget', { defaultValue: 'Save time' })}
                      </button>
                    </div>
                  </div>
                );
              })()}

              <div className="clay-card bg-white dark:bg-gray-800 p-5 sm:p-6 dark:border dark:border-gray-700">
                <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-1 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-emerald-500" />
                  {tProfile('goals.yourLearningGoals')}
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                  {tProfile('goals.learningGoalsHint')}
                </p>
                <textarea
                  value={goalsPrefs.learningGoals}
                  onChange={(e) => updateGoalsField('learningGoals', e.target.value)}
                  rows={4}
                  maxLength={2000}
                  placeholder={tProfile('goals.learningGoalsPlaceholder')}
                  aria-label={tProfile('goals.learningGoalsLabel')}
                  className="w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400/60 focus:border-emerald-400 resize-y"
                />
                <div className="text-right text-xs text-gray-400 mt-1">{goalsPrefs.learningGoals.length}/2000</div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="clay-card bg-white dark:bg-gray-800 p-5 sm:p-6 dark:border dark:border-gray-700">
                  <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-1 flex items-center gap-2">
                    <Brain className="w-5 h-5 text-purple-500" />
                    {tProfile('goals.preferredStyleTitle')}
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                    {tProfile('goals.preferredStyleHint')}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {LEARNING_STYLES.map(({ value, labelKey }) => {
                      const isActive = (goalsPrefs.preferredLearningStyle || 'Visual') === value;
                      return (
                        <button
                          key={value}
                          type="button"
                          onClick={() => updateGoalsField('preferredLearningStyle', value)}
                          aria-pressed={isActive ? 'true' : 'false'}
                          className={`min-h-[44px] touch-manipulation rounded-xl border px-3 py-2 text-xs font-semibold transition-all sm:text-sm ${
                            isActive
                              ? 'border-purple-600 bg-purple-600 text-white shadow-sm dark:border-purple-500 dark:bg-purple-600'
                              : 'border-purple-200 bg-white/90 text-gray-700 hover:border-purple-300 hover:bg-white dark:border-gray-600 dark:bg-gray-800/80 dark:text-gray-200 dark:hover:border-purple-500/60'
                          }`}
                        >
                          {tProfile(`goals.${labelKey}`)}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="clay-card bg-white dark:bg-gray-800 p-5 sm:p-6 dark:border dark:border-gray-700">
                  <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-1 flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-rose-500" />
                    {tProfile('goals.preferredLanguageTitle')}
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                    {tProfile('goals.preferredLanguageHint')}
                  </p>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <button
                      type="button"
                      onClick={() => updateGoalsField('preferredLanguage', 'en')}
                      aria-pressed={preferredLang === 'en' ? 'true' : 'false'}
                      className={`min-h-[48px] touch-manipulation rounded-xl border px-4 py-3 text-sm font-semibold transition-all ${
                        preferredLang === 'en'
                          ? 'border-rose-600 bg-rose-600 text-white shadow-sm dark:border-rose-500 dark:bg-rose-600'
                          : 'border-rose-200 bg-white text-gray-800 hover:border-rose-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:border-rose-500/50'
                      }`}
                    >
                      {tCommon('languageEnglish')}
                    </button>
                    <button
                      type="button"
                      onClick={() => updateGoalsField('preferredLanguage', 'fa')}
                      aria-pressed={preferredLang === 'fa' ? 'true' : 'false'}
                      className={`min-h-[48px] touch-manipulation rounded-xl border px-4 py-3 text-sm font-semibold transition-all ${
                        preferredLang === 'fa'
                          ? 'border-rose-600 bg-rose-600 text-white shadow-sm dark:border-rose-500 dark:bg-rose-600'
                          : 'border-rose-200 bg-white text-gray-800 hover:border-rose-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:border-rose-500/50'
                      }`}
                    >
                      {tCommon('languagePersian')}
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-2">
                {goalsDirty && (
                  <span className="text-xs text-amber-600 dark:text-amber-400">{tProfile('goals.unsavedChanges')}</span>
                )}
                <button
                  type="button"
                  onClick={() => void loadGoalsData()}
                  disabled={savingGoals || loadingGoals}
                  className="px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
                >
                  {tProfile('goals.reset')}
                </button>
                <button
                  type="button"
                  onClick={() => void handleSaveGoals()}
                  disabled={!goalsDirty || savingGoals || savingDailyCommitment || loadingGoals}
                  className="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold shadow disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {savingGoals && <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" aria-hidden="true" />}
                  {tProfile('goals.saveChanges')}
                </button>
              </div>
            </div>
          ) : (
            <div className="clay-card bg-white dark:bg-gray-800 p-8 text-center dark:border dark:border-gray-700">
              <Target className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600 dark:text-gray-400 mb-3">{tProfile('goals.loadGoalsFailed')}</p>
              <button
                type="button"
                onClick={() => void loadGoalsData()}
                className="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold"
              >
                Try again
              </button>
            </div>
          )}
        </div>
      );
    }

    if (tab === 'subscription') {
      const plansSection =
        subscriptionPlans.length > 0 ? (
          <div className="clay-card bg-white dark:bg-gray-800 p-6 rounded-2xl border border-gray-200 dark:border-gray-700">
            <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-1.5 sm:text-lg">
              {subscriptionStatus?.isActive ? tSubscription('otherPlansPricing') : tSubscription('availablePlans')}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {subscriptionStatus?.isActive
                ? tSubscription('comparePlansRenew')
                : tProfile('subscriptionTab.upgradeHintInactive')}
            </p>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              {subscriptionPlans.map((plan) => {
                const isCurrent =
                  subscriptionStatus?.isActive &&
                  (subscriptionStatus.planName?.toLowerCase() === plan.name.toLowerCase() ||
                    subscriptionStatus.currentSubscription?.subscriptionPlan?.id === plan.id);
                const isPopular = plan.duration === 90;
                const isBestValue = plan.duration === 180;
                return (
                  <SubscriptionPlanCard
                    key={plan.id}
                    plan={plan}
                    compact
                    isCurrent={isCurrent}
                    highlighted={!isCurrent && (isPopular || isBestValue)}
                    prominenceBadge={
                      isCurrent ? null : isPopular ? tSubscription('mostPopular') : isBestValue ? tSubscription('bestValue') : null
                    }
                    actionLabel={
                      isCurrent ? tProfile('subscriptionTab.currentPlan') : tProfile('subscriptionTab.purchasePlan')
                    }
                    disabled={isCurrent}
                    onAction={() => {
                      if (!isCurrent) showSuccess(tProfile('subscriptionTab.purchaseSoon'));
                    }}
                  />
                );
              })}
            </div>
            <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <p className="text-sm text-blue-800 dark:text-blue-200">
                {tProfile('subscriptionTab.purchaseNote')}
              </p>
            </div>
          </div>
        ) : null;

      return (
        <div className="space-y-6" dir={direction}>
          {renderTabContentHero('subscription')}

          {loadingSubscription ? (
            <LearnerInlineContentLoader message={tProfile('subscriptionTab.loading')} />
          ) : (
            <div className="space-y-6">
              {subscriptionStatus?.isActive ? (
                <div className="clay-card overflow-hidden rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm">
                  <div className="relative p-6 sm:p-8">
                    <RtlDecorLayer
                      className="pointer-events-none absolute inset-0 bg-gradient-to-br from-violet-100/90 via-white to-fuchsia-50/80 dark:from-violet-950/50 dark:via-gray-900 dark:to-fuchsia-950/30"
                      aria-hidden
                    />
                    <RtlDecorLayer className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-violet-400/20 blur-3xl" aria-hidden />
                    <div className="relative flex flex-col gap-6 sm:flex-row sm:items-center">
                      <div
                        className="flex h-20 w-20 shrink-0 items-center justify-center rounded-2xl border-2 border-white/80 bg-white shadow-lg dark:border-gray-600 dark:bg-gray-800"
                        style={subscriptionPlanBadgeStyle(activePlanBadge?.color)}
                      >
                        {activePlanBadge?.icon ? (
                          <SubscriptionPlanIconGlyph
                            icon={activePlanBadge.icon}
                            color={activePlanBadge.color ?? null}
                            className="h-10 w-10"
                          />
                        ) : (
                          <CreditCard className="h-10 w-10 text-violet-600 dark:text-violet-400" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <span className="inline-flex items-center rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wide text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200">
                          {tProfile('subscriptionTab.activeSubscription')}
                        </span>
                        <h3 className="mt-2 text-base font-semibold tracking-tight text-gray-900 dark:text-white sm:text-lg">
                          {subscriptionStatus.planName}
                        </h3>
                        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                          {tProfile('subscriptionTab.activeSubscriptionBody')}
                        </p>
                      </div>
                    </div>
                    {subscriptionStatus.startDate && subscriptionStatus.endDate && (
                      <div className="relative mt-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
                        <div className="rounded-xl border border-gray-200/80 bg-white/80 p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800/80">
                          <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                            {tProfile('subscriptionTab.started')}
                          </p>
                          <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-gray-100">
                            {formatDate(subscriptionStatus.startDate)}
                          </p>
                        </div>
                        <div className="rounded-xl border border-gray-200/80 bg-white/80 p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800/80">
                          <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                            {tProfile('subscriptionTab.ends')}
                          </p>
                          <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-gray-100">
                            {formatDate(subscriptionStatus.endDate)}
                          </p>
                        </div>
                        <div className="rounded-xl border border-emerald-200/80 bg-emerald-50/90 p-4 shadow-sm dark:border-emerald-800/60 dark:bg-emerald-950/40">
                          <p className="text-xs font-medium uppercase tracking-wide text-emerald-700/80 dark:text-emerald-300/90">
                            {tProfile('subscriptionTab.remaining')}
                          </p>
                          <p className="mt-1 text-2xl font-bold text-emerald-800 dark:text-emerald-200">
                            {subscriptionStatus.remainingDays}{' '}
                            <span className="text-base font-semibold">
                              {subscriptionStatus.remainingDays === 1 ? tProfile('subscriptionTab.day') : tProfile('subscriptionTab.days')}
                            </span>
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className={rtlDecor('clay-card overflow-hidden rounded-2xl border border-amber-200/80 dark:border-amber-800/60 bg-gradient-to-br from-amber-50 via-white to-orange-50 dark:from-amber-950/40 dark:via-gray-900 dark:to-orange-950/30 p-6 sm:p-8')}>
                  <div className="flex items-center gap-4">
                    <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-amber-100 dark:bg-amber-900/50">
                      <User className="h-8 w-8 text-amber-700 dark:text-amber-300" />
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-gray-900 dark:text-white sm:text-lg">{tProfile('subscriptionTab.freePlan')}</h3>
                      <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                        {tProfile('subscriptionTab.freePlanBody')}
                      </p>
                    </div>
                  </div>
                  {subscriptionStatus?.isTrialActive && subscriptionStatus.trialEndDate && (
                    <div className="mt-4 rounded-xl border border-amber-200/60 bg-white/70 p-4 dark:border-amber-800/50 dark:bg-gray-800/60">
                      <p className="text-sm font-medium text-amber-900 dark:text-amber-200">{tProfile('subscriptionTab.trialPeriod')}</p>
                      <p className="mt-1 text-gray-800 dark:text-gray-200">
                        {tProfile('subscriptionTab.trialEndsOn', { date: formatDate(subscriptionStatus.trialEndDate) })}
                        {subscriptionStatus.trialRemainingDays > 0 && (
                          <span className="ml-1 text-gray-600 dark:text-gray-400">
                            {tProfile('subscriptionTab.daysRemaining', { count: subscriptionStatus.trialRemainingDays })}
                          </span>
                        )}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {plansSection}
            </div>
          )}

          <ProfileSubscriptionHistory />
        </div>
      );
    }

    if (tab === 'messages') {
      return (
        <div className="space-y-6" dir={direction}>
          {renderTabContentHero('messages')}
          <InboxPushHint />
          <Inbox
            onMessageRead={() => loadUnreadCount()}
            defaultFiltersExpanded={false}
            key={`inbox-${inboxRefreshKey}`}
          />
        </div>
      );
    }

    if (tab === 'comments') {
      return (
        <div className="flex flex-col space-y-2" dir={direction}>
          {renderTabContentHero('comments')}
          <UserCommentsPanel
            embedded
            active={activeTab === 'comments'}
            {...(onNavigateToLesson && { onNavigateToLesson })}
            {...(onNavigateToBook && { onNavigateToBook })}
          />
        </div>
      );
    }

    if (tab === 'favorites') {
      const renderFavoritesSectionShell = (
        sectionKey: string,
        title: string,
        count: number,
        icon: LucideIcon,
        iconClass: string,
        expanded: boolean,
        onToggle: () => void,
        subtitle: string | null,
        content: React.ReactNode
      ) => {
        const SectionIcon = icon;
        return (
          <section
            key={sectionKey}
            className="overflow-hidden rounded-xl border border-gray-200/90 bg-white/80 [overflow-anchor:none] dark:border-gray-700/60 dark:bg-gray-800/40"
          >
            <button
              type="button"
              onClick={onToggle}
              dir={direction}
              className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-start transition-colors hover:bg-gray-50/80 dark:hover:bg-gray-800/60 sm:px-4 sm:py-3"
              aria-expanded={expanded}
            >
              <span className="flex min-w-0 items-center gap-2" dir={direction}>
                <SectionIcon className={`h-4 w-4 shrink-0 ${iconClass}`} aria-hidden />
                <span className="min-w-0 truncate text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</span>
                <span className="inline-flex min-w-[1.25rem] shrink-0 items-center justify-center rounded-full bg-gray-200/90 px-1.5 py-0.5 text-[10px] font-bold tabular-nums text-gray-700 dark:bg-gray-700 dark:text-gray-200">
                  {count}
                </span>
              </span>
              <ChevronDown
                className={`h-5 w-5 shrink-0 text-gray-400 transition-transform duration-200 ease-out dark:text-gray-500 ${
                  expanded ? 'rotate-180' : 'rotate-0'
                }`}
                aria-hidden
              />
            </button>
            <div
              className="grid transition-[grid-template-rows,opacity] duration-200 ease-out motion-reduce:transition-none"
              style={{ gridTemplateRows: expanded ? '1fr' : '0fr', opacity: expanded ? 1 : 0 }}
            >
              <div className="min-h-0 overflow-hidden">
                <div
                  className={`border-t border-gray-100 px-3 py-3 transition-[transform,opacity] duration-200 ease-out motion-reduce:transition-none dark:border-gray-700/80 sm:px-4 sm:pb-4 ${
                    expanded ? 'translate-y-0 opacity-100' : '-translate-y-2 opacity-0'
                  }`}
                >
                  {subtitle && (
                    <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">{subtitle}</p>
                  )}
                  {content}
                </div>
              </div>
            </div>
          </section>
        );
      };

      return (
        <div className="space-y-4" dir={direction}>
          {renderTabContentHero('favorites')}
          {loadingFavorites ? (
            <LearnerInlineContentLoader message={tProfile('favorites.loading')} />
          ) : (
            <div className="space-y-3">
              {renderFavoritesSectionShell(
                'favorite-books',
                tProfile('favorites.booksTitle'),
                favoriteLibraryBooks.length,
                Library,
                'text-indigo-500 dark:text-indigo-400',
                favBooksSectionOpen,
                () => setFavBooksSectionOpen((o) => !o),
                tProfile('favorites.booksHint'),
                favoriteLibraryBooks.length === 0 ? (
                  <p className="py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                    {tProfile('favorites.emptyBooks')}
                  </p>
                ) : (
                  <ul className="space-y-2">
                    {favoriteLibraryBooks.map((item) => (
                      <li
                        key={`library-book-${item.id}`}
                        className={`flex items-center gap-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/80 hover:bg-gray-50 dark:hover:bg-gray-700/80 transition-colors group ${
                          item.isAccessible === false ? 'opacity-50' : ''
                        } ${
                          favoriteRemovePendingKey === `library-book-${item.id}` ||
                          favoriteRemoveClosingKey === `library-book-${item.id}`
                            ? 'overflow-visible'
                            : 'overflow-hidden'
                        }`}
                      >
                        {item.coverImageUrl ? (
                          <img
                            src={normalizeAppHostedUploadUrl(item.coverImageUrl)}
                            alt=""
                            className={`w-10 h-14 object-cover shrink-0 ${isRtl ? 'rounded-r-xl' : 'rounded-l-xl'}`}
                          />
                        ) : (
                          <div className={`w-10 h-14 bg-indigo-50 dark:bg-indigo-950/50 flex items-center justify-center shrink-0 ${isRtl ? 'rounded-r-xl' : 'rounded-l-xl'}`}>
                            <Library className="w-5 h-5 text-indigo-400" />
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() =>
                            openIfStillAvailable(item.isAccessible, tLearning('book.noLongerAvailable'), () => {
                              onNavigateToBook?.(item.libraryBookId);
                            })
                          }
                          dir={favTextContentDir}
                          lang={isPersian ? 'fa' : undefined}
                          className={`flex-1 min-w-0 px-2 py-3 ${favTextAlignClass}`}
                        >
                          <span className={favTitleTextClass}>
                            {pickBookTitle(item)}
                          </span>
                          {(pickDescription(item) || pickBookAuthor(item)) && (
                            <span className={favMetaTextClass}>
                              {pickDescription(item) || pickBookAuthor(item)}
                            </span>
                          )}
                        </button>
                        {renderFavoriteRemoveControl(`library-book-${item.id}`, async () => {
                          await libraryService.removeBookFavorite(item.libraryBookId);
                          setFavoriteLibraryBooks((prev) => prev.filter((f) => f.id !== item.id));
                          setFavoritesRefreshKey((k) => k + 1);
                        })}
                      </li>
                    ))}
                  </ul>
                )
              )}
              {renderFavoritesSectionShell(
                'favorite-lessons',
                tProfile('favorites.lessonsTitle'),
                favoriteLessons.length,
                BookOpen,
                'text-blue-500 dark:text-blue-400',
                favLessonsSectionOpen,
                () => setFavLessonsSectionOpen((o) => !o),
                tProfile('favorites.lessonsHint'),
                favoriteLessons.length === 0 ? (
                  <p className="py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                    {tProfile('favorites.emptyLessons')}
                  </p>
                ) : (
                  <ul className="space-y-2">
                    {favoriteLessons.map((item) => {
                      const lessonContextPath = buildFavoriteContextPath(
                        { title: item.courseTitle, titleFa: item.courseTitleFa },
                        { title: item.levelTitle, titleFa: item.levelTitleFa }
                      );
                      return (
                      <li
                        key={`lesson-${item.id}`}
                        className={`flex items-center gap-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/80 hover:bg-gray-50 dark:hover:bg-gray-700/80 transition-colors group ${
                          item.isAccessible === false ? 'opacity-50' : ''
                        } ${
                          favoriteRemovePendingKey === `course-lesson-${item.id}` ||
                          favoriteRemoveClosingKey === `course-lesson-${item.id}`
                            ? 'overflow-visible'
                            : 'overflow-hidden'
                        }`}
                      >
                        <button
                          type="button"
                          onClick={() =>
                            openIfStillAvailable(item.isAccessible, tLearning('lesson.noLongerAvailable'), () => {
                              onNavigateToLesson?.(item.lessonId);
                            })
                          }
                          dir={favTextContentDir}
                          lang={isPersian ? 'fa' : undefined}
                          className={`flex-1 min-w-0 px-4 py-3 ${favTextAlignClass}`}
                        >
                          <span className={favTitleTextClass}>
                            {pickTitle({ title: item.lessonTitle, titleFa: item.lessonTitleFa })}
                          </span>
                          {lessonContextPath && (
                            <FlowContextBreadcrumb
                              contextSubtitle={lessonContextPath.contextSubtitle}
                              contextSubtitleFa={lessonContextPath.contextSubtitleFa}
                              isRtl={isRtl}
                              className={favMetaTextClass}
                            />
                          )}
                        </button>
                        {renderFavoriteRemoveControl(`course-lesson-${item.id}`, async () => {
                          await favoriteService.removeFavorite(item.lessonId);
                          setFavoriteLessons((prev) => prev.filter((f) => f.id !== item.id));
                          setFavoritesRefreshKey((k) => k + 1);
                        })}
                      </li>
                    );
                    })}
                  </ul>
                )
              )}
              {renderFavoritesSectionShell(
                'favorite-topic-tags',
                tProfile('favorites.tagsTitle'),
                favoriteTopicTagsItemCount,
                Tag,
                'text-violet-500 dark:text-violet-400',
                favTopicTagsSectionOpen,
                () => setFavTopicTagsSectionOpen((o) => !o),
                tProfile('favorites.tagsHint'),
                favoriteTopicTagsItemCount === 0 ? (
                  <p className="py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                    {tProfile('favorites.emptyTags')}
                  </p>
                ) : (
                  <div className="space-y-4">
                    {mergedTopicTagFavorites.map((group) => (
                      <div
                        key={group.tagId}
                        className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-3"
                        style={{ borderLeftWidth: 4, borderLeftColor: group.color || '#6366f1' }}
                      >
                        <button
                          type="button"
                          onClick={() => onNavigateToTag?.(group.tagId)}
                          dir={favTextContentDir}
                          lang={isPersian ? 'fa' : undefined}
                          className={`mb-2 w-full ${favTextAlignClass}`}
                        >
                          <span className={favTagHeaderTitleClass}>{group.tagName}</span>
                          {group.tagDescription && (
                            <span className={favMetaTextClass}>{group.tagDescription}</span>
                          )}
                        </button>
                        <ul className="space-y-2">
                          {group.blocks.map((item) => {
                            const blockContextPath = buildFavoriteContextPath(
                              { title: item.courseTitle, titleFa: item.courseTitleFa },
                              { title: item.levelTitle, titleFa: item.levelTitleFa },
                              { title: item.lessonTitle, titleFa: item.lessonTitleFa }
                            );
                            return (
                            <li
                              key={`tag-block-${item.id}`}
                              className={`flex items-center gap-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/80 hover:bg-gray-50 dark:hover:bg-gray-700/80 transition-colors ${
                                item.isAccessible === false ? 'opacity-50' : ''
                              } ${
                                favoriteRemovePendingKey === `tag-block-${item.id}` ||
                                favoriteRemoveClosingKey === `tag-block-${item.id}`
                                  ? 'overflow-visible'
                                  : 'overflow-hidden'
                              }`}
                            >
                              <button
                                type="button"
                                onClick={() =>
                                  openIfStillAvailable(item.isAccessible, tLearning('lesson.noLongerAvailable'), () => {
                                    onNavigateToTag?.(item.tagId, item.contentBlockId);
                                  })
                                }
                                dir={favTextContentDir}
                                lang={isPersian ? 'fa' : undefined}
                                className={`flex-1 min-w-0 px-3 py-2 ${favTextAlignClass}`}
                              >
                                <span className={favKindLabelClass('violet')}>Block</span>
                                <span className={favTitleTextClass}>
                                  {pickBlockTitle({ title: item.blockTitle, titleFa: item.blockTitleFa }) ||
                                    pickTitle({ title: item.lessonTitle, titleFa: item.lessonTitleFa })}
                                </span>
                                {blockContextPath && (
                                  <FlowContextBreadcrumb
                                    contextSubtitle={blockContextPath.contextSubtitle}
                                    contextSubtitleFa={blockContextPath.contextSubtitleFa}
                                    isRtl={isRtl}
                                    className={favMetaTextClass}
                                  />
                                )}
                              </button>
                              {renderFavoriteRemoveControl(`tag-block-${item.id}`, async () => {
                                await favoriteService.removeFavoriteTagBlock(item.tagId, item.contentBlockId);
                                setFavoriteTags((prev) => prev.filter((f) => f.id !== item.id));
                                setFavoritesRefreshKey((k) => k + 1);
                              })}
                            </li>
                          );
                          })}
                          {group.lessons.map((item) => {
                            const tagLessonContextPath = buildFavoriteContextPath(
                              { title: item.courseTitle, titleFa: item.courseTitleFa },
                              { title: item.levelTitle, titleFa: item.levelTitleFa }
                            );
                            return (
                            <li
                              key={`tag-lesson-${item.id}`}
                              className={`flex items-center gap-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/80 hover:bg-gray-50 dark:hover:bg-gray-700/80 transition-colors ${
                                item.isAccessible === false ? 'opacity-50' : ''
                              } ${
                                favoriteRemovePendingKey === `tag-lesson-${item.id}` ||
                                favoriteRemoveClosingKey === `tag-lesson-${item.id}`
                                  ? 'overflow-visible'
                                  : 'overflow-hidden'
                              }`}
                            >
                              <button
                                type="button"
                                onClick={() =>
                                  openIfStillAvailable(item.isAccessible, tLearning('lesson.noLongerAvailable'), () => {
                                    onNavigateToTag?.(item.tagId, undefined, item.lessonId);
                                  })
                                }
                                dir={favTextContentDir}
                                lang={isPersian ? 'fa' : undefined}
                                className={`flex-1 min-w-0 px-3 py-2 ${favTextAlignClass}`}
                              >
                                <span className={favKindLabelClass('emerald')}>Lesson</span>
                                <span className={favTitleTextClass}>
                                  {pickTitle({ title: item.lessonTitle, titleFa: item.lessonTitleFa })}
                                </span>
                                {tagLessonContextPath && (
                                  <FlowContextBreadcrumb
                                    contextSubtitle={tagLessonContextPath.contextSubtitle}
                                    contextSubtitleFa={tagLessonContextPath.contextSubtitleFa}
                                    isRtl={isRtl}
                                    className={favMetaTextClass}
                                  />
                                )}
                              </button>
                              {renderFavoriteRemoveControl(`tag-lesson-${item.id}`, async () => {
                                await favoriteService.removeFavoriteTagLesson(item.tagId, item.lessonId);
                                setFavoriteTagLessons((prev) => prev.filter((f) => f.id !== item.id));
                                setFavoritesRefreshKey((k) => k + 1);
                              })}
                            </li>
                          );
                          })}
                        </ul>
                      </div>
                    ))}
                  </div>
                )
              )}
            </div>
          )}
        </div>
      );
    }

    if (tab === 'progress') {
      const statNum = (obj: unknown, key: string): number => {
        if (!obj || typeof obj !== 'object') return 0;
        const o = obj as Record<string, unknown>;
        const v = o[key] ?? o[key.charAt(0).toUpperCase() + key.slice(1)];
        if (typeof v === 'number' && !Number.isNaN(v)) return v;
        const n = Number(v);
        return Number.isFinite(n) ? n : 0;
      };
      const scopedSections =
        Array.isArray(progressScopeCourseIds) && progressScopeCourseIds.length > 0 && progressScopedDetail;
      const statsForSections = (
        scopedSections ? progressScopedDetail.stats ?? progressScopedDetail.Stats : progressionStats
      ) as typeof progressionStats;
      const detailForSections = scopedSections ? progressScopedDetail : progressDetail;
      const completed = statsForSections?.completedActivities ?? statsForSections?.CompletedActivities;
      const averages = statsForSections?.averageScores ?? statsForSections?.AverageScores;
      const times = statsForSections?.timeSpentByActivity ?? statsForSections?.TimeSpentByActivity;
      const chartSlicesRaw = (statsForSections?.chartSlices ??
        statsForSections?.ChartSlices) as Array<Record<string, unknown>> | undefined;
      const fallbackActivityKeys = ['quiz', 'exercise', 'vocabulary', 'grammar', 'lessons'] as const;
      const usageSummary = detailForSections?.usageSummary ?? detailForSections?.UsageSummary;
      const totalFromUsage = Number(usageSummary?.totalStudySeconds ?? usageSummary?.TotalStudySeconds);
      const uniqueFromStats = Number(
        statsForSections?.uniqueStudySeconds ?? statsForSections?.UniqueStudySeconds ?? NaN
      );
      const sliceNum = (s: Record<string, unknown>, camel: string, pascal: string): number => {
        const v = s[camel] ?? s[pascal];
        if (typeof v === 'number' && !Number.isNaN(v)) return v;
        const n = Number(v);
        return Number.isFinite(n) ? n : 0;
      };
      const sumFromBucketsFallback = fallbackActivityKeys.reduce((sum, k) => sum + statNum(times, k), 0);
      const totalStudySeconds =
        Number.isFinite(totalFromUsage) && totalFromUsage > 0
          ? totalFromUsage
          : Number.isFinite(uniqueFromStats) && uniqueFromStats > 0
            ? uniqueFromStats
            : sumFromBucketsFallback;
      const translateChartSliceLabel = (key: string, label: string): string => {
        const normalized = (key || '').toLowerCase().trim();
        if (normalized.startsWith('tag:')) {
          const tagId = Number.parseInt(normalized.slice(4), 10);
          if (Number.isFinite(tagId)) {
            const tag = progressPublicTags.find((t) => t.id === tagId);
            if (tag) return pickTagName(tag);
          }
          return tProfile('progress.charts.topic');
        }
        if (normalized === 'quiz') return tProfile('progress.charts.quizzes');
        if (normalized === 'exercise') return tProfile('progress.charts.exercises');
        if (normalized === 'lessons') return tProfile('progress.charts.lessons');
        if (normalized === 'book') return tProfile('progressTab.books');
        if (normalized === 'vocabulary') return tProfile('progress.charts.vocabulary');
        if (normalized === 'grammar') return tProfile('progress.charts.grammar');
        if (normalized === 'topic') return tProfile('progress.charts.topic');
        const byEnglish: Record<string, string> = {
          Quizzes: tProfile('progress.charts.quizzes'),
          Quiz: tProfile('progress.charts.quizzes'),
          Exercises: tProfile('progress.charts.exercises'),
          Exercise: tProfile('progress.charts.exercises'),
          Lessons: tProfile('progress.charts.lessons'),
          Lesson: tProfile('progress.charts.lessons'),
          Books: tProfile('progressTab.books'),
          Book: tProfile('progressTab.books'),
          Vocabulary: tProfile('progress.charts.vocabulary'),
          Grammar: tProfile('progress.charts.grammar'),
          Topic: tProfile('progress.charts.topic'),
          Remaining: tProfile('progress.charts.remaining'),
          Completed: tProfile('progress.charts.completed'),
          Passed: tProfile('progress.charts.passed'),
          Reviewed: tProfile('progress.charts.reviewed'),
          Progress: tProfile('progress.charts.progress'),
        };
        const fromEnglish = byEnglish[label] ?? byEnglish[label.trim()];
        if (fromEnglish) return fromEnglish;
        if (!isPersian && /[\u0600-\u06FF]/.test(label)) {
          return tProfile('progress.charts.topic');
        }
        return label;
      };
      const formatStudySeconds = (totalSeconds: number): string => {
        const h = Math.floor(totalSeconds / 3600);
        const m = Math.floor((totalSeconds % 3600) / 60);
        const s = Math.floor(totalSeconds % 60);
        if (locale === 'fa') {
          if (h > 0 && m > 0) return `${h} ساعت ${m} دقیقه`;
          if (h > 0) return `${h} ساعت`;
          if (m > 0) return `${m} دقیقه`;
          return `${s} ثانیه`;
        }
        if (h > 0) return `${h}h ${m}m`;
        if (m > 0) return `${m}m`;
        return `${s}s`;
      };
      const studyPrimary = formatStudySeconds(totalStudySeconds);

      const usageSummaryGlobal = progressDetail?.usageSummary ?? progressDetail?.UsageSummary;
      const totalFromUsageGlobal = Number(usageSummaryGlobal?.totalStudySeconds ?? usageSummaryGlobal?.TotalStudySeconds);
      const timesGlobal = progressionStats?.timeSpentByActivity ?? progressionStats?.TimeSpentByActivity;
      const sumFromBucketsGlobal = fallbackActivityKeys.reduce((sum, k) => sum + statNum(timesGlobal, k), 0);
      const uniqueFromStatsGlobal = Number(
        progressionStats?.uniqueStudySeconds ?? progressionStats?.UniqueStudySeconds ?? NaN
      );
      const totalStudySecondsGlobal =
        Number.isFinite(totalFromUsageGlobal) && totalFromUsageGlobal > 0
          ? totalFromUsageGlobal
          : Number.isFinite(uniqueFromStatsGlobal) && uniqueFromStatsGlobal > 0
            ? uniqueFromStatsGlobal
            : sumFromBucketsGlobal;
      const accountStudyPrimary = formatStudySeconds(totalStudySecondsGlobal);
      const blockSecs = Number(usageSummary?.totalBlockTimeSeconds ?? usageSummary?.TotalBlockTimeSeconds);
      const quizSecs = Number(usageSummary?.totalQuizTimeSeconds ?? usageSummary?.TotalQuizTimeSeconds);
      const exerciseSecs = Number(usageSummary?.totalExerciseTimeSeconds ?? usageSummary?.TotalExerciseTimeSeconds ?? 0);
      const topLessonsRaw = (detailForSections?.topLessonsByTime ??
        detailForSections?.TopLessonsByTime) as Array<Record<string, unknown>> | undefined;
      const topLessonsList = Array.isArray(topLessonsRaw)
        ? topLessonsRaw.map((row) => ({
            lessonId: Number(row.lessonId ?? row.LessonId ?? 0),
            title: String(row.lessonTitle ?? row.LessonTitle ?? ''),
            course: (row.courseName ?? row.CourseName) as string | null | undefined,
            seconds: Number(row.seconds ?? row.Seconds ?? 0)
          }))
        : [];

      const formatDur = (sec: number) => formatStudySeconds(sec);

      const profileChartFont = profileChartFontFamily(isPersian);
      const chartAxisFontSize = getProfileChartAxisFontSize();
      const chartBarLabelFontSize = getProfileChartBarLabelFontSize();
      const formatChartMinutesLabel = (minutes: number) =>
        tLearning('dashboardExtra.minutesShort', { count: minutes });

      const lastAct = usageSummary?.lastTrackedActivityUtc ?? usageSummary?.LastTrackedActivityUtc;
      const lastActLabel =
        typeof lastAct === 'string' && lastAct
          ? formatAppDateTime(lastAct, locale)
          : null;
      const sessionsTracked = Number(
        usageSummary?.trackedContentBlockSessions ?? usageSummary?.TrackedContentBlockSessions ?? 0
      );
      const distinctLessons = Number(
        usageSummary?.distinctLessonsWithTime ?? usageSummary?.DistinctLessonsWithTime ?? 0
      );

      const chartPalette = [
        'rgba(239, 68, 68, 0.85)',
        'rgba(245, 158, 11, 0.85)',
        'rgba(59, 130, 246, 0.85)',
        'rgba(16, 185, 129, 0.85)',
        'rgba(139, 92, 246, 0.85)',
        'rgba(236, 72, 153, 0.85)',
        'rgba(14, 165, 233, 0.85)',
        'rgba(100, 116, 139, 0.85)'
      ];
      const chartBorderPalette = chartPalette.map((c) => c.replace('0.85', '1'));

      const topLessonsBarData =
        topLessonsList.length > 0
          ? {
              labels: topLessonsList.map((row) => {
                const raw = (row.title || tProfile('progressTab.lessonIdFallback', { id: row.lessonId })).trim();
                return raw.length > 26 ? `${raw.slice(0, 24)}…` : raw;
              }),
              datasets: [
                {
                  label: tProfile('progress.charts.studyTimeMinutes'),
                  data: topLessonsList.map((row) => Math.round(row.seconds / 60)),
                  backgroundColor: topLessonsList.map((_, i) => chartPalette[i % chartPalette.length]),
                  borderColor: topLessonsList.map((_, i) => chartBorderPalette[i % chartBorderPalette.length]),
                  borderWidth: 1.5,
                  borderRadius: 4
                }
              ]
            }
          : null;

      const buildSliceCharts = () => {
        if (!statsForSections) return { activity: null, score: null, time: null };
        if (Array.isArray(chartSlicesRaw) && chartSlicesRaw.length > 0) {
          const labels = chartSlicesRaw.map((row) => {
            const key = String(row.key ?? row.Key ?? '');
            const label = String(row.label ?? row.Label ?? key);
            return translateChartSliceLabel(key, label);
          });
          const completedData = chartSlicesRaw.map((row) => sliceNum(row, 'completed', 'Completed'));
          const avgData = chartSlicesRaw.map((row) => sliceNum(row, 'averageScore', 'AverageScore'));
          const timeMin = chartSlicesRaw.map((row) => Math.round(sliceNum(row, 'timeSeconds', 'TimeSeconds') / 60));
          const bg = labels.map((_, i) => chartPalette[i % chartPalette.length]);
          const bd = labels.map((_, i) => chartBorderPalette[i % chartBorderPalette.length]);
          return {
            activity: {
              labels,
              datasets: [{ label: tProfile('progress.charts.completedActivities'), data: completedData, backgroundColor: bg, borderColor: bd, borderWidth: 2 }]
            },
            score: {
              labels,
              datasets: [{ label: tProfile('progress.charts.averageScore'), data: avgData, backgroundColor: 'rgba(139, 92, 246, 0.6)', borderColor: 'rgb(139, 92, 246)', borderWidth: 2 }]
            },
            time: {
              labels,
              datasets: [{
                label: tProfile('progress.charts.timeSpentMinutes'),
                data: timeMin,
                backgroundColor: bg,
                borderColor: bd,
                borderWidth: 1.5,
                borderRadius: 4
              }]
            }
          };
        }
        const labelsFb = [
          tProfile('progress.charts.quizzes'),
          tProfile('progress.charts.exercises'),
          tProfile('progress.charts.vocabulary'),
          tProfile('progress.charts.grammar'),
          tProfile('progress.charts.lessons'),
        ];
        const keysFb = fallbackActivityKeys;
        const timeBarColorsFb = [
          'rgba(236, 72, 153, 0.75)',
          'rgba(245, 158, 11, 0.8)',
          'rgba(59, 130, 246, 0.75)',
          'rgba(16, 185, 129, 0.75)',
          'rgba(139, 92, 246, 0.75)'
        ];
        const timeBarBordersFb = ['rgb(219, 39, 119)', 'rgb(217, 119, 6)', 'rgb(37, 99, 235)', 'rgb(5, 150, 105)', 'rgb(109, 40, 217)'];
        return {
          activity: {
            labels: labelsFb,
            datasets: [{
              label: tProfile('progress.charts.completedActivities'),
              data: keysFb.map((k) => statNum(completed, k)),
              backgroundColor: keysFb.map((_, i) => chartPalette[i % chartPalette.length]),
              borderColor: keysFb.map((_, i) => chartBorderPalette[i % chartBorderPalette.length]),
              borderWidth: 2
            }]
          },
          score: {
            labels: labelsFb,
            datasets: [{
              label: tProfile('progress.charts.averageScore'),
              data: keysFb.map((k) => statNum(averages, k)),
              backgroundColor: 'rgba(139, 92, 246, 0.6)',
              borderColor: 'rgb(139, 92, 246)',
              borderWidth: 2
            }]
          },
          time: times
            ? {
                labels: labelsFb,
                datasets: [{
                  label: tProfile('progress.charts.timeSpentMinutes'),
                  data: keysFb.map((k) => Math.round(statNum(times, k) / 60)),
                  backgroundColor: keysFb.map((_, i) => timeBarColorsFb[i % timeBarColorsFb.length]),
                  borderColor: keysFb.map((_, i) => timeBarBordersFb[i % timeBarBordersFb.length]),
                  borderWidth: 1.5,
                  borderRadius: 4
                }]
              }
            : null
        };
      };

      const { activity: activityChartData, time: timeChartData } = buildSliceCharts();

      const statsAny = statsForSections as Record<string, unknown> | undefined;
      const catQTotal = Number(statsAny?.catalogTotalQuizzes ?? statsAny?.CatalogTotalQuizzes ?? 0);
      const catQPassed = Number(statsAny?.catalogPassedQuizzes ?? statsAny?.CatalogPassedQuizzes ?? 0);
      const catETotal = Number(statsAny?.catalogTotalExercises ?? statsAny?.CatalogTotalExercises ?? 0);
      const catERev = Number(statsAny?.catalogReviewedExercises ?? statsAny?.CatalogReviewedExercises ?? 0);
      const catQuizPct = catQTotal > 0 ? Math.round((catQPassed / catQTotal) * 100) : 0;
      const catExPct = catETotal > 0 ? Math.round((catERev / catETotal) * 100) : 0;
      const catLessTotal = Number(statsAny?.catalogTotalLessons ?? statsAny?.CatalogTotalLessons ?? 0);
      const catLessDone = Number(statsAny?.catalogCompletedLessons ?? statsAny?.CatalogCompletedLessons ?? 0);
      const catLessPct = catLessTotal > 0 ? Math.round((catLessDone / catLessTotal) * 100) : 0;

      const pieChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'bottom' as const,
            labels: {
              color: isDark ? '#e5e7eb' : '#374151',
              padding: 15,
              font: { family: profileChartFont, size: 11 }
            }
          },
          tooltip: {
            backgroundColor: isDark ? 'rgba(31, 41, 55, 0.9)' : 'rgba(255, 255, 255, 0.9)',
            titleColor: isDark ? '#e5e7eb' : '#374151',
            bodyColor: isDark ? '#e5e7eb' : '#374151',
            borderColor: isDark ? '#4b5563' : '#e5e7eb',
            borderWidth: 1,
            titleFont: { family: profileChartFont },
            bodyFont: { family: profileChartFont }
          }
        }
      };

      const timeBarChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: 'index' as const,
          intersect: false
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: isDark ? 'rgba(31, 41, 55, 0.9)' : 'rgba(255, 255, 255, 0.9)',
            titleColor: isDark ? '#e5e7eb' : '#374151',
            bodyColor: isDark ? '#e5e7eb' : '#374151',
            borderColor: isDark ? '#4b5563' : '#e5e7eb',
            borderWidth: 1,
            titleFont: { family: profileChartFont },
            bodyFont: { family: profileChartFont },
            callbacks: {
              title: (items: { label?: string }[]) =>
                items.length && items[0]?.label ? String(items[0].label) : '',
              label: (ctx: { parsed?: { y: number } }) => {
                const m = ctx.parsed?.y ?? 0;
                return tProfile('progressTab.minTracked', { minutes: m });
              }
            }
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            grace: '18%',
            ticks: {
              color: isDark ? '#9ca3af' : '#6b7280',
              precision: 0,
              font: { family: profileChartFont, size: chartAxisFontSize }
            },
            grid: {
              color: isDark ? 'rgba(75, 85, 99, 0.35)' : 'rgba(229, 231, 235, 0.8)'
            }
          },
          x: {
            ticks: {
              color: isDark ? '#9ca3af' : '#6b7280',
              maxRotation: 50,
              minRotation: 0,
              autoSkip: true,
              maxTicksLimit: 14,
              font: { family: profileChartFont, size: chartAxisFontSize }
            },
            grid: { display: false }
          }
        }
      };
      const timeBarPlugins = [
        createProfileTimeBarTopLabelsPlugin(
          isDark,
          profileChartFont,
          formatChartMinutesLabel,
          chartBarLabelFontSize
        ),
      ];

      const topLessonsBarOptions = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: 'index' as const,
          intersect: false
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: isDark ? 'rgba(31, 41, 55, 0.9)' : 'rgba(255, 255, 255, 0.9)',
            titleColor: isDark ? '#e5e7eb' : '#374151',
            bodyColor: isDark ? '#e5e7eb' : '#374151',
            borderColor: isDark ? '#4b5563' : '#e5e7eb',
            borderWidth: 1,
            titleFont: { family: profileChartFont },
            bodyFont: { family: profileChartFont },
            callbacks: {
              title: (items: { dataIndex?: number }[]) => {
                const i = items[0]?.dataIndex ?? 0;
                const row = topLessonsList[i];
                if (!row) return '';
                const course = row.course ? ` (${row.course})` : '';
                return `${row.title || tProfile('progressTab.lessonIdFallback', { id: row.lessonId })}${course}`;
              },
              label: (ctx: { dataIndex?: number }) => {
                const i = ctx.dataIndex ?? 0;
                const secs = topLessonsList[i]?.seconds ?? 0;
                return tProfile('progressTab.timeInLessonViewer', { duration: formatDur(secs) });
              }
            }
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            grace: '18%',
            ticks: {
              color: isDark ? '#9ca3af' : '#6b7280',
              precision: 0,
              font: { family: profileChartFont, size: chartAxisFontSize }
            },
            grid: {
              color: isDark ? 'rgba(75, 85, 99, 0.35)' : 'rgba(229, 231, 235, 0.8)'
            }
          },
          x: {
            ticks: {
              color: isDark ? '#9ca3af' : '#6b7280',
              maxRotation: 55,
              minRotation: 0,
              autoSkip: false,
              font: { family: profileChartFont, size: chartAxisFontSize }
            },
            grid: { display: false }
          }
        }
      };
      const topLessonsBarPlugins = [
        createProfileTimeBarTopLabelsPlugin(
          isDark,
          profileChartFont,
          formatChartMinutesLabel,
          chartBarLabelFontSize
        ),
      ];

      const hourlyActivityPattern = (progressDetail?.hourlyActivityPattern ??
        progressDetail?.HourlyActivityPattern) as
        | Array<{
            hour?: number;
            Hour?: number;
            actionCount?: number;
            ActionCount?: number;
            successfulCount?: number;
            SuccessfulCount?: number;
            failedCount?: number;
            FailedCount?: number;
            successRate?: number;
            SuccessRate?: number;
          }>
        | undefined;

      const dailyActivityHeatmap = normalizeHeatmapResponse(
        progressDetail?.dailyActivityHeatmap ?? progressDetail?.DailyActivityHeatmap
      );

      // Backend buckets the hourly pattern by ApplicationUser.TimeZone when set, otherwise the
      // device zone we passed on the request. Mirror that priority here for the label so the
      // displayed zone always matches the data: saved user zone (from Quick settings) wins,
      // otherwise fall back to the device IANA zone.
      const clientDeviceTz = getDeviceTimeZone();
      const savedUserTz = goalsPrefs?.timeZone ? String(goalsPrefs.timeZone).trim() : '';
      const profileDoughnutChartOptions = {
        cutout: '70%',
        plugins: {
          legend: { display: false },
          tooltip: {
            enabled: true,
            titleFont: { family: profileChartFont },
            bodyFont: { family: profileChartFont },
            callbacks: {
              label: (context: { label: string; parsed: number }) => `${context.label}: ${context.parsed}%`
            }
          }
        },
        maintainAspectRatio: false
      };

      const activityPatternTzLabel = savedUserTz || clientDeviceTz;

      return (
        <div className="space-y-6" dir={direction}>
          {renderTabContentHero('progress')}

          {loadingProgress ? (
            <LearnerInlineContentLoader message={tProfile('progressTab.loading')} />
          ) : (
            <>
              {progressionStats && (
                <>
                  <div className="clay-card overflow-hidden dark:border dark:border-gray-700 w-full mb-4 p-1 sm:p-1.5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 sm:gap-1.5">
                      <div
                        className="relative rounded-xl px-4 py-4 sm:px-5 sm:py-5 text-center sm:text-start overflow-hidden sm:rounded-e-none sm:rounded-s-xl"
                        dir={direction}
                        style={{
                          background: isDark
                            ? makeGradient('#422006', '#713f12', '#451a03')
                            : makeGradient('#fef9c3', '#fde68a', '#fef08a')
                        }}
                      >
                        <RtlDecorLayer className="absolute -right-6 -top-6 h-24 w-24 rounded-full bg-amber-400/25 dark:bg-amber-300/10" aria-hidden />
                        <div className={tabHeroRowClass}>
                          <div className="mx-auto flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/80 dark:bg-black/25 text-amber-600 dark:text-amber-300 shadow-sm sm:mx-0">
                            <Sparkles className="h-5 w-5" aria-hidden />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-900/80 dark:text-amber-100/80">
                              {tProfile('progressTab.experience')}
                            </p>
                            <p className="text-xl sm:text-2xl font-bold tabular-nums text-amber-950 dark:text-amber-50">
                              {(progressionStats.totalXP ?? progressionStats.TotalXP ?? 0).toLocaleString(locale === 'fa' ? 'fa-IR' : 'en-US')}
                            </p>
                            <p className="text-xs text-amber-900/70 dark:text-amber-100/70 mt-0.5">{tProfile('progressTab.totalXpAccount')}</p>
                          </div>
                        </div>
                      </div>
                      <div
                        className="relative rounded-xl px-4 py-4 sm:px-5 sm:py-5 text-center sm:text-start overflow-hidden sm:rounded-s-none sm:rounded-e-xl"
                        dir={direction}
                        style={{
                          background: isDark
                            ? makeGradient('#0c4a6e', '#075985', '#0e7490')
                            : makeGradient('#e0f2fe', '#bae6fd', '#a5f3fc')
                        }}
                      >
                        <RtlDecorLayer className="absolute -left-8 bottom-0 h-28 w-28 rounded-full bg-sky-400/20 dark:bg-cyan-300/10" aria-hidden />
                        <div className={tabHeroRowClass}>
                          <div className="mx-auto flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/80 dark:bg-black/25 text-sky-700 dark:text-sky-200 shadow-sm sm:mx-0">
                            <Timer className="h-5 w-5" aria-hidden />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-[11px] font-semibold uppercase tracking-wide text-sky-900/75 dark:text-sky-100/80">
                              {tProfile('progressTab.studyTime')}
                            </p>
                            <p className="text-xl sm:text-2xl font-bold tabular-nums text-sky-950 dark:text-sky-50">{accountStudyPrimary}</p>
                            <p className="text-xs text-sky-900/70 dark:text-sky-100/70 mt-0.5">{tProfile('progressTab.totalFocusedTime')}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mb-4 space-y-4">
                    <HourlyActivityPatternCard
                      timeZoneLabel={activityPatternTzLabel}
                      initialData={hourlyActivityPattern}
                    />
                    {dailyActivityHeatmap && (
                      <>
                        <ActivityHeatmap
                          data={dailyActivityHeatmap}
                          className="clay-card dark:border dark:border-gray-700"
                          title={tProfile('progress.charts.activityOverTime')}
                          subtitle={tProfile('progress.charts.activityOverTimeHint')}
                        />
                      </>
                    )}
                  </div>

                  {progressCoursesCatalog.length > 0 && (
                    <div className="rounded-2xl border border-gray-200/90 dark:border-gray-600 bg-white dark:bg-gray-800/50 px-3 py-3 sm:px-4 mb-4">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <span className="text-xs font-semibold text-gray-700 dark:text-gray-200">{tProfile('progressTab.timeActivityScope')}</span>
                        {loadingProgressScope && (
                          <span className="inline-flex h-4 w-4 border-2 border-gray-200 border-t-teal-600 rounded-full animate-spin" aria-hidden />
                        )}
                      </div>
                      <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-2">
                        {tProfile('progressTab.scopeHint')}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => setProgressScopeCourseIds(null)}
                          className={`min-h-[36px] rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                            !progressScopeCourseIds || progressScopeCourseIds.length === 0
                              ? 'border-teal-500 bg-teal-50 text-teal-900 dark:bg-teal-950/50 dark:text-teal-100'
                              : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200'
                          }`}
                        >
                          {tProfile('progressTab.allCourses')}
                        </button>
                        {progressCoursesCatalog.map((c) => {
                          const selected = Array.isArray(progressScopeCourseIds) && progressScopeCourseIds.includes(c.id);
                          return (
                            <button
                              key={c.id}
                              type="button"
                              onClick={() => {
                                setProgressScopeCourseIds((prev) => {
                                  const cur = prev ?? [];
                                  if (cur.includes(c.id)) {
                                    const next = cur.filter((x) => x !== c.id);
                                    return next.length ? next : null;
                                  }
                                  return [...cur, c.id];
                                });
                              }}
                              className={`max-w-[200px] min-h-[36px] truncate rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                                selected
                                  ? 'border-violet-500 bg-violet-50 text-violet-900 dark:bg-violet-950/40 dark:text-violet-100'
                                  : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200'
                              }`}
                              title={c.title}
                            >
                              {c.title}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div className="mb-6 overflow-hidden rounded-2xl border border-gray-200/90 bg-white shadow-sm [overflow-anchor:none] dark:border-gray-600 dark:bg-gray-800/40">
                    <button
                      type="button"
                      onClick={() => setProgressTimeSectionOpen((o) => !o)}
                      aria-expanded={progressTimeSectionOpen}
                      className="w-full flex items-center justify-between gap-3 px-4 py-3.5 text-left transition-colors hover:bg-gray-50/80 dark:hover:bg-gray-800/60"
                    >
                      <span className="flex items-center gap-2.5 min-w-0">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-teal-100 dark:bg-teal-900/50">
                          <Timer className="h-5 w-5 text-teal-600 dark:text-teal-300" />
                        </span>
                        <span className="min-w-0">
                          <span className="block text-sm font-semibold text-gray-900 dark:text-gray-100">{tProfile('progressTab.timeSection')}</span>
                          <span className="block text-xs text-gray-500 dark:text-gray-400">
                            {tProfile('progressTab.timeSectionHint')}
                          </span>
                        </span>
                      </span>
                      <ChevronDown
                        className={`h-5 w-5 shrink-0 text-gray-400 transition-transform duration-200 ease-out ${
                          progressTimeSectionOpen ? 'rotate-180' : 'rotate-0'
                        }`}
                        aria-hidden
                      />
                    </button>
                    <div
                      className="grid transition-[grid-template-rows,opacity] duration-200 ease-out motion-reduce:transition-none"
                      style={{
                        gridTemplateRows: progressTimeSectionOpen ? '1fr' : '0fr',
                        opacity: progressTimeSectionOpen ? 1 : 0
                      }}
                    >
                      <div className="min-h-0 overflow-hidden">
                        <div
                          className={`space-y-4 border-t border-gray-100 bg-slate-50/60 px-3 py-4 transition-[transform,opacity] duration-200 ease-out motion-reduce:transition-none dark:border-gray-700 dark:bg-gray-900/30 sm:px-4 ${
                            progressTimeSectionOpen ? 'translate-y-0 opacity-100' : '-translate-y-2 opacity-0'
                          }`}
                        >
                        <div className="clay-card bg-white dark:bg-gray-800 p-4 dark:border dark:border-gray-700 w-full min-w-0">
                          <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2 flex items-center">
                            <Clock className="w-4 h-4 mr-2 text-blue-500" />
                            {tProfile('progressTab.studyTimeHeading')}
                          </h3>
                          <div className="text-center">
                            <p className="text-2xl sm:text-3xl font-bold text-blue-600 dark:text-blue-400">{studyPrimary}</p>
                          </div>
                        </div>

                        {timeChartData && (
                          <div className="clay-card bg-white dark:bg-gray-800 p-4 dark:border dark:border-gray-700 w-full min-w-0">
                            <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2 flex items-center">
                              <Activity className="w-4 h-4 mr-2 text-blue-500" />
                              {tProfile('progressTab.timeSpentByActivity')}
                            </h3>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3 leading-relaxed">
                              {tProfile('progressTab.timeSpentGuidanceShort')}
                            </p>
                            <div className="h-64 w-full min-w-0">
                              <Bar data={timeChartData} options={timeBarChartOptions} plugins={timeBarPlugins} />
                            </div>
                          </div>
                        )}

                        {usageSummary && (
                          <div className="grid w-full min-w-0 grid-cols-2 xl:grid-cols-5 gap-3 sm:gap-4">
                            <div className="clay-card bg-white dark:bg-gray-800 p-3 sm:p-4 dark:border dark:border-gray-700 rounded-xl">
                              <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 text-xs font-medium uppercase tracking-wide mb-1">
                                <Timer className="w-4 h-4" />
                                {tProfile('progressTab.totalStudy')}
                              </div>
                              <p className="text-lg sm:text-xl font-bold text-gray-900 dark:text-gray-100">{studyPrimary}</p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{tProfile('progressTab.lessonsQuizzesExercises')}</p>
                            </div>
                            <div className="clay-card bg-white dark:bg-gray-800 p-3 sm:p-4 dark:border dark:border-gray-700 rounded-xl">
                              <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 text-xs font-medium uppercase tracking-wide mb-1">
                                <BookOpen className="w-4 h-4" />
                                {tProfile('progressTab.lessonTime')}
                              </div>
                              <p className="text-lg sm:text-xl font-bold text-indigo-600 dark:text-indigo-400">
                                {formatDur(Number.isFinite(blockSecs) ? blockSecs : 0)}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                {tProfile('progressTab.blockSessions', { count: sessionsTracked })}
                              </p>
                            </div>
                            <div className="clay-card bg-white dark:bg-gray-800 p-3 sm:p-4 dark:border dark:border-gray-700 rounded-xl">
                              <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 text-xs font-medium uppercase tracking-wide mb-1">
                                <Target className="w-4 h-4" />
                                {tProfile('progressTab.quizzes')}
                              </div>
                              <p className="text-lg sm:text-xl font-bold text-rose-600 dark:text-rose-400">
                                {formatDur(Number.isFinite(quizSecs) ? quizSecs : 0)}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{tProfile('progressTab.quizTimerHint')}</p>
                            </div>
                            <div className="clay-card bg-white dark:bg-gray-800 p-3 sm:p-4 dark:border dark:border-gray-700 rounded-xl">
                              <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 text-xs font-medium uppercase tracking-wide mb-1">
                                <PenLine className="w-4 h-4" />
                                {tProfile('progressTab.exercises')}
                              </div>
                              <p className="text-lg sm:text-xl font-bold text-stone-700 dark:text-stone-300">
                                {formatDur(Number.isFinite(exerciseSecs) ? exerciseSecs : 0)}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{tProfile('progressTab.exerciseScopeHint')}</p>
                            </div>
                            <div className="clay-card bg-white dark:bg-gray-800 p-3 sm:p-4 dark:border dark:border-gray-700 rounded-xl col-span-2 xl:col-span-1 w-full min-w-0">
                              <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 text-xs font-medium uppercase tracking-wide mb-1">
                                <Calendar className="w-4 h-4" />
                                {tProfile('progressTab.activitySection')}
                              </div>
                              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 leading-snug">
                                {lastActLabel ?? '—'}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                {distinctLessons > 0
                                  ? tProfile('progressTab.lessonsWithTime', { count: distinctLessons })
                                  : tProfile('progressTab.noLessonTimer')}
                              </p>
                            </div>
                          </div>
                        )}

                        {topLessonsBarData && (
                          <div className="clay-card bg-white dark:bg-gray-800 p-4 dark:border dark:border-gray-700 w-full min-w-0">
                            <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1 flex items-center">
                              <BookOpen className="w-4 h-4 mr-2 text-emerald-500" />
                              {tProfile('progressTab.topLessonsTitle')}
                            </h3>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                              {tProfile('progressTab.topLessonsHint')}
                            </p>
                            <div className="h-[min(20rem,48dvh)] min-h-[14rem] w-full min-w-0">
                              <Bar data={topLessonsBarData} options={topLessonsBarOptions} plugins={topLessonsBarPlugins} />
                            </div>
                          </div>
                        )}
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {progressionStats && (
                <div className={`space-y-6${isPersian ? ' font-persian' : ''}`}>
                  <div className="overflow-hidden rounded-2xl border border-gray-200/90 bg-white shadow-sm [overflow-anchor:none] dark:border-gray-600 dark:bg-gray-800/40">
                    <button
                      type="button"
                      onClick={() => setProgressActivitySectionOpen((o) => !o)}
                      aria-expanded={progressActivitySectionOpen}
                      className="w-full flex items-center justify-between gap-3 px-4 py-3.5 text-left transition-colors hover:bg-gray-50/80 dark:hover:bg-gray-800/60"
                    >
                      <span className="flex items-center gap-2.5 min-w-0">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-violet-100 dark:bg-violet-900/50">
                          <ClipboardList className="h-5 w-5 text-violet-600 dark:text-violet-300" />
                        </span>
                        <span className="min-w-0">
                          <span className="block text-sm font-semibold text-gray-900 dark:text-gray-100">{tProfile('progressTab.activitySection')}</span>
                          <span className="block text-xs text-gray-500 dark:text-gray-400">
                            {tProfile('progressTab.activitySectionCatalogHint')}
                          </span>
                        </span>
                      </span>
                      <ChevronDown
                        className={`h-5 w-5 shrink-0 text-gray-400 transition-transform duration-200 ease-out ${
                          progressActivitySectionOpen ? 'rotate-180' : 'rotate-0'
                        }`}
                        aria-hidden
                      />
                    </button>
                    <div
                      className="grid transition-[grid-template-rows,opacity] duration-200 ease-out motion-reduce:transition-none"
                      style={{
                        gridTemplateRows: progressActivitySectionOpen ? '1fr' : '0fr',
                        opacity: progressActivitySectionOpen ? 1 : 0
                      }}
                    >
                      <div className="min-h-0 overflow-hidden">
                        <div
                          className={`space-y-4 border-t border-gray-100 bg-slate-50/60 px-3 py-4 transition-[transform,opacity] duration-200 ease-out motion-reduce:transition-none dark:border-gray-700 dark:bg-gray-900/30 sm:px-4 ${
                            progressActivitySectionOpen ? 'translate-y-0 opacity-100' : '-translate-y-2 opacity-0'
                          }`}
                        >
                  {activityChartData && (
                    <div className="clay-card bg-white dark:bg-gray-800 p-4 dark:border dark:border-gray-700">
                      <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2 flex items-center">
                        <PieChart className="w-5 h-5 mr-2 text-purple-500" />
                        {tProfile('progressTab.activityDistribution')}
                      </h3>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mb-4 leading-relaxed">
                        {tProfile('progressTab.activityDistributionDesc')}
                      </p>
                      <div className="h-64">
                        <Pie data={activityChartData} options={pieChartOptions} />
                      </div>
                    </div>
                  )}

                  {/* Practice coverage — same ratios as dashboard Learning Statistics */}
                  {(catQTotal > 0 || catETotal > 0) && (
                    <div className={rtlDecor('clay-card overflow-hidden bg-gradient-to-br from-rose-50 via-white to-stone-100 p-4 dark:from-rose-950/30 dark:via-gray-900 dark:to-stone-900/25 dark:border dark:border-gray-700')}>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1 flex items-center gap-2">
                        <Target className="w-5 h-5 text-rose-500" />
                        {tProfile('progressTab.practiceCoverage')}
                      </h3>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mb-4 max-w-2xl">
                        {tProfile('progressTab.practiceCoverageHint')}
                      </p>
                      <div className="grid gap-6 sm:grid-cols-2">
                        <div>
                          <div className="flex justify-between text-sm font-medium text-gray-800 dark:text-gray-200 mb-2">
                            <span>{tProfile('progressTab.quizzesPassedLabel')}</span>
                            <span className="tabular-nums text-rose-600 dark:text-rose-400">
                              {catQPassed}/{catQTotal || '—'} ({catQTotal > 0 ? `${catQuizPct}%` : '—'})
                            </span>
                          </div>
                          <div className="h-3 w-full rounded-full bg-rose-100 dark:bg-rose-950/50 overflow-hidden">
                            <div
                              className={rtlDecor('h-full rounded-full bg-gradient-to-r from-rose-500 to-pink-500 transition-all duration-500')}
                              style={{ width: `${catQuizPct}%` }}
                            />
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between text-sm font-medium text-gray-800 dark:text-gray-200 mb-2">
                            <span>{tProfile('progressTab.exercisesReviewedLabel')}</span>
                            <span className="tabular-nums text-stone-800 dark:text-stone-300">
                              {catERev}/{catETotal || '—'} ({catETotal > 0 ? `${catExPct}%` : '—'})
                            </span>
                          </div>
                          <div className="h-3 w-full rounded-full bg-stone-200 dark:bg-stone-800/50 overflow-hidden">
                            <div
                              className={rtlDecor('h-full rounded-full bg-gradient-to-r from-stone-600 to-amber-900 transition-all duration-500')}
                              style={{ width: `${catExPct}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {(catLessTotal > 0 ||
                    progressPublicTags.length > 0 ||
                    catQTotal > 0 ||
                    catETotal > 0) && (
                    <div className={rtlDecor('clay-card overflow-hidden rounded-xl border border-amber-200/50 bg-gradient-to-br from-amber-50 via-white to-stone-50 p-5 dark:border-amber-900/40 dark:from-amber-950/25 dark:via-gray-900 dark:to-stone-950/20 dark:bg-gray-900/40')}>
                      <h3 className="mb-1 flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
                        <BookOpen className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                        {tProfile('progressTab.lessonsCatalogTopics')}
                      </h3>
                      <p className="mb-4 max-w-2xl text-xs text-gray-600 dark:text-gray-400">
                        {tProfile('progressTab.lessonsCatalogTopicsHint')}
                        {onNavigateToTag ? tProfile('progressTab.lessonsCatalogTopicsTapHint') : ''}
                      </p>
                      <div
                        data-no-tab-swipe
                        className={`-mx-1 flex gap-3 overflow-x-auto overscroll-x-contain touch-pan-x px-1 pb-1${simpleHome ? ' mme-progress-charts' : ''}`}
                        style={{ WebkitOverflowScrolling: 'touch', scrollbarWidth: 'thin' }}
                      >
                        {catLessTotal > 0 && (
                          <div className="flex min-w-[220px] max-w-[280px] flex-shrink-0 flex-col rounded-xl border border-emerald-200/70 bg-white/80 p-3 dark:border-emerald-900/50 dark:bg-slate-800/50">
                            <div className="mb-2 flex items-center gap-2">
                              <BookOpen className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
                              <p className="truncate text-sm font-medium text-gray-800 dark:text-gray-200">{tProfile('progress.charts.lessons')}</p>
                            </div>
                            <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
                              {tProfile('progressTab.lessonsCompletedCount', { done: catLessDone, total: catLessTotal })}
                            </p>
                            <div className="relative mx-auto h-28 w-28">
                              <Doughnut
                                data={{
                                  labels: [tProfile('progress.charts.completed'), tProfile('progress.charts.remaining')],
                                  datasets: [
                                    {
                                      data: [catLessPct, 100 - catLessPct],
                                      backgroundColor: ['rgba(16, 185, 129, 0.85)', 'rgba(229, 231, 235, 0.35)'],
                                      borderColor: ['rgba(5, 150, 105, 1)', 'rgba(229, 231, 235, 0.5)'],
                                      borderWidth: 0
                                    }
                                  ]
                                }}
                                options={profileDoughnutChartOptions}
                              />
                              <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                                <span className="text-sm font-bold text-emerald-800 dark:text-emerald-300">{catLessPct}%</span>
                              </div>
                            </div>
                            {onNavigateHome && (
                              <button
                                type="button"
                                onClick={onNavigateHome}
                                className="mt-2 text-center text-xs font-medium text-emerald-700 underline decoration-emerald-400/60 hover:text-emerald-900 dark:text-emerald-400"
                              >
                                {tProfile('progressTab.dashboardCourses')}
                              </button>
                            )}
                          </div>
                        )}
                        {progressPublicTags.map((tag) => {
                          const IconComponent = tag.icon ? getIconByName(tag.icon) : BookOpen;
                          const tagProgress = progressTagProgressList.find((p) => p.tagId === tag.id);
                          const passedBlocks = tagProgress?.passedBlocks ?? 0;
                          const totalBlocks = tagProgress?.totalBlocks ?? 0;
                          const passedLessons = tagProgress?.passedLessons ?? 0;
                          const totalLessons = tagProgress?.totalLessons ?? 0;
                          const passedAll = passedBlocks + passedLessons;
                          const totalAll = totalBlocks + totalLessons;
                          const progress =
                            tagProgress != null && totalAll > 0
                              ? Math.round(tagProgress.progressPercentage)
                              : totalAll > 0
                                ? Math.round((passedAll / totalAll) * 100)
                                : 0;
                          const progressClamped = Math.min(100, Math.max(0, progress));
                          const chartData = {
                            labels: [tProfile('progress.charts.progress'), tProfile('progress.charts.remaining')],
                            datasets: [
                              {
                                data: [progressClamped, 100 - progressClamped],
                                backgroundColor: ['rgba(217, 119, 6, 0.85)', 'rgba(229, 231, 235, 0.35)'],
                                borderColor: ['rgba(180, 83, 9, 1)', 'rgba(229, 231, 235, 0.5)'],
                                borderWidth: 0
                              }
                            ]
                          };
                          const doughnutOpts = profileDoughnutChartOptions;
                          return (
                            <button
                              key={tag.id}
                              type="button"
                              tabIndex={onNavigateToTag ? 0 : -1}
                              onClick={() => onNavigateToTag?.(tag.id)}
                              className={`flex min-w-[220px] max-w-[280px] flex-shrink-0 flex-col rounded-xl border border-amber-200/60 bg-white/70 p-3 text-left font-inherit dark:border-gray-700 dark:bg-slate-800/50 ${
                                onNavigateToTag
                                  ? 'cursor-pointer transition-colors hover:bg-white dark:hover:bg-slate-800/80'
                                  : 'cursor-default'
                              }`}
                            >
                              <div className="mb-2 flex items-center gap-2">
                                <IconComponent className="h-4 w-4 shrink-0 text-amber-700 dark:text-amber-400" />
                                <p className="truncate text-sm font-medium text-gray-800 dark:text-gray-200">
                                  {pickTagName(tag)}
                                </p>
                              </div>
                              <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
                                {totalAll === 0
                                  ? tProfile('progressTab.noTaggedContent')
                                  : tProfile('progressTab.tagCompleteCount', { passed: passedAll, total: totalAll })}
                                {totalBlocks > 0 && totalLessons > 0 && (
                                  <span className="mt-0.5 block text-[10px] leading-snug opacity-90">
                                    {tProfile('progressTab.tagBlocksLessons', {
                                      passedBlocks,
                                      totalBlocks,
                                      passedLessons,
                                      totalLessons,
                                    })}
                                  </span>
                                )}
                              </p>
                              <div className="relative mx-auto h-28 w-28">
                                <Doughnut data={chartData} options={doughnutOpts} />
                                <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                                  <span className="text-sm font-bold text-amber-800 dark:text-amber-400">
                                    {progressClamped}%
                                  </span>
                                </div>
                              </div>
                            </button>
                          );
                        })}
                        {catQTotal > 0 && (
                          <div className="flex min-w-[220px] max-w-[280px] flex-shrink-0 flex-col rounded-xl border border-rose-200/70 bg-white/80 p-3 dark:border-rose-900/50 dark:bg-slate-800/50">
                            <div className="mb-2 flex items-center gap-2">
                              <Trophy className="h-4 w-4 shrink-0 text-rose-600 dark:text-rose-400" />
                              <p className="truncate text-sm font-medium text-gray-800 dark:text-gray-200">{tProfile('progressTab.quizzes')}</p>
                            </div>
                            <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
                              {tProfile('progressTab.quizzesPassedCount', { passed: catQPassed, total: catQTotal })}
                            </p>
                            <div className="relative mx-auto h-28 w-28">
                              <Doughnut
                                data={{
                                  labels: [tProfile('progress.charts.passed'), tProfile('progress.charts.remaining')],
                                  datasets: [
                                    {
                                      data: [catQuizPct, 100 - catQuizPct],
                                      backgroundColor: ['rgba(236, 72, 153, 0.8)', 'rgba(229, 231, 235, 0.35)'],
                                      borderColor: ['rgba(236, 72, 153, 1)', 'rgba(229, 231, 235, 0.5)'],
                                      borderWidth: 0
                                    }
                                  ]
                                }}
                                options={profileDoughnutChartOptions}
                              />
                              <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                                <span className="text-sm font-bold text-rose-600 dark:text-rose-300">{catQuizPct}%</span>
                              </div>
                            </div>
                          </div>
                        )}
                        {catETotal > 0 && (
                          <div className="flex min-w-[220px] max-w-[280px] flex-shrink-0 flex-col rounded-xl border border-stone-300/80 bg-white/80 p-3 dark:border-stone-700/55 dark:bg-slate-800/50">
                            <div className="mb-2 flex items-center gap-2">
                              <PenLine className="h-4 w-4 shrink-0 text-stone-700 dark:text-stone-300" />
                              <p className="truncate text-sm font-medium text-gray-800 dark:text-gray-200">{tProfile('progressTab.exercises')}</p>
                            </div>
                            <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
                              {tProfile('progressTab.exercisesReviewedCount', { reviewed: catERev, total: catETotal })}
                            </p>
                            <div className="relative mx-auto h-28 w-28">
                              <Doughnut
                                data={{
                                  labels: [tProfile('progress.charts.reviewed'), tProfile('progress.charts.remaining')],
                                  datasets: [
                                    {
                                      data: [catExPct, 100 - catExPct],
                                      backgroundColor: ['rgba(120, 113, 108, 0.88)', 'rgba(229, 231, 235, 0.35)'],
                                      borderColor: ['rgba(68, 64, 60, 1)', 'rgba(229, 231, 235, 0.5)'],
                                      borderWidth: 0
                                    }
                                  ]
                                }}
                                options={profileDoughnutChartOptions}
                              />
                              <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                                <span className="text-sm font-bold text-stone-800 dark:text-stone-300">{catExPct}%</span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                        </div>
                      </div>
                    </div>
                  </div>

                </div>
              )}

              {!progressionStats && !loadingProgress && (
                <div className="clay-card bg-white dark:bg-gray-800 p-12 text-center dark:border dark:border-gray-700">
                  <TrendingUp className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 dark:text-gray-400 mb-2">{tProfile('progress.noDataTitle')}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-500">{tProfile('progress.noDataHint')}</p>
                </div>
              )}
            </>
          )}
        </div>
      );
    }

    return null;
  };

  if (loading) {
    return <PersianOverlayLoader isVisible message={tCommon('loaders.loadingProfile')} />;
  }

  if (!user) {
    return (
      <div className={`flex items-center justify-center min-h-screen ${className}`}>
        <div className="text-center">
          <p className="text-gray-600 mb-4">{tProfile('form.loadFailed')}</p>
          <button
            onClick={loadUserProfile}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            {tCommon('errors.tryAgain')}
          </button>
        </div>
      </div>
    );
  }

  return (
      <div className={`mme-tone max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 ${className || ''}`}>
      <div className="space-y-6">
        <div className="pt-6">
          {simpleHome ? (
            <SimplePageHeader
              tourId="profile-header"
              title={tProfile('myProfileTitle')}
              onBack={onNavigateHome}
              backLabel={tProfile('backToDashboard')}
            />
          ) : (
          <LearnerPageHero
            tourId="profile-header"
            className="mb-6"
            compactChrome
            eyebrow={tProfile('accountEyebrow')}
            title={tProfile('myProfileTitle')}
            subtitle={tProfile('myProfileSubtitle')}
            icon={User}
            belowIcon={
              <div className="flex flex-col items-center gap-0.5">
                <button
                  type="button"
                  onClick={() => window.history.back()}
                  className="inline-flex h-5 w-8 items-center justify-center rounded-md border border-violet-200/90 bg-white/70 text-violet-950 shadow-sm backdrop-blur-[2px] transition-all hover:border-violet-300 hover:bg-white/90 active:scale-[0.98] dark:border-violet-700/50 dark:bg-violet-950/40 dark:text-violet-50 dark:hover:border-violet-600/60 dark:hover:bg-violet-950/55 md:w-9"
                  aria-label={tLearning('lesson.goBack')}
                  title={tLearning('lesson.goBack')}
                >
                  <ArrowLeft className={`h-3.5 w-3.5 ${isRtl ? 'rotate-180' : ''}`} aria-hidden />
                </button>
                {onNavigateHome ? (
                  <button
                    type="button"
                    onClick={onNavigateHome}
                    className="inline-flex h-5 w-8 items-center justify-center rounded-md border border-violet-200/90 bg-white/70 text-violet-950 shadow-sm backdrop-blur-[2px] transition-all hover:border-violet-300 hover:bg-white/90 active:scale-[0.98] dark:border-violet-700/50 dark:bg-violet-950/40 dark:text-violet-50 dark:hover:border-violet-600/60 dark:hover:bg-violet-950/55 md:w-9"
                    aria-label={tProfile('backToDashboard')}
                    title={tProfile('backToDashboard')}
                  >
                    <LayoutDashboard className="h-3.5 w-3.5" aria-hidden />
                  </button>
                ) : null}
              </div>
            }
          />
          )}
        </div>

        {onOpenKnowledgePlacement && (
          <div
            data-tour-id="profile-knowledge-placement"
            className={rtlDecor('clay-card overflow-hidden border border-violet-200/80 bg-gradient-to-br from-violet-50/90 to-indigo-50/80 dark:border-violet-800/50 dark:from-violet-950/35 dark:to-slate-900/90')}
          >
            <button
              type="button"
              onClick={() => setIsKnowledgePlacementExpanded((prev) => !prev)}
              className="flex w-full items-center justify-between px-4 py-2.5 transition-colors hover:bg-violet-100/50 dark:hover:bg-violet-900/20 sm:px-5 sm:py-3"
              aria-expanded={isKnowledgePlacementExpanded}
              aria-label={
                isKnowledgePlacementExpanded
                  ? tProfile('knowledgePlacement.collapse')
                  : tProfile('knowledgePlacement.expand')
              }
            >
              <div className="flex min-w-0 items-center gap-3 text-left">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-violet-600 text-white shadow-md">
                  <Brain className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-base font-bold leading-none text-gray-900 dark:text-gray-100 sm:text-lg">{tProfile('knowledgePlacement.title')}</h3>
                </div>
              </div>
              <ChevronDown
                className={`h-5 w-5 shrink-0 text-violet-700 transition-transform duration-200 ease-out dark:text-violet-300 ${
                  isKnowledgePlacementExpanded ? 'rotate-180' : 'rotate-0'
                }`}
                aria-hidden
              />
            </button>
            <div
              className="grid transition-[grid-template-rows,opacity] duration-200 ease-out motion-reduce:transition-none"
              style={{
                gridTemplateRows: isKnowledgePlacementExpanded ? '1fr' : '0fr',
                opacity: isKnowledgePlacementExpanded ? 1 : 0
              }}
            >
              <div className="min-h-0 overflow-hidden">
              <div
                className={`px-4 pb-4 transition-[transform,opacity] duration-200 ease-out motion-reduce:transition-none sm:px-5 sm:pb-5 ${
                  isKnowledgePlacementExpanded ? 'translate-y-0 opacity-100' : '-translate-y-2 opacity-0'
                }`}
              >
                <p className="text-xs text-gray-500 dark:text-gray-400 sm:text-sm leading-relaxed mb-4">
                  {tProfile('knowledgePlacement.description')}
                </p>
                {placementState?.lastRecommendation && (
                  <p className="mt-1 text-sm font-medium text-gray-800 dark:text-gray-200">
                    {tProfile('knowledgePlacement.lastSuggestion')}{' '}
                    <span className="text-violet-800 dark:text-violet-300">{placementState.lastRecommendation.contextSubtitle}</span>
                  </p>
                )}
                {placementState?.lastOutcome === 'advanced' && !placementState?.lastRecommendation && (
                  <p className="mt-1 flex items-center gap-2 text-sm font-medium text-violet-900 dark:text-violet-200">
                    <Sparkles className="h-4 w-4 shrink-0" />
                    {tProfile('knowledgePlacement.advancedOutcome')}
                  </p>
                )}
                {placementState?.lastOutcome === 'opt_out' && (
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{tProfile('knowledgePlacement.optOutOutcome')}</p>
                )}
                {placementState?.lastRecommendation?.evaluatedAt && (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
                    {tProfile('knowledgePlacement.updated', { date: formatDate(placementState.lastRecommendation.evaluatedAt) })}
                  </p>
                )}
                <div className="mt-4 flex flex-wrap gap-2 sm:shrink-0">
                  {placementState?.lastRecommendation && onNavigateToLesson && (
                    <button
                      type="button"
                      onClick={() => onNavigateToLesson(placementState.lastRecommendation!.lessonId)}
                      className="rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-violet-800 shadow ring-1 ring-violet-200 hover:bg-violet-50 dark:bg-gray-800 dark:text-violet-200 dark:ring-violet-700 dark:hover:bg-gray-700"
                    >
                      {tProfile('knowledgePlacement.openSuggestedLesson')}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={onOpenKnowledgePlacement}
                    className="rounded-xl bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white shadow hover:bg-violet-700"
                  >
                    {placementState?.lastRecommendation || placementState?.lastOutcome === 'advanced' ? tProfile('knowledgePlacement.runAgain') : tProfile('knowledgePlacement.start')}
                  </button>
                </div>
              </div>
              </div>
            </div>
          </div>
        )}

        {/* Success/Error Messages */}
        {success && (
          <div
            className="clay-card p-4"
            style={{
              background: isDark ? colorWithAlpha('#15803d', 0.18) : '#ecfdf3',
              border: `1px solid ${isDark ? colorWithAlpha('#22c55e', 0.45) : '#bbf7d0'}`
            }}
          >
            <div className="flex items-center">
              <span className="text-green-600 mr-2">✅</span>
              <PersianText className="text-green-800 dark:text-green-100">{success}</PersianText>
            </div>
          </div>
        )}

        {error && (
          <div
            className="clay-card p-4"
            style={{
              background: isDark ? colorWithAlpha('#991b1b', 0.18) : '#fef2f2',
              border: `1px solid ${isDark ? colorWithAlpha('#f87171', 0.45) : '#fecaca'}`
            }}
          >
            <div className="flex items-center">
              <span className="text-red-600 mr-2">❌</span>
              <PersianText className="text-red-800 dark:text-red-100">{error}</PersianText>
            </div>
          </div>
        )}

        {/* Combined tab navigation + content card. Do not put `overflow-hidden` on this outer card: it becomes a scroll
            containment ancestor and breaks `position: sticky` for the tab bar (tabs must stick to the app scroll area).
            Horizontal clipping for the carousel lives on the tab-content wrapper only.
            `clay-card-solid-surface` disables clay-card backdrop blur so `position: sticky` works on mobile Chrome/WebKit. */}
        <div
          className="clay-card clay-card-solid-surface bg-white p-2 sm:p-4 mb-4 sm:mb-6 dark:bg-gray-900 dark:border dark:border-gray-800"
          style={{
            ['--profile-sticky-tabs-top' as string]: 'calc(4rem + env(safe-area-inset-top, 0px))',
            ['--profile-tab-bar-height' as string]: profileTabBarHeight > 0 ? `${profileTabBarHeight}px` : '0px'
          }}
        >
          {simpleHome && profileMenu ? (
            <MmePickList
              items={TAB_ORDER.map((tab) => ({
                id: tab,
                title: tProfile(`tabs.${tab}`),
                icon: PROFILE_TAB_ICONS[tab],
                tourId: `profile-tab-${tab}`,
                ...(tab === 'messages' && unreadMessageCount > 0 ? { badge: unreadMessageCount } : {}),
              }))}
              onPick={(id) => {
                const tab = id as TabType;
                setProfileMenu(false);
                if (tab !== activeTabRef.current) handleTabChange(tab);
                else pushProfileTabHistory(tab);
              }}
            />
          ) : null}
          {simpleHome && !profileMenu ? (
            <div className="px-1 pt-1">
              <MmePickBack
                onBack={() => {
                  setProfileMenu(true);
                  const menuUrl = `${window.location.pathname}${window.location.search}#menus`;
                  const prev = window.history.state;
                  const base = typeof prev === 'object' && prev !== null ? { ...prev } : {};
                  if (window.location.hash !== '#menus') {
                    window.history.pushState({ ...base, view: 'profile' }, '', menuUrl);
                  }
                }}
              />
            </div>
          ) : null}
          {/* Stick below the fixed app nav so icons stay visible, not only titles. */}
          {!simpleHome ? (
          <div
            ref={profileTabBarRef}
            className="sticky z-30 relative -mx-2 sm:-mx-4 -mt-2 sm:-mt-4 mb-0 rounded-t-lg border-b border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900 sm:rounded-t-xl"
            style={{ top: 'var(--profile-sticky-tabs-top)' }}
          >
            <div className="relative tab-nav-container">
              <nav
                ref={navRef}
                dir="ltr"
                onScroll={updateTabNavOverflow}
                className="flex w-full overflow-x-hidden max-md:overflow-x-auto scrollbar-hide max-md:touch-pan-x pt-2 pb-1.5 sm:pb-2 sm:pt-4"
                aria-label={tProfile('tabsAriaLabel')}
                style={{ WebkitOverflowScrolling: 'touch' }}
              >
                <div
                  ref={tabNavRowRef}
                  className="relative flex min-w-max flex-nowrap gap-1 sm:mx-auto sm:gap-4"
                >
                  {TAB_ORDER.map((tab) => {
                    const meta: { label: string; Icon: LucideIcon; activeColor: string } = (() => {
                      switch (tab) {
                        case 'profile': return { label: tProfile('tabs.profile'), Icon: User, activeColor: 'text-blue-600 dark:text-blue-400' };
                        case 'goals': return { label: tProfile('tabs.goals'), Icon: Target, activeColor: 'text-emerald-600 dark:text-emerald-400' };
                        case 'subscription': return { label: tProfile('tabs.subscription'), Icon: CreditCard, activeColor: 'text-blue-600 dark:text-blue-400' };
                        case 'progress': return { label: tProfile('tabs.progress'), Icon: BarChart3, activeColor: 'text-blue-600 dark:text-blue-400' };
                        case 'messages': return { label: tProfile('tabs.messages'), Icon: Mail, activeColor: 'text-blue-600 dark:text-blue-400' };
                        case 'comments': return { label: tProfile('tabs.comments'), Icon: MessageSquare, activeColor: 'text-blue-600 dark:text-blue-400' };
                        case 'favorites': return { label: tProfile('tabs.favorites'), Icon: Heart, activeColor: 'text-rose-500 dark:text-rose-400' };
                      }
                    })();
                    const Icon = meta.Icon;
                    const isActive = activeTab === tab;
                    return (
                      <button
                        key={tab}
                        data-tour-id={`profile-tab-${tab}`}
                        ref={(el) => { if (el) tabRefs.current[tab] = el; }}
                        onClick={(e) => handleTabChange(tab, e)}
                        aria-current={isActive ? 'page' : undefined}
                        style={{ order: tabVisualOrder(tab) }}
                        className={`flex flex-col items-center justify-center py-2 sm:py-3 px-3 sm:px-6 font-medium text-xs sm:text-sm whitespace-nowrap transition-colors touch-target touch-manipulation min-w-[72px] sm:min-w-[100px] ${
                          isActive
                            ? meta.activeColor
                            : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                        }`}
                      >
                        <div className="relative flex-shrink-0">
                          <Icon className={`w-5 h-5 sm:w-8 sm:h-8 mb-1 sm:mb-2 ${isActive ? meta.activeColor : 'text-gray-500 dark:text-gray-400'}`} />
                          {tab === 'messages' && unreadMessageCount > 0 && (
                            <span className="absolute -top-1 -end-1 bg-red-500 text-white text-[10px] sm:text-xs font-bold rounded-full h-4 w-4 sm:h-5 sm:w-5 flex items-center justify-center border-2 border-white dark:border-gray-900 shadow-lg">
                              {unreadMessageCount > 9 ? '9+' : unreadMessageCount}
                            </span>
                          )}
                          {tab === 'subscription' && activePlanBadge && (
                            <ActiveSubscriptionPlanBadge plan={activePlanBadge} />
                          )}
                        </div>
                        <span className="truncate text-center">{meta.label}</span>
                      </button>
                    );
                  })}
                  {/* Underline indicator — left/width set by applyTabIndicator (DOM), not React style. */}
                  <div
                    ref={(el) => {
                      tabIndicatorElRef.current = el;
                      if (el) queueMicrotask(() => updateIndicatorRef.current());
                    }}
                    className="tab-indicator"
                  />
                </div>
              </nav>
              <div className="md:hidden">
                <HorizontalEdgeScrollButtons
                  showStart={isRtl ? tabNavOverflow.moreRight : tabNavOverflow.moreLeft}
                  showEnd={isRtl ? tabNavOverflow.moreLeft : tabNavOverflow.moreRight}
                  onTowardStart={() => {
                    const nav = navRef.current;
                    const delta = nav ? Math.min(200, nav.clientWidth * 0.7) : 100;
                    scrollTabNavBy(isRtl ? delta : -delta);
                  }}
                  onTowardEnd={() => {
                    const nav = navRef.current;
                    const delta = nav ? Math.min(200, nav.clientWidth * 0.7) : 100;
                    scrollTabNavBy(isRtl ? -delta : delta);
                  }}
                  isRtl={isRtl}
                  startAriaLabel={tProfile('scrollTabsBack')}
                  endAriaLabel={tProfile('scrollTabsForward')}
                />
              </div>
            </div>
          </div>
          ) : null}

          {pinProfileActions && activeTab === 'profile' && !isDragging && pinnedActionsFrame && !(simpleHome && profileMenu) ? (
            <div
              className="fixed z-20 border-t border-gray-200 bg-white px-0 pb-2 pt-3 dark:border-gray-700 dark:bg-gray-900"
              style={{
                top: pinnedActionsFrame.top,
                left: pinnedActionsFrame.left,
                width: pinnedActionsFrame.width
              }}
              dir={direction}
            >
              <LearnerProfileActionButtons
                onEditProfile={() => setEditMode(true)}
                onChangePassword={() => setShowPasswordChange(true)}
              />
            </div>
          ) : null}

          {/* Tab Content carousel — height fits the active tab; transform slides between tabs (drag-driven on touch).
              IMPORTANT: keep top/bottom padding *inside* the measured pane (`tabContentRefs`) so its scrollHeight already
              accounts for that spacing. If padding lived on this outer wrapper, `height: scrollHeight` would clip the
              bottom (border-box: total height = content + padding, but we only measured content). */}
          <div
            ref={tabContentContainerRef}
            dir="ltr"
            hidden={simpleHome && profileMenu ? true : undefined}
            className={`${simpleHome && profileMenu ? 'hidden' : ''} -mx-2 sm:-mx-4 tab-content-container overflow-hidden`}
            style={{
              height: tabHeights[activeTab] !== undefined ? `${tabHeights[activeTab]}px` : 'auto',
              // Snap height — animating height while sliding panes causes mobile jank.
              transition: 'none'
            }}
          >
            <div
              ref={cardsScrollRef}
              dir="ltr"
              className="flex w-full items-start"
              style={{
                touchAction: 'pan-y',
                transform: simpleHome
                  ? 'none'
                  : `translate3d(calc(-${tabCarouselIndex(activeTab) * 100}% + var(--profile-tab-drag-x, 0px)), 0, 0)`,
                transition: simpleHome || isDragging ? 'none' : TAB_CAROUSEL_TRANSITION
              }}
            >
              {(simpleHome ? [activeTab] : tabCarouselOrder).map((tab) => (
                <div
                  key={tab}
                  className="w-full min-w-0 flex-[0_0_100%] shrink-0 self-start px-4 sm:px-10"
                >
                  <div
                    ref={(el) => { tabContentRefs.current[tab] = el; }}
                    {...(simpleHome ? { 'data-tour-id': `profile-tab-${tab}` } : {})}
                    className="overscroll-x-none pb-6 pt-4 sm:pb-10 sm:pt-8"
                  >
                    {renderTabContent(tab)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Password Change Modal */}
        <ProfileSlideFormModalShell
          open={showPasswordChange}
          onClose={() => setShowPasswordChange(false)}
          titleId="profile-change-password-modal-title"
          title={tProfile('profileHero.changePassword')}
          closeAriaLabel={tProfile('formEdit.close')}
          footer={
            <>
              <button
                type="button"
                onClick={() => setShowPasswordChange(false)}
                className="rounded-xl border border-slate-400/80 bg-white/95 px-4 py-2.5 text-sm font-semibold text-slate-800 shadow-sm transition-colors hover:bg-slate-50 dark:border-slate-500 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
              >
                {tCommon('close')}
              </button>
              <button
                type="submit"
                form="profile-change-password-form"
                className="flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 px-5 py-2.5 text-sm font-semibold text-white shadow-md shadow-violet-900/20 transition-colors hover:from-violet-700 hover:to-indigo-700 disabled:opacity-50 dark:shadow-violet-950/40"
              >
                {tProfile('profileHero.changePassword')}
              </button>
            </>
          }
        >
          <PasswordChangeForm
            variant="embedded"
            hideActions
            onSuccess={() => setShowPasswordChange(false)}
          />
        </ProfileSlideFormModalShell>

        {/* Edit Profile Modal */}
        <ProfileSlideFormModalShell
          open={editMode}
          onClose={() => setEditMode(false)}
          titleId="edit-profile-modal-title"
          title={tProfile('formEdit.title')}
          closeAriaLabel={tProfile('formEdit.close')}
          panelMaxWidthClass="sm:max-w-3xl"
          footer={
            <>
              <button
                type="button"
                onClick={() => setEditMode(false)}
                disabled={updating}
                className="rounded-xl border border-slate-400/80 bg-white/95 px-4 py-2.5 text-sm font-semibold text-slate-800 shadow-sm transition-colors hover:bg-slate-50 disabled:opacity-50 dark:border-slate-500 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
              >
                {tCommon('close')}
              </button>
              <button
                type="submit"
                form="edit-profile-form"
                disabled={updating}
                className="flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 px-5 py-2.5 text-sm font-semibold text-white shadow-md shadow-violet-900/20 transition-colors hover:from-violet-700 hover:to-indigo-700 disabled:opacity-50 dark:shadow-violet-950/40"
              >
                {updating && <Loader2 className="h-4 w-4 animate-spin" />}
                {updating ? tProfile('formEdit.saving') : tProfile('formEdit.saveChanges')}
              </button>
            </>
          }
        >
                <form id="edit-profile-form" onSubmit={handleUpdateProfile} dir={direction} className="space-y-6 px-4 py-4 sm:px-5 sm:py-5 custom-scrollbar">
                  <p className="text-sm text-slate-600 dark:text-slate-400">{tProfile('formEdit.subtitle')}</p>
                    {/* Personal Information Section */}
                    <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
                      <h4 className={EDIT_PROFILE_SECTION_HEADING_CLASS}>
                        <User className="w-5 h-5 text-purple-600 dark:text-purple-400 shrink-0" />
                        {tProfile('formEdit.personalInformation')}
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div className="space-y-1">
                          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                            {tProfile('details.firstName')} <span className="text-red-500 dark:text-red-400">*</span>
                          </label>
                          <input
                            type="text"
                            value={formData.firstName}
                            onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                            placeholder={tProfile('formEdit.firstNamePlaceholder')}
                            className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800/80 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:border-purple-500 dark:focus:border-purple-400 focus:ring-4 focus:ring-purple-100 dark:focus:ring-purple-900/40 transition-all duration-200 disabled:bg-gray-50 dark:disabled:bg-gray-800/50 disabled:cursor-not-allowed"
                            disabled={updating}
                            required
                          />
                        </div>

                        <div className="space-y-1">
                          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                            {tProfile('details.lastName')} <span className="text-red-500 dark:text-red-400">*</span>
                          </label>
                          <input
                            type="text"
                            value={formData.lastName}
                            onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                            placeholder={tProfile('formEdit.lastNamePlaceholder')}
                            className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800/80 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:border-purple-500 dark:focus:border-purple-400 focus:ring-4 focus:ring-purple-100 dark:focus:ring-purple-900/40 transition-all duration-200 disabled:bg-gray-50 dark:disabled:bg-gray-800/50 disabled:cursor-not-allowed"
                            disabled={updating}
                            required
                          />
                        </div>
                      </div>
                    </div>

                    {/* Contact Information Section */}
                    <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
                      <h4 className={EDIT_PROFILE_SECTION_HEADING_CLASS}>
                        <svg className="w-5 h-5 text-purple-600 dark:text-purple-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        {tProfile('formEdit.contactInformation')}
                      </h4>
                      <div className="space-y-5">
                        <div className="space-y-1">
                          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                            {tProfile('details.emailAddress')} <span className="text-red-500 dark:text-red-400">*</span>
                          </label>
                          <input
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            placeholder={tProfile('formEdit.emailPlaceholder')}
                            className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800/80 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:border-purple-500 dark:focus:border-purple-400 focus:ring-4 focus:ring-purple-100 dark:focus:ring-purple-900/40 transition-all duration-200 disabled:bg-gray-50 dark:disabled:bg-gray-800/50 disabled:cursor-not-allowed"
                            disabled={updating}
                            required
                          />
                        </div>

                        <div className="space-y-1">
                          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                            {tProfile('details.username')}
                          </label>
                          <input
                            type="text"
                            value={formData.userName}
                            onChange={(e) => setFormData({ ...formData, userName: e.target.value })}
                            placeholder={tProfile('formEdit.usernamePlaceholder')}
                            className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800/80 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:border-purple-500 dark:focus:border-purple-400 focus:ring-4 focus:ring-purple-100 dark:focus:ring-purple-900/40 transition-all duration-200 disabled:bg-gray-50 dark:disabled:bg-gray-800/50 disabled:cursor-not-allowed"
                            disabled={updating}
                          />
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{tProfile('formEdit.usernameHint')}</p>
                        </div>
                      </div>
                    </div>

                    {/* Phone Number Section */}
                    <div
                      id="profile-phone-verification"
                      className={`border-b border-gray-200 pb-4 dark:border-gray-700 ${
                        telegramPhoneHint ? 'rounded-xl ring-2 ring-amber-400 ring-offset-2 ring-offset-slate-50 dark:ring-offset-gray-900' : ''
                      }`}
                    >
                      <h4 className={EDIT_PROFILE_SECTION_HEADING_CLASS}>
                        <svg className="w-5 h-5 text-purple-600 dark:text-purple-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                        {tProfile('details.phoneNumber')}{' '}
                        <span className="text-xs font-normal text-gray-500 dark:text-gray-400">
                          {tProfile('form.optional')}
                        </span>
                      </h4>
                      <div className="space-y-2">
                        {telegramPhoneHint && (
                          <p className="rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-900 dark:border-amber-700 dark:bg-amber-950/50 dark:text-amber-100">
                            {tProfile('telegramBotPhoneHint')}
                          </p>
                        )}
                        <ProfilePhoneVerificationField
                          value={formData.phoneNumber}
                          savedPhone={user?.phoneNumber}
                          savedConfirmed={Boolean(user?.phoneNumberConfirmed)}
                          disabled={updating}
                          onChange={handlePhoneNumberChange}
                          onVerified={handlePhoneVerified}
                        />
                        {phoneNumberError && (
                          <p className="text-xs text-red-600 dark:text-red-400">{phoneNumberError}</p>
                        )}
                      </div>
                    </div>

                    {/* Additional Information Section */}
                    <div className="space-y-5">
                      <h4 className={`${EDIT_PROFILE_SECTION_HEADING_CLASS} mb-0`}>
                        <svg className="w-5 h-5 text-purple-600 dark:text-purple-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        {tProfile('formEdit.additionalInformation')}
                      </h4>
                      
                      <div className="space-y-1">
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                          {tProfile('details.bio')}{' '}
                          <span className="text-xs font-normal text-gray-500 dark:text-gray-400">
                            ({tProfile('formEdit.bioOptionalHint')})
                          </span>
                        </label>
                        <textarea
                          value={formData.bio}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value.length <= 100) {
                              setFormData({ ...formData, bio: value });
                            }
                          }}
                          placeholder={tProfile('formEdit.bioPlaceholder')}
                          rows={4}
                          maxLength={100}
                          className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800/80 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:border-purple-500 dark:focus:border-purple-400 focus:ring-4 focus:ring-purple-100 dark:focus:ring-purple-900/40 transition-all duration-200 resize-none disabled:bg-gray-50 dark:disabled:bg-gray-800/50 disabled:cursor-not-allowed"
                          disabled={updating}
                        />
                        <div className="flex justify-end">
                          <span className={`text-xs ${formData.bio.length >= 100 ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-gray-500 dark:text-gray-400'}`}>
                            {formData.bio.length}/100
                          </span>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div className="space-y-1">
                          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                            {tProfile('details.nationality')}{' '}
                            <span className="text-xs font-normal text-gray-500 dark:text-gray-400">
                              {tProfile('form.optional')}
                            </span>
                          </label>
                          <ProfileComboList
                            id="edit-profile-nationality"
                            value={formData.nationality || ''}
                            onChange={(v) => setFormData({ ...formData, nationality: v })}
                            options={nationalityComboOptions}
                            searchable
                            searchPlaceholder={tProfile('formEdit.searchCountries')}
                            placeholder={tProfile('formEdit.selectCountry')}
                            aria-label={tProfile('details.nationality')}
                            disabled={updating}
                            closeOnOutsideClick={false}
                            open={openEditProfileCombo === 'nationality'}
                            onOpenChange={editProfileComboOpenChange('nationality')}
                            {...EDIT_PROFILE_COMBO_STYLE}
                          />
                        </div>

                        <div className="space-y-1">
                          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                            {tProfile('details.age')}{' '}
                            <span className="text-xs font-normal text-gray-500 dark:text-gray-400">
                              {tProfile('form.optional')}
                            </span>
                          </label>
                          <ProfileComboList
                            id="edit-profile-age"
                            value={formData.age !== undefined && formData.age !== null ? String(formData.age) : ''}
                            onChange={(v) =>
                              setFormData({
                                ...formData,
                                age: v ? parseInt(v, 10) : undefined,
                              })
                            }
                            options={ageComboOptions}
                            placeholder={tProfile('formEdit.selectAge')}
                            aria-label={tProfile('details.age')}
                            disabled={updating}
                            open={openEditProfileCombo === 'age'}
                            onOpenChange={editProfileComboOpenChange('age')}
                            {...EDIT_PROFILE_COMBO_STYLE}
                          />
                        </div>

                        <div className="space-y-1">
                          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                            {tProfile('details.gender')}{' '}
                            <span className="text-xs font-normal text-gray-500 dark:text-gray-400">
                              {tProfile('form.optional')}
                            </span>
                          </label>
                          <ProfileComboList
                            id="edit-profile-gender"
                            value={formData.gender || ''}
                            onChange={(v) => setFormData({ ...formData, gender: v })}
                            options={genderComboOptions}
                            placeholder={tProfile('formEdit.selectGender')}
                            aria-label={tProfile('details.gender')}
                            disabled={updating}
                            open={openEditProfileCombo === 'gender'}
                            onOpenChange={editProfileComboOpenChange('gender')}
                            {...EDIT_PROFILE_COMBO_STYLE}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Error Message */}
                    {error && (
                      <div className="bg-red-50 dark:bg-red-950/40 border-2 border-red-200 dark:border-red-800/80 rounded-xl p-4 flex items-start gap-2">
                        <svg className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                        <p className="text-sm text-red-700 dark:text-red-300 flex-1">{error}</p>
                      </div>
                    )}
                  </form>
        </ProfileSlideFormModalShell>
      </div>
    </div>
  );
};

export default UserProfileDashboard;